import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Plus, Trash2, Search, Scissors, Download, Upload, LayoutDashboard, Undo2,
  FileText, BarChart3, AlertTriangle, Barcode, Package, Tag, Box, Layers,
  AlertCircle, TrendingDown, Archive, ChevronRight, ClipboardList, QrCode,
  X, RefreshCw, MapPin, Printer, Eye, Info, CheckCircle, Camera, Zap
} from 'lucide-react';

// ============================================================================
// PART 1 — CONSTANTS & CROSS-MODULE DATA HELPERS
// ============================================================================

const DB_URL = 'https://nirvikalp.integrashion.com/api/save_universal.php';
const AI_URL = 'https://nirvikalp.integrashion.com/api/ai_service.php';

const saveDB = async (category, data) => {
  try {
    await fetch(DB_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, data })
    });
  } catch (e) { console.warn('DB save:', e); }
};

const DELETE_URL = 'https://nirvikalp.integrashion.com/api/delete_universal.php';

// Hard-delete a record. Tries dedicated delete endpoint first, then soft-delete fallback.
const deleteFromDB = async (category, id) => {
  try {
    // 1. Try dedicated delete endpoint
    const res = await fetch(DELETE_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, id })
    });
    const text = await res.text();
    let json; try { json = JSON.parse(text); } catch (_) { json = {}; }
    if (json.success || json.deleted) return; // confirmed deleted
    // 2. Fallback: save_universal with action:'delete'
    await fetch(DB_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, action: 'delete', id })
    });
  } catch (e) {
    console.warn('DB delete failed, using soft-delete fallback:', e);
    // 3. Ultimate fallback: mark as _deleted so it's filtered on load
    try {
      await fetch(DB_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, data: { id, _deleted: true } })
      });
    } catch (_) {}
  }
};

// ── StyleRegistry helpers ────────────────────────────────────────────────────
/** All styles from db.mgmtStyles */
const getStyles = (db) => db?.mgmtStyles || [];

/** All unique style refs */
const getStyleRefs = (db) => [...new Set(getStyles(db).map(s => s.styleRef).filter(Boolean))].sort();

/** POs for a given styleRef */
const getPOsForStyle = (db, styleRef) => {
  if (!styleRef) return [];
  // First try poSizeTable (most detailed)
  const pst = (db?.poSizeTable || []).filter(r =>
    (r.style_ref || r.styleRef || '') === styleRef
  );
  if (pst.length) return [...new Set(pst.map(r => r.po_no || r.poNo).filter(Boolean))].sort();
  // Fallback: mgmtStyles pos array
  const style = getStyles(db).find(s => s.styleRef === styleRef);
  return (style?.pos || []).map(p => p.num).filter(Boolean);
};

/** Colors for a given styleRef + PO */
const getColorsForPO = (db, styleRef, poNo) => {
  const pst = (db?.poSizeTable || []).filter(r =>
    (!styleRef || (r.style_ref || r.styleRef) === styleRef) &&
    (!poNo     || (r.po_no || r.poNo) === poNo)
  );
  if (pst.length) return [...new Set(pst.map(r => r.color || r.colour).filter(Boolean))];
  const style = getStyles(db).find(s => s.styleRef === styleRef);
  return (style?.pos || []).filter(p => !poNo || p.num === poNo).map(p => p.col).filter(Boolean);
};

/** Sizes for a given styleRef + PO */
const getSizesForPO = (db, styleRef, poNo) => {
  const pst = (db?.poSizeTable || []).filter(r =>
    (!styleRef || (r.style_ref || r.styleRef) === styleRef) &&
    (!poNo     || (r.po_no || r.poNo) === poNo)
  );
  const sizes = [...new Set(pst.map(r => r.size).filter(Boolean))];
  return sizes.length ? sizes : ['XS','S','M','L','XL','XXL','ALL'];
};

/** Total order qty for styleRef + PO */
const getPOQty = (db, styleRef, poNo) => {
  const pst = (db?.poSizeTable || []).filter(r =>
    (!styleRef || (r.style_ref || r.styleRef) === styleRef) &&
    (!poNo     || (r.po_no || r.poNo) === poNo)
  );
  if (pst.length) return pst.reduce((s, r) => s + (parseInt(r.qty) || 0), 0);
  const style = getStyles(db).find(s => s.styleRef === styleRef);
  return (style?.pos || []).filter(p => !poNo || p.num === poNo)
    .reduce((s, p) => s + (parseInt(p.qty) || 0), 0);
};

/** Auto-fill patch for a form when style changes */
const getStyleAutoFill = (db, styleRef, poNo) => {
  const pos = getPOsForStyle(db, styleRef);
  const colors = getColorsForPO(db, styleRef, poNo || pos[0]);
  const sizes  = getSizesForPO(db, styleRef, poNo || pos[0]);
  const qty    = getPOQty(db, styleRef, poNo || pos[0]);
  return { pos, colors, sizes, qty: qty || '' };
};

// ── QualityControl helpers ───────────────────────────────────────────────────
/** Fabric inspection records from db.warehouse (QC module) */
const getFabricInspections = (db) =>
  (db?.warehouse || []).filter(r => r.formType === 'FABRIC_INSP');

/** Trims inspection records */
const getTrimsInspections = (db) =>
  (db?.warehouse || []).filter(r => r.formType === 'TRIMS_INSP' || r.formType === 'TRIMS_APPROVAL');

/** Get QC result for a specific roll by Roll_ID, SKU_ID, Invoice_No, style, color */
const getQCForRoll = (db, roll) => {
  const insp = getFabricInspections(db);
  // Match: roll.Roll_ID appears in inspection roll list, or invoice matches, or style+color matches
  return insp.filter(rec => {
    const rollMatch  = (rec.rolls || []).some(rl =>
      rl.rollNo === roll.Roll_ID || rl.rollNo === roll.SKU_ID
    );
    const invMatch   = rec.invoiceNo && rec.invoiceNo === roll.Invoice_No;
    const styleColor = rec.cipl === roll.Style_Name &&
                       (rec.colour || '').toLowerCase() === (roll.Color || '').toLowerCase();
    return rollMatch || invMatch || styleColor;
  });
};

/** Get aggregated QC status for a roll (PASS / FAIL / PENDING) */
const getRollQCStatus = (db, roll) => {
  const recs = getQCForRoll(db, roll);
  if (!recs.length) return 'PENDING';
  const results = recs.flatMap(r => (r.rolls || []).map(rl => rl.result));
  if (results.includes('FAIL')) return 'FAIL';
  if (results.includes('PASS')) return 'PASS';
  return 'PENDING';
};

/** Get total defect points for a roll */
const getRollDefects = (db, roll) => {
  const recs = getQCForRoll(db, roll);
  return recs.reduce((s, r) =>
    s + (r.rolls || []).reduce((rs, rl) => rs + (parseFloat(rl.defPts100) || 0), 0), 0
  );
};

/** RTV qty from FABRIC_CLAIM records matching invoice or style+color */
const getRTVQty = (db, roll) => {
  const claims = (db?.warehouse || []).filter(r => r.formType === 'FABRIC_CLAIM');
  return claims
    .filter(c =>
      (c.invoice && c.invoice === roll.Invoice_No) ||
      ((c.colour || '').toLowerCase() === (roll.Color || '').toLowerCase() &&
       (c.status === 'OPEN' || c.status === 'REPLACE PENDING'))
    )
    .reduce((s, c) => s + (parseInt(c.reqYds) || 0), 0);
};

/** QC status for a trim item */
const getTrimQCStatus = (db, item) => {
  const recs = (db?.warehouse || []).filter(r =>
    (r.formType === 'TRIMS_INSP' || r.formType === 'TRIMS_APPROVAL') &&
    (!item.Style_Name || r.style === item.Style_Name || r.styleRef === item.Style_Name) &&
    (!item.SKU_ID || (r.trimCode || r.skuId) === item.SKU_ID)
  );
  if (!recs.length) return 'PENDING';
  if (recs.some(r => r.result === 'REJECTED' || r.overall === 'REJECTED')) return 'FAIL';
  if (recs.some(r => r.result === 'APPROVED' || r.overall === 'APPROVED')) return 'PASS';
  return 'HOLD';
};

// ── HSN dictionary ────────────────────────────────────────────────────────────
const HSN_DICT = {
  button:'9606210000',buttons:'9606210000',zipper:'9607110000',zip:'9607110000',
  thread:'5204110000',needle:'7319400010',label:'5807100000','woven label':'5807100000',
  'care label':'5807100000',elastic:'5806200000','elastic tape':'5806200000',
  interlining:'5903100000',fusing:'5903100000',polybag:'3923210010','poly bag':'3923210010',
  hanger:'3926909990',carton:'4819100010',box:'4819100010',hangtag:'4823909100',
  'hang tag':'4823909100','price tag':'4823909100',clip:'7326909990','safety pin':'7319400000',
  ribbon:'5806320000',tape:'5806320000',velcro:'5607500000',hook:'9606290000',
  patch:'5807100000',fabric:'5208110000','cotton fabric':'5208110000',lining:'5407610000',
  denim:'5209420000',twill:'5209110000',knit:'6006310000',jersey:'6006310000',
  sticker:'4911910000',butterfly:'3926909990',tissue:'4803000000','silica gel':'2811220010',
};
const getHSN = (name) => {
  if (!name) return '';
  const lo = name.toLowerCase();
  for (const [k, v] of Object.entries(HSN_DICT)) if (lo.includes(k)) return v;
  return '';
};

// ── Stock status engine (fixed) ───────────────────────────────────────────────
const getStockStatus = (item) => {
  const rcv  = parseFloat(item.Received_Qty ?? item.M_Qty ?? 0);
  const iss  = parseFloat(item.Issued_Qty ?? item.Issue_Qty ?? 0);
  const bal  = parseFloat(item.Balance_Qty ?? (rcv - iss));
  const req  = parseFloat(item.Required_Qty ?? 0);
  const days = parseFloat(item.Days_Coverage ?? 0);
  if (iss > rcv || bal < 0) return 'critical';
  if (req === 0 && bal === 0) return 'critical';
  if (req === 0) return 'adequate';
  const pct = (bal / req) * 100;
  if (pct < 10 || (days > 0 && days < 3)) return 'critical';
  if (pct < 30 || (days > 0 && days < 7)) return 'low';
  if (pct > 150) return 'excess';
  return 'adequate';
};

/** Human-readable reason for status — shown on hover */
const getStatusReason = (item) => {
  const rcv  = parseFloat(item.Received_Qty ?? item.M_Qty ?? 0);
  const iss  = parseFloat(item.Issued_Qty ?? item.Issue_Qty ?? 0);
  const bal  = parseFloat(item.Balance_Qty ?? (rcv - iss));
  const req  = parseFloat(item.Required_Qty ?? 0);
  const days = parseFloat(item.Days_Coverage ?? 0);
  if (iss > rcv) return `⚠ Issued (${iss}) exceeds Received (${rcv})`;
  if (bal < 0)   return `⚠ Negative balance: ${bal.toFixed(2)}`;
  if (req === 0 && bal === 0) return 'No stock and no requirement set';
  if (req === 0) return `Balance: ${bal.toFixed(2)} — No requirement set`;
  const pct = (bal / req) * 100;
  if (pct < 10)  return `Only ${pct.toFixed(1)}% of required in stock (${bal.toFixed(0)} of ${req})`;
  if (days > 0 && days < 3) return `Only ${days} days of stock coverage remaining`;
  if (pct < 30)  return `${pct.toFixed(1)}% of required in stock — reorder soon`;
  if (days > 0 && days < 7) return `${days} days coverage — order within 2 days`;
  if (pct > 150) return `Excess stock: ${pct.toFixed(0)}% of requirement (${bal.toFixed(0)} vs ${req})`;
  return `${pct.toFixed(1)}% coverage — ${days > 0 ? days + 'd remaining' : 'adequate'}`;
};

const C = {
  critical:{ bg:'#fef2f2', border:'#fecaca', text:'#c93652', badge:'#fde8eb', label:'CRITICAL' },
  low:     { bg:'#fffbeb', border:'#fde68a', text:'#fdab3d', badge:'#fff5e6', label:'LOW STOCK' },
  adequate:{ bg:'#f0fdf4', border:'#b3f5d6', text:'#00a65e', badge:'#e3faf0', label:'ADEQUATE'  },
  excess:  { bg:'#e8f0ff', border:'#a8d1ff', text:'#0073ea', badge:'#d0e4ff', label:'EXCESS'    },
};

const QC_COL = { PASS:'#00a65e', FAIL:'#c93652', PENDING:'#9699a6', HOLD:'#fdab3d' };
const QC_BG  = { PASS:'#f0fdf4', FAIL:'#fef2f2', PENDING:'#f5f6f8', HOLD:'#fffbeb' };

const useIsTablet = () => {
  const [t, setT] = useState(window.innerWidth <= 1024);
  useEffect(() => { const f = () => setT(window.innerWidth <= 1024); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  return t;
};

// ============================================================================
// PART 2 — SHARED UI COMPONENTS
// ============================================================================

const ss = { backgroundColor:'white', border:'1px solid #e6e9ef', borderRadius:'10px', padding:'20px', marginBottom:'20px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' };
const hs = { fontSize:'13px', fontWeight:'800', textTransform:'uppercase', color:'#323338', marginBottom:'16px', borderBottom:'2px solid #f0f1f4', paddingBottom:'10px', display:'flex', alignItems:'center', gap:'8px' };
const TH = { padding:'9px 10px', borderBottom:'2px solid #e6e9ef', textAlign:'left', fontSize:'10px', fontWeight:'700', textTransform:'uppercase', color:'#676879', background:'#f5f6f8', whiteSpace:'nowrap' };
const TD = { padding:'8px 10px', borderBottom:'1px solid #f0f1f4', fontSize:'12px', color:'#323338', whiteSpace:'nowrap' };

const SubTabBtn = ({ label, active, onClick, icon:Icon, count=0 }) => (
  <button onClick={onClick} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'10px 16px', fontSize:'13px', fontWeight:'700', border:'none', borderBottom: active?'3px solid #0073ea':'3px solid transparent', background: active?'#e8f0ff':'transparent', color: active?'#0062c4':'#676879', cursor:'pointer', whiteSpace:'nowrap' }}>
    {Icon && <Icon size={14}/>}{label}
    {count>0 && <span style={{ background:'#c93652', color:'white', borderRadius:'20px', padding:'1px 6px', fontSize:'10px', fontWeight:'800' }}>{count}</span>}
  </button>
);

const StatCard = ({ label, value, sub, color='#0073ea', icon:Icon }) => (
  <div style={{ background:'white', border:'1px solid #e6e9ef', borderRadius:'10px', padding:'16px' }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
      <div>
        <div style={{ fontSize:'11px', color:'#676879', fontWeight:'700', textTransform:'uppercase', marginBottom:'6px' }}>{label}</div>
        <div style={{ fontSize:'24px', fontWeight:'900', color, lineHeight:1 }}>{value}</div>
        {sub && <div style={{ fontSize:'11px', color:'#9699a6', marginTop:'4px' }}>{sub}</div>}
      </div>
      {Icon && <div style={{ width:'38px', height:'38px', borderRadius:'8px', background:color+'18', display:'flex', alignItems:'center', justifyContent:'center' }}><Icon size={18} color={color}/></div>}
    </div>
  </div>
);

const Badge = ({ status }) => {
  const s = C[status]||C.adequate;
  return <span style={{ padding:'3px 8px', borderRadius:'20px', fontSize:'10px', fontWeight:'800', background:s.badge, color:s.text, border:`1px solid ${s.border}`, whiteSpace:'nowrap' }}>{s.label}</span>;
};

/** Badge with hover tooltip showing status reason */
const StatusBadgeWithReason = ({ item }) => {
  const status = getStockStatus(item);
  const reason = getStatusReason(item);
  const s = C[status]||C.adequate;
  return (
    <span title={reason} style={{ padding:'3px 8px', borderRadius:'20px', fontSize:'10px', fontWeight:'800', background:s.badge, color:s.text, border:`1px solid ${s.border}`, whiteSpace:'nowrap', cursor:'help', textDecoration:'underline dotted' }}>
      {s.label}
    </span>
  );
};

const QCBadge = ({ status }) => (
  <span style={{ padding:'3px 8px', borderRadius:'20px', fontSize:'10px', fontWeight:'800', background:QC_BG[status]||'#f5f6f8', color:QC_COL[status]||'#9699a6', border:`1px solid ${QC_COL[status]||'#e6e9ef'}40`, whiteSpace:'nowrap' }}>
    {status==='PASS'?'✓ PASS':status==='FAIL'?'✗ FAIL':status==='HOLD'?'⚠ HOLD':'— PENDING'}
  </span>
);

const Fld = ({ label, value, onChange, type='text', readOnly=false, placeholder='', options=null, required=false, error='', style={} }) => (
  <div style={{ display:'flex', flexDirection:'column', ...style }}>
    <label style={{ fontSize:'10px', fontWeight:'700', color:error?'#c93652':'#676879', marginBottom:'3px', textTransform:'uppercase' }}>
      {label}{required && <span style={{color:'#c93652'}}> *</span>}
    </label>
    {options ? (
      <select value={value||''} onChange={onChange} disabled={readOnly} style={{ width:'100%', padding:'8px 10px', fontSize:'12px', border:`1px solid ${error?'#c93652':'#c5c7d4'}`, borderRadius:'6px', background:readOnly?'#f0f1f4':'white', outline:'none' }}>
        <option value=''>Select…</option>
        {options.map(o => <option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
      </select>
    ) : (
      <input type={type} value={value||''} onChange={onChange} readOnly={readOnly} placeholder={placeholder}
        style={{ width:'100%', padding:'8px 10px', fontSize:'12px', border:`1px solid ${error?'#c93652':'#c5c7d4'}`, borderRadius:'6px', background:readOnly?'#f0f1f4':'white', color:readOnly?'#676879':'#1f1f1f', outline:'none', boxSizing:'border-box' }}
      />
    )}
    {error && <span style={{fontSize:'10px',color:'#c93652',marginTop:'2px'}}>{error}</span>}
  </div>
);

const QRImg = ({ data, label, size=120 }) => {
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}`;
  return (
    <div style={{ textAlign:'center', padding:'10px', border:'1px solid #e6e9ef', borderRadius:'8px', background:'white' }}>
      <img src={url} alt="QR" width={size} height={size} style={{display:'block',margin:'0 auto'}}/>
      <div style={{ fontSize:'9px', color:'#676879', marginTop:'4px', fontWeight:'700', wordBreak:'break-all', maxWidth:size, margin:'4px auto 0' }}>{label}</div>
    </div>
  );
};

// ============================================================================
// PART 3 — REQUISITION SLIP MODAL
// ============================================================================

function RequisitionModal({ open, onClose, onConfirm, items=[], db }) {
  const styleRefs = getStyleRefs(db);
  const blank = {
    Slip_No:`REQ-${Date.now().toString().slice(-8)}`, Date:new Date().toISOString().split('T')[0],
    Style_Name:'', Order_No:'', Color:'', Size:'', Section:'Cutting',
    Line_No:'', Supervisor:'', Handed_To:'', Item_Id:'', Qty:'', Remarks:'',
    // Cut plan link
    cutPlanId: '', layNo: '', importMode: 'manual',  // 'manual' | 'by_lay' | 'from_plan'
    selectedLays: [],  // array of lay numbers if importMode='from_plan'
  };
  const [slip, setSlip] = useState(blank);
  const [errs, setErrs] = useState({});
  const [poOpts,    setPoOpts]    = useState([]);
  const [colorOpts, setColorOpts] = useState([]);
  const [sizeOpts,  setSizeOpts]  = useState([]);
  const [cutPlanOpts, setCutPlanOpts] = useState([]);  // saved cut plans for this style/PO/color
  const [selectedPlanRolls, setSelectedPlanRolls] = useState([]);  // rolls from chosen cut plan

  useEffect(() => {
    if (!slip.Style_Name) return;
    const pos = getPOsForStyle(db, slip.Style_Name);
    setPoOpts(pos);
    const colors = getColorsForPO(db, slip.Style_Name, slip.Order_No);
    setColorOpts(colors);
    const sizes  = getSizesForPO(db, slip.Style_Name, slip.Order_No);
    setSizeOpts(sizes);
    setSlip(p => ({ ...p, Order_No: p.Order_No || pos[0] || '', Color: p.Color || colors[0] || '' }));
  }, [slip.Style_Name, slip.Order_No]);

  // Load cut plans when style/PO/color are set
  useEffect(() => {
    if (!slip.Style_Name) { setCutPlanOpts([]); return; }
    const plans = (db?.cutPlans || db?.stylePlans || []).filter(p =>
      p.type === 'CUT_PLAN' &&
      (!slip.Style_Name || p.style === slip.Style_Name) &&
      (!slip.Order_No   || p.po    === slip.Order_No) &&
      (!slip.Color      || p.color === slip.Color)
    );
    setCutPlanOpts(plans);
  }, [slip.Style_Name, slip.Order_No, slip.Color, db]);

  // When a cut plan is chosen, extract all unique rolls from it
  const handlePlanSelect = (planId) => {
    setSlip(p => ({...p, cutPlanId: planId, selectedLays: [], Item_Id: ''}));
    const plan = cutPlanOpts.find(p => String(p.id) === String(planId));
    if (!plan) { setSelectedPlanRolls([]); return; }
    // Collect all unique rolls across all lays and steps
    const rollMap = {};
    (plan.steps || []).forEach(st => {
      (st.lays || []).forEach(lay => {
        (lay.rolls || []).forEach(r => {
          const key = r.rollId;
          if (!rollMap[key]) {
            // Find matching inventory item
            const invItem = items.find(i => i.Roll_ID === r.rollId || String(i.id) === String(r.rollId));
            rollMap[key] = { ...r, invItem,
              totalUsed: 0, rack: r.rack, cell: r.cell, lays: [] };
          }
          rollMap[key].totalUsed += parseFloat(r.usedM || 0);
          rollMap[key].lays.push(`L${lay.layNo}`);
        });
      });
    });
    setSelectedPlanRolls(Object.values(rollMap));
  };

  // Handle lay-number based selection
  const handleLaySelect = (layNo) => {
    const plan = cutPlanOpts.find(p => String(p.id) === String(slip.cutPlanId));
    if (!plan || !layNo) return;
    const layRolls = (plan.steps || []).flatMap(st =>
      (st.lays || []).filter(l => String(l.layNo) === String(layNo)).flatMap(l => l.rolls || [])
    );
    if (layRolls.length === 0) return;
    // Auto-set the first roll as Item_Id and total qty as Qty
    const firstInvItem = items.find(i => i.Roll_ID === layRolls[0]?.rollId);
    const totalQty = layRolls.reduce((s,r) => s + parseFloat(r.usedM||0), 0);
    setSlip(p => ({...p, layNo, Item_Id: String(firstInvItem?.id||''), Qty: totalQty.toFixed(2),
      Remarks: `Lay ${layNo} — Rolls: ${layRolls.map(r=>r.rollId).join(', ')}`
    }));
  };

  const selectedItem = items.find(i => i.id === parseInt(slip.Item_Id));
  const avail = parseFloat(selectedItem?.Balance_Qty ?? 0);

  // Validate — relaxed for cut-plan import mode
  const validate = () => {
    const e = {};
    if (slip.importMode === 'manual') {
      if (!slip.Item_Id) e.Item_Id = 'Select item';
      if (!slip.Qty || parseFloat(slip.Qty) <= 0) e.Qty = 'Enter valid qty';
      if (parseFloat(slip.Qty) > avail) e.Qty = `Exceeds balance (${avail.toFixed(2)})`;
    }
    if (!slip.Supervisor) e.Supervisor = 'Required';
    if (!slip.Handed_To)  e.Handed_To  = 'Required';
    setErrs(e);
    return !Object.keys(e).length;
  };

  // For from_plan mode, confirm issues multiple rolls at once
  const handleConfirmFromPlan = () => {
    if (!validate()) return;
    if (selectedPlanRolls.length === 0) { alert('No rolls loaded from cut plan.'); return; }
    // Call onConfirm for each roll
    selectedPlanRolls.forEach(planRoll => {
      if (!planRoll.invItem) return;
      onConfirm({
        ...slip,
        Item_Id: String(planRoll.invItem.id),
        Qty: planRoll.totalUsed.toFixed(4),
        rollId: planRoll.rollId,
        Remarks: `${slip.Remarks||''} — Cut Plan ${slip.cutPlanId}, Lays: ${planRoll.lays.join(',')}`,
        layNos: planRoll.lays,
      });
    });
  };

  if (!open) return null;
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}}>
      <div style={{background:'white',borderRadius:'12px',width:'100%',maxWidth:'680px',maxHeight:'92vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:'1px solid #e6e9ef',background:'#1f1f1f',borderRadius:'12px 12px 0 0'}}>
          <div style={{color:'white',fontWeight:'800',fontSize:'15px'}}>📋 Material Requisition Slip</div>
          <button onClick={onClose} style={{background:'none',border:'none',color:'white',cursor:'pointer'}}><X size={18}/></button>
        </div>
        <div style={{padding:'20px 24px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'14px'}}>
            <Fld label="Slip No" value={slip.Slip_No} onChange={e=>setSlip(p=>({...p,Slip_No:e.target.value}))}/>
            <Fld label="Date" value={slip.Date} onChange={e=>setSlip(p=>({...p,Date:e.target.value}))} type="date"/>
          </div>
          {/* Order linkage — from Style Registry */}
          <div style={{background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'14px',marginBottom:'12px'}}>
            <div style={{fontSize:'11px',fontWeight:'800',color:'#676879',marginBottom:'10px'}}>🔗 ORDER (from Style Registry)</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:'10px'}}>
              <Fld label="Style" value={slip.Style_Name} onChange={e=>setSlip(p=>({...p,Style_Name:e.target.value,Order_No:'',Color:''}))} options={styleRefs}/>
              <Fld label="PO No" value={slip.Order_No} onChange={e=>setSlip(p=>({...p,Order_No:e.target.value}))} options={poOpts}/>
              <Fld label="Color" value={slip.Color} onChange={e=>setSlip(p=>({...p,Color:e.target.value}))} options={colorOpts}/>
              <Fld label="Size"  value={slip.Size}  onChange={e=>setSlip(p=>({...p,Size:e.target.value}))}  options={sizeOpts.length ? sizeOpts : ['XS','S','M','L','XL','XXL','All']}/>
            </div>
          </div>
          {/* Dispatch */}
          <div style={{background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'14px',marginBottom:'12px'}}>
            <div style={{fontSize:'11px',fontWeight:'800',color:'#676879',marginBottom:'10px'}}>🏭 DISPATCH DETAILS</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:'10px'}}>
              <Fld label="Section" value={slip.Section} onChange={e=>setSlip(p=>({...p,Section:e.target.value}))} options={['Cutting','Sewing','Finishing','Embroidery','Printing','Packing','QC']}/>
              <Fld label="Line No" value={slip.Line_No} onChange={e=>setSlip(p=>({...p,Line_No:e.target.value}))} placeholder="L-3"/>
              <Fld label="Supervisor" value={slip.Supervisor} onChange={e=>setSlip(p=>({...p,Supervisor:e.target.value}))} error={errs.Supervisor} required/>
              <Fld label="Handed To" value={slip.Handed_To} onChange={e=>setSlip(p=>({...p,Handed_To:e.target.value}))} error={errs.Handed_To} required/>
            </div>
          </div>
          {/* Mode selector */}
          <div style={{display:'flex',gap:'8px',marginBottom:'14px',background:'#f0f1f4',padding:'6px',borderRadius:'8px'}}>
            {[['manual','✏️ Manual Roll'],['by_lay','🔢 By Lay Number'],['from_plan','📋 From Cut Plan']].map(([m,l])=>(
              <button key={m} onClick={()=>setSlip(p=>({...p,importMode:m}))}
                style={{flex:1,padding:'8px',background:slip.importMode===m?'white':'transparent',border:slip.importMode===m?'1px solid #e6e9ef':'1px solid transparent',borderRadius:'6px',fontWeight:slip.importMode===m?'800':'600',cursor:'pointer',fontSize:'12px',color:slip.importMode===m?'#323338':'#676879'}}>
                {l}
              </button>
            ))}
          </div>

          {/* ── MANUAL MODE ─────────────────────────────────────────────────── */}
          {slip.importMode==='manual' && (
            <div style={{background:'#e8f0ff',border:'1px solid #a8d1ff',borderRadius:'8px',padding:'14px',marginBottom:'14px'}}>
              <div style={{fontSize:'11px',fontWeight:'800',color:'#0062c4',marginBottom:'10px'}}>📦 SELECT ROLL TO ISSUE</div>
              <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:'10px'}}>
                <Fld label="Select Roll" value={slip.Item_Id} onChange={e=>setSlip(p=>({...p,Item_Id:e.target.value}))} error={errs.Item_Id}
                  options={items.filter(i=>(!slip.Style_Name||i.Style_Name===slip.Style_Name)&&(!slip.Order_No||i.Order_No===slip.Order_No)&&(!slip.Color||i.Color===slip.Color)&&parseFloat(i.Balance_Qty||0)>0).map(i=>({value:i.id,label:`${i.Roll_ID} — ${i.Color||''} ${i.Shade||''} (${parseFloat(i.Balance_Qty||0).toFixed(2)}m left)`}))} required/>
                <Fld label="Quantity (m)" value={slip.Qty} onChange={e=>setSlip(p=>({...p,Qty:e.target.value}))} type="number" error={errs.Qty} required/>
              </div>
              {selectedItem && (
                <div style={{display:'flex',gap:'14px',marginTop:'8px',fontSize:'12px',padding:'8px',background:'white',borderRadius:'6px',border:'1px solid #a8d1ff',alignItems:'center',flexWrap:'wrap'}}>
                  <span>Roll: <strong style={{color:'#8b5cf6'}}>{selectedItem.Roll_ID}</strong></span>
                  <span>Available: <strong style={{color:avail>0?'#00a65e':'#c93652'}}>{avail.toFixed(2)}m</strong></span>
                  <span>Rack: <strong>{selectedItem.Rack_No}/{selectedItem.Cell_No}</strong></span>
                  <StatusBadgeWithReason item={selectedItem}/>
                </div>
              )}
            </div>
          )}

          {/* ── BY LAY NUMBER MODE ──────────────────────────────────────────── */}
          {slip.importMode==='by_lay' && (
            <div style={{background:'#f0fdf4',border:'1px solid #b3f5d6',borderRadius:'8px',padding:'14px',marginBottom:'14px'}}>
              <div style={{fontSize:'11px',fontWeight:'800',color:'#15803d',marginBottom:'10px'}}>🔢 ISSUE BY LAY NUMBER</div>
              {cutPlanOpts.length===0 && <div style={{color:'#676879',fontSize:'12px'}}>No saved cut plans found for this Style/PO/Color. Save a plan in Production Planning → Cut Plan first.</div>}
              {cutPlanOpts.length>0 && (
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  <Fld label="Select Cut Plan" value={slip.cutPlanId} onChange={e=>handlePlanSelect(e.target.value)}
                    options={cutPlanOpts.map(p=>({value:p.id,label:`Plan ${new Date(p.savedAt||p.id).toLocaleDateString()} — ${(p.steps||[]).reduce((s,st)=>s+(st.laysCount||0),0)} lays`}))}/>
                  {slip.cutPlanId && (
                    <Fld label="Select Lay Number" value={slip.layNo} onChange={e=>handleLaySelect(e.target.value)}
                      options={cutPlanOpts.find(p=>String(p.id)===String(slip.cutPlanId))?
                        (cutPlanOpts.find(p=>String(p.id)===String(slip.cutPlanId)).steps||[]).flatMap(st=>(st.lays||[]).map(l=>l.layNo)):
                        []}/>
                  )}
                </div>
              )}
              {slip.layNo && (
                <div style={{marginTop:'10px',padding:'10px',background:'white',borderRadius:'6px',border:'1px solid #b3f5d6',fontSize:'12px'}}>
                  Lay {slip.layNo} rolls auto-filled: <strong>{slip.Remarks}</strong>
                  <div style={{marginTop:'6px',color:'#676879'}}>Total qty: <strong style={{color:'#00a65e'}}>{slip.Qty}m</strong></div>
                </div>
              )}
            </div>
          )}

          {/* ── FROM CUT PLAN MODE ──────────────────────────────────────────── */}
          {slip.importMode==='from_plan' && (
            <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'8px',padding:'14px',marginBottom:'14px'}}>
              <div style={{fontSize:'11px',fontWeight:'800',color:'#92400e',marginBottom:'10px'}}>📋 IMPORT ALL ROLLS FROM CUT PLAN</div>
              {cutPlanOpts.length===0 && <div style={{color:'#676879',fontSize:'12px'}}>No saved cut plans found. Save a plan in Production Planning → Cut Plan first.</div>}
              {cutPlanOpts.length>0 && (
                <Fld label="Select Cut Plan" value={slip.cutPlanId} onChange={e=>handlePlanSelect(e.target.value)}
                  options={cutPlanOpts.map(p=>({value:p.id,label:`Plan ${new Date(p.savedAt||p.id).toLocaleDateString()} — ${(p.steps||[]).reduce((s,st)=>s+(st.laysCount||0),0)} lays · ${p.totalPcs||0} pcs`}))}/>
              )}
              {selectedPlanRolls.length>0 && (
                <div style={{marginTop:'12px',overflowX:'auto'}}>
                  <div style={{fontSize:'11px',color:'#92400e',marginBottom:'6px',fontWeight:'700'}}>
                    {selectedPlanRolls.length} rolls will be issued in one click:
                  </div>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                    <thead><tr>{['Roll ID','Total Qty (m)','Lays','Rack/Cell','In Inventory'].map(h=><th key={h} style={{padding:'4px 8px',background:'#fff5e6',fontWeight:'700',textAlign:'left',fontSize:'10px'}}>{h}</th>)}</tr></thead>
                    <tbody>
                      {selectedPlanRolls.map((r,i)=>(
                        <tr key={i} style={{background:i%2===0?'white':'#fafafa',borderBottom:'1px solid #f0f1f4'}}>
                          <td style={{padding:'4px 8px',fontFamily:'monospace',color:'#8b5cf6',fontWeight:'700'}}>{r.rollId}</td>
                          <td style={{padding:'4px 8px',textAlign:'right',color:'#00a65e',fontWeight:'700'}}>{r.totalUsed.toFixed(2)}m</td>
                          <td style={{padding:'4px 8px',fontSize:'10px'}}>{r.lays.join(', ')}</td>
                          <td style={{padding:'4px 8px',fontSize:'10px'}}>{r.rack||'—'}/{r.cell||'—'}</td>
                          <td style={{padding:'4px 8px'}}>{r.invItem?<span style={{color:'#00a65e',fontWeight:'700'}}>✓ Found ({parseFloat(r.invItem.Balance_Qty||0).toFixed(1)}m left)</span>:<span style={{color:'#c93652'}}>✗ Not found</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div style={{marginTop:'10px',marginBottom:'14px'}}><Fld label="Remarks" value={slip.Remarks} onChange={e=>setSlip(p=>({...p,Remarks:e.target.value}))} placeholder="Instructions, special handling…"/></div>
          <div style={{display:'flex',gap:'12px'}}>
            {slip.importMode==='from_plan' ? (
              <button onClick={handleConfirmFromPlan} style={{flex:1,padding:'12px',background:'#fdab3d',color:'white',border:'none',borderRadius:'8px',fontWeight:'800',cursor:'pointer',fontSize:'14px'}}>
                📋 ISSUE ALL {selectedPlanRolls.length} ROLLS FROM PLAN
              </button>
            ) : (
              <button onClick={()=>{ if(validate()) onConfirm(slip); }} style={{flex:1,padding:'12px',background:'#00a65e',color:'white',border:'none',borderRadius:'8px',fontWeight:'800',cursor:'pointer',fontSize:'14px'}}>✅ CONFIRM ISSUE</button>
            )}
            <button onClick={onClose} style={{padding:'12px 24px',background:'#f0f1f4',color:'#676879',border:'1px solid #e6e9ef',borderRadius:'8px',fontWeight:'700',cursor:'pointer'}}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PART 4 — INVOICE READER (AI-powered)
// ============================================================================

function InvoiceReader({ onExtracted, category='Fabric' }) {
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const processFile = async (file) => {
    setLoading(true); setError(''); setExtracted(null);
    try {
      let fileData, fileType;
      if (file.type.match(/image.*/)) {
        fileData = await new Promise((res, rej) => {
          const r = new FileReader(); r.onload = e => res(e.target.result); r.onerror = rej; r.readAsDataURL(file);
        });
        fileType = 'image';
      } else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        // Load PDF.js on-demand
        if (!window._pdfjsLib) {
          await new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
            s.onload = () => { window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'; window._pdfjsLib = window.pdfjsLib; res(); };
            s.onerror = rej; document.head.appendChild(s);
          });
        }
        const buf = await file.arrayBuffer();
        const pdf = await window._pdfjsLib.getDocument({ data: buf }).promise;
        const page = await pdf.getPage(1);
        const vp = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        canvas.width = vp.width; canvas.height = vp.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
        fileData = canvas.toDataURL('image/jpeg', 0.82);
        fileType = 'image';
      } else {
        fileData = await new Promise((res, rej) => { const r = new FileReader(); r.onload = e => res(e.target.result); r.onerror = rej; r.readAsText(file); });
        fileType = 'text';
      }

      const res = await fetch(AI_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: fileData, fileType, docType: 'INVOICE', invoiceType: category })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setExtracted(data);
    } catch (e) {
      setError('Extraction failed: ' + e.message);
    }
    setLoading(false);
  };

  const handleFile = (e) => { const f = e.target.files[0]; if (f) processFile(f); e.target.value=''; };

  return (
    <div style={{...ss}}>
      <h3 style={hs}><Camera size={14}/> AI Invoice Reader — {category}</h3>
      <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'8px',padding:'14px',marginBottom:'18px',fontSize:'12px',color:'#78350f'}}>
        📷 Upload supplier invoice (image, PDF). AI extracts: Invoice No, Date, Supplier, Items, Qty, Rate, Roll details automatically.
      </div>
      <input ref={fileRef} type="file" accept="image/*,.pdf" style={{display:'none'}} onChange={handleFile}/>
      <div style={{display:'flex',gap:'12px',marginBottom:'18px'}}>
        <button onClick={()=>fileRef.current.click()} disabled={loading} style={{padding:'10px 24px',background:'#0073ea',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'700',display:'flex',alignItems:'center',gap:'8px',opacity:loading?0.6:1}}>
          {loading ? <><RefreshCw size={14} style={{animation:'spin 1s linear infinite'}}/> Reading Invoice…</> : <><Camera size={14}/> Upload Invoice</>}
        </button>
        {extracted && <button onClick={()=>onExtracted(extracted)} style={{padding:'10px 24px',background:'#00a65e',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'700'}}>✅ Use Extracted Data</button>}
        {extracted && <button onClick={()=>setExtracted(null)} style={{padding:'10px 16px',background:'#f0f1f4',color:'#676879',border:'1px solid #e6e9ef',borderRadius:'6px',cursor:'pointer'}}>Clear</button>}
      </div>
      {error && <div style={{padding:'12px',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:'6px',color:'#c93652',fontSize:'12px',marginBottom:'14px'}}>❌ {error}</div>}
      {extracted && (
        <div style={{background:'#f0fdf4',border:'1px solid #b3f5d6',borderRadius:'8px',padding:'16px'}}>
          <div style={{fontWeight:'800',fontSize:'13px',color:'#15803d',marginBottom:'12px'}}>✅ Extracted Invoice Data</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:'10px',fontSize:'12px'}}>
            {[['Invoice No',extracted.invoice_no||extracted.invoiceNo],['Date',extracted.date||extracted.invoice_date],['Supplier',extracted.supplier||extracted.supplier_name],['Total Qty',extracted.total_qty||extracted.qty],['Total Value',extracted.total_value||extracted.amount]].map(([l,v])=>v?(
              <div key={l} style={{background:'white',padding:'8px 12px',borderRadius:'6px',border:'1px solid #b3f5d6'}}>
                <div style={{fontSize:'10px',color:'#676879',fontWeight:'700',textTransform:'uppercase'}}>{l}</div>
                <div style={{fontWeight:'700',color:'#323338',marginTop:'2px'}}>{v}</div>
              </div>
            ):null)}
          </div>
          {(extracted.items||extracted.rolls||[]).length > 0 && (
            <div style={{marginTop:'12px'}}>
              <div style={{fontSize:'11px',fontWeight:'700',color:'#676879',marginBottom:'8px',textTransform:'uppercase'}}>Line Items</div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                  <thead><tr style={{background:'#e3faf0'}}>{['Item','Roll/Lot No','Qty','UOM','Rate','Amount'].map(h=><th key={h} style={{...TH,background:'#e3faf0'}}>{h}</th>)}</tr></thead>
                  <tbody>
                    {(extracted.items||extracted.rolls||[]).map((it,i)=>(
                      <tr key={i} style={{borderBottom:'1px solid #f0f1f4'}}>
                        <td style={TD}>{it.item_name||it.description||it.name||'—'}</td>
                        <td style={TD}>{it.roll_no||it.lot_no||'—'}</td>
                        <td style={{...TD,textAlign:'right'}}>{it.qty||it.quantity||'—'}</td>
                        <td style={TD}>{it.uom||it.unit||'—'}</td>
                        <td style={{...TD,textAlign:'right'}}>{it.rate||it.unit_price||'—'}</td>
                        <td style={{...TD,textAlign:'right'}}>{it.amount||it.total||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PART 5 — RACK / SHELF CONFIGURATOR + QR PRINTER
// ============================================================================

function RackConfigurator({ db, setDb }) {
  const [racks, setRacks] = useState(db?.rackLayout || []);
  const [form, setForm] = useState({ rackNo:'', shelves:'5', category:'Fabric', location:'', notes:'' });
  const [selected, setSelected] = useState(new Set());
  const [printMode, setPrintMode] = useState(false);
  const printRef = useRef();

  useEffect(() => { setRacks(db?.rackLayout || []); }, [db]);

  const addRack = () => {
    if (!form.rackNo) return alert('Rack No required');
    const shelvesList = Array.from({ length: parseInt(form.shelves) || 5 }, (_, i) => `S-${i + 1}`);
    const newRack = { id: Date.now(), ...form, shelvesList };
    const updated = [...racks, newRack];
    setRacks(updated); setDb(p => ({ ...p, rackLayout: updated }));
    saveDB('rack_layout', newRack);
    setForm({ rackNo:'', shelves:'5', category:'Fabric', location:'', notes:'' });
  };

  const delRack = (id) => {
    if (!window.confirm('Delete rack?')) return;
    const updated = racks.filter(r => r.id !== id);
    setRacks(updated); setDb(p => ({ ...p, rackLayout: updated }));
  };

  const toggleSelect = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const printSelected = () => {
    const printItems = racks.filter(r => selected.size === 0 || selected.has(r.id));
    if (!printItems.length) return alert('No racks to print');
    setPrintMode(true);
    setTimeout(() => { window.print(); setPrintMode(false); }, 300);
  };

  const qrData = (rack, shelf) =>
    `RACK:${rack.rackNo}|SHELF:${shelf}|CAT:${rack.category}|LOC:${rack.location}`;

  if (printMode) {
    const toPrint = racks.filter(r => selected.size === 0 || selected.has(r.id));
    return (
      <div style={{padding:'20px'}}>
        <style>{`@media print { body * { visibility:hidden } #print-area, #print-area * { visibility:visible } #print-area { position:absolute; top:0; left:0; width:100% } }`}</style>
        <div id="print-area" style={{display:'flex',flexWrap:'wrap',gap:'12px'}}>
          {toPrint.map(rack =>
            rack.shelvesList.map(shelf => (
              <div key={rack.rackNo+shelf} style={{border:'1px solid #e6e9ef',borderRadius:'8px',padding:'12px',textAlign:'center',width:'140px'}}>
                <QRImg data={qrData(rack,shelf)} label={`${rack.rackNo} / ${shelf}`} size={100}/>
                <div style={{fontSize:'11px',fontWeight:'700',marginTop:'6px'}}>{rack.category}</div>
                <div style={{fontSize:'10px',color:'#676879'}}>{rack.location}</div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'360px 1fr',gap:'20px'}}>
        <div style={ss}>
          <h3 style={hs}><MapPin size={14}/> Add Rack / Shelf</h3>
          <Fld label="Rack No" value={form.rackNo} onChange={e=>setForm(p=>({...p,rackNo:e.target.value}))} required placeholder="e.g. R-01"/>
          <Fld label="Number of Shelves" value={form.shelves} onChange={e=>setForm(p=>({...p,shelves:e.target.value}))} type="number" style={{marginTop:'10px'}}/>
          <Fld label="Category" value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))} options={['Fabric','Trims','Accessories','Packing','Mixed']} style={{marginTop:'10px'}}/>
          <Fld label="Location / Zone" value={form.location} onChange={e=>setForm(p=>({...p,location:e.target.value}))} placeholder="e.g. Zone-A, Bay-2" style={{marginTop:'10px'}}/>
          <Fld label="Notes" value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Optional notes" style={{marginTop:'10px'}}/>
          <button onClick={addRack} style={{width:'100%',marginTop:'14px',padding:'11px',background:'#0073ea',color:'white',border:'none',borderRadius:'6px',fontWeight:'800',cursor:'pointer'}}>+ Add Rack</button>
        </div>
        <div style={ss}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'14px',flexWrap:'wrap',gap:'8px'}}>
            <h3 style={{...hs,marginBottom:0}}><MapPin size={14}/> Rack Layout ({racks.length} racks)</h3>
            <div style={{display:'flex',gap:'8px'}}>
              <button onClick={()=>setSelected(new Set(racks.map(r=>r.id)))} style={{padding:'7px 12px',background:'#e8f0ff',color:'#0073ea',border:'1px solid #a8d1ff',borderRadius:'6px',cursor:'pointer',fontSize:'12px'}}>Select All</button>
              <button onClick={()=>setSelected(new Set())} style={{padding:'7px 12px',background:'#f0f1f4',color:'#676879',border:'1px solid #e6e9ef',borderRadius:'6px',cursor:'pointer',fontSize:'12px'}}>Clear</button>
              <button onClick={printSelected} style={{padding:'7px 14px',background:'#1f1f1f',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700',display:'flex',alignItems:'center',gap:'6px'}}>
                <Printer size={13}/> Print QR {selected.size>0?`(${selected.size})`:'(All)'}
              </button>
            </div>
          </div>
          {!racks.length && <div style={{textAlign:'center',color:'#9699a6',padding:'40px',fontSize:'13px'}}>No racks configured. Add your first rack using the form.</div>}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:'14px'}}>
            {racks.map(rack => {
              const sel = selected.has(rack.id);
              const catColor = rack.category==='Fabric'?'#8b5cf6':rack.category==='Trims'?'#0073ea':rack.category==='Accessories'?'#fdab3d':'#00a65e';
              return (
                <div key={rack.id} onClick={()=>toggleSelect(rack.id)} style={{border:`2px solid ${sel?catColor:'#e6e9ef'}`,borderRadius:'10px',padding:'14px',cursor:'pointer',background:sel?catColor+'0a':'white',transition:'all 0.15s'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'8px'}}>
                    <div style={{fontWeight:'800',fontSize:'15px',color:'#323338'}}>{rack.rackNo}</div>
                    <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                      <input type="checkbox" checked={sel} onChange={()=>toggleSelect(rack.id)} onClick={e=>e.stopPropagation()}/>
                      <button onClick={e=>{e.stopPropagation();delRack(rack.id)}} style={{background:'none',border:'none',color:'#c93652',cursor:'pointer',padding:'2px'}}><Trash2 size={13}/></button>
                    </div>
                  </div>
                  <div style={{fontSize:'11px',color:catColor,fontWeight:'700',background:catColor+'18',padding:'2px 8px',borderRadius:'4px',display:'inline-block',marginBottom:'6px'}}>{rack.category}</div>
                  <div style={{fontSize:'11px',color:'#676879',marginBottom:'6px'}}>{rack.location||'—'}</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'4px'}}>
                    {(rack.shelvesList||[]).map(sh => (
                      <span key={sh} style={{background:'#f0f1f4',padding:'2px 6px',borderRadius:'4px',fontSize:'10px',fontWeight:'700',color:'#676879'}}>{sh}</span>
                    ))}
                  </div>
                  <div style={{marginTop:'8px',fontSize:'10px',color:'#9699a6'}}>{(rack.shelvesList||[]).length} shelves</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PART 6 — KANBAN DASHBOARD — Consumption-aware, connected to DB coverage
// ============================================================================

function KanbanDashboard({ fabric, trims, accessories, packing, db }) {
  const [kanbanGroup, setKanbanGroup] = useState('style');

  // ── Coverage tree from stored consumption ──────────────────────────────────
  // buildCoverageTree reads db.mgmtStyles[].fabric_consumption + poSizeTable
  const coverageTree = useMemo(() => buildCoverageTree(db, fabric), [db, fabric]);

  // Quick lookup: styleRef → coverage entry
  const covByStyle = useMemo(() => {
    const m = {};
    coverageTree.forEach(s => { m[s.styleRef] = s; });
    return m;
  }, [coverageTree]);

  // ── Aggregate style/PO/color summary ──────────────────────────────────────
  const styleSummary = useMemo(() => {
    const allRaw = [
      ...fabric.map(i=>({...i,_cat:'Fabric'})),
      ...trims.map(i=>({...i,_cat:'Trims'})),
      ...accessories.map(i=>({...i,_cat:'Accessories'})),
      ...packing.map(i=>({...i,_cat:'Packing'})),
    ];
    const map = {};
    allRaw.forEach(item => {
      const style = item.Style_Name||'(No Style)';
      const po    = item.Order_No||'—';
      const color = item.Color||'—';
      const key   = `${style}|||${po}|||${color}`;
      if (!map[key]) {
        const regQty = getPOQty(db, style, po);
        map[key] = { style, po, color, orderQty: regQty||0, cats:{}, items:[] };
      }
      const g = map[key];
      if (!g.cats[item._cat]) g.cats[item._cat] = { ordered:0, received:0, issued:0, balance:0, required:0, rtv:0, recvDate:'', qcStatus:'PENDING', defects:0 };
      const cat = g.cats[item._cat];
      cat.ordered   += parseFloat(item.Ordered_Qty||0);
      cat.received  += parseFloat(item.Received_Qty||item.M_Qty||0);
      cat.issued    += parseFloat(item.Issued_Qty||item.Issue_Qty||0);
      cat.balance   += parseFloat(item.Balance_Qty||0);
      cat.required  += parseFloat(item.Required_Qty||0);
      cat.rtv       += getRTVQty(db, item);
      if (item.Invoice_Rcv_Date && item.Invoice_Rcv_Date > cat.recvDate) cat.recvDate = item.Invoice_Rcv_Date;
      const qcS = item._cat === 'Fabric' ? getRollQCStatus(db, item) : getTrimQCStatus(db, item);
      if (qcS === 'FAIL') cat.qcStatus = 'FAIL';
      else if (qcS === 'PASS' && cat.qcStatus === 'PENDING') cat.qcStatus = 'PASS';
      cat.defects += getRollDefects(db, item);
      g.items.push(item);
    });

    // Enrich each group with consumption-based coverage
    return Object.values(map).map(group => {
      const cov = covByStyle[group.style];
      const consCfg = cov ? (getStyleConsumption(db, group.style) || {}) : {};
      const defCons = parseFloat(consCfg.default || 0);
      const wastePct = parseFloat(consCfg.wastage_pct || 5) / 100;
      const effCons = defCons > 0 ? defCons * (1 + wastePct) : 0;

      // Fabric balance for this style/PO/color
      const fabCat = group.cats['Fabric'];
      const fabBalance = fabCat ? fabCat.balance : 0;

      // Required fabric from PO qty × effective consumption
      const requiredFabric = group.orderQty > 0 && effCons > 0 ? group.orderQty * effCons : 0;
      const coveragePct    = requiredFabric > 0 ? (fabBalance / requiredFabric * 100) : null;
      const possiblePcs    = effCons > 0 ? Math.floor(fabBalance / effCons) : null;
      const shortExcess    = requiredFabric > 0 ? fabBalance - requiredFabric : null;
      const covStatus      = coveragePct === null ? null
                           : coveragePct < 80  ? 'critical'
                           : coveragePct < 95  ? 'warning'
                           : coveragePct < 110 ? 'ok'
                           : 'excess';

      return { ...group, defCons, effCons, requiredFabric, coveragePct, possiblePcs, shortExcess, covStatus };
    });
  }, [fabric, trims, accessories, packing, db, covByStyle]);

  // ── Coverage-aware kanban groups ──────────────────────────────────────────
  const kanbanGroups = useMemo(() => {
    const map = {};
    [...fabric,...trims,...accessories,...packing].forEach(item => {
      const key = kanbanGroup==='style' ? (item.Style_Name||'No Style')
                : kanbanGroup==='po'    ? (item.Order_No||'No PO')
                : (item.Color||'No Color');
      if (!map[key]) map[key] = { label:key, items:[], styles:new Set() };
      map[key].items.push(item);
      if (item.Style_Name) map[key].styles.add(item.Style_Name);
    });
    return Object.values(map).map(g => {
      const totalBal = g.items.reduce((s,i)=>s+(parseFloat(i.Balance_Qty)||0),0);

      // Coverage-based status: use stored consumption if available
      let covStatus = null;
      if (kanbanGroup === 'style') {
        const cov = covByStyle[g.label];
        if (cov) covStatus = cov.status;
      } else {
        // Aggregate coverage across all styles in this group
        const statuses = [...g.styles].map(sRef => covByStyle[sRef]?.status).filter(Boolean);
        if (statuses.includes('critical')) covStatus = 'critical';
        else if (statuses.includes('warning')) covStatus = 'warning';
        else if (statuses.every(s => s==='excess')) covStatus = 'excess';
        else if (statuses.length) covStatus = 'ok';
      }

      // Fall back to roll-level status if no consumption data
      const rollStatuses = g.items.map(i => getStockStatus(i));
      const rollAgg = rollStatuses.includes('critical') ? 'critical'
                    : rollStatuses.includes('low') ? 'low'
                    : rollStatuses.every(s=>s==='excess') ? 'excess' : 'adequate';

      // Coverage status maps: critical→critical, warning→low, ok→adequate, excess→excess
      const statusMap = { critical:'critical', warning:'low', ok:'adequate', excess:'excess' };
      const finalStatus = covStatus ? (statusMap[covStatus] || 'adequate') : rollAgg;

      const reqFromCov = [...g.styles].reduce((s, sRef) => {
        const cov = covByStyle[sRef];
        return s + (cov?.required || 0);
      }, 0);
      const pct = reqFromCov > 0 ? Math.min(100,(totalBal/reqFromCov)*100)
                : (totalBal>0 ? 100 : 0);

      return { ...g, status:finalStatus, pct, totalBal, totalReq:reqFromCov, covStatus,
        critCount: g.items.filter(i=>getStockStatus(i)==='critical').length };
    }).sort((a,b)=>({critical:0,low:1,adequate:2,excess:3}[a.status]-{critical:0,low:1,adequate:2,excess:3}[b.status]));
  }, [fabric,trims,accessories,packing,kanbanGroup,covByStyle]);

  const byStatus = { critical:[], low:[], adequate:[], excess:[] };
  kanbanGroups.forEach(g => byStatus[g.status].push(g));

  const allItems = [...fabric,...trims,...accessories,...packing];
  const catColor  = {Fabric:'#8b5cf6',Trims:'#0073ea',Accessories:'#fdab3d',Packing:'#00a65e'};

  // Coverage-based counts (preferred over roll-level)
  const hasCovData = coverageTree.some(s => s.hasConsumption);
  const covCritical = coverageTree.filter(s => s.status==='critical').length;
  const covWarning  = coverageTree.filter(s => s.status==='warning').length;
  const covOk       = coverageTree.filter(s => s.status==='ok' || s.status==='excess').length;
  const covNoSetup  = coverageTree.filter(s => !s.hasConsumption).length;

  const KanbanCard = ({ group }) => {
    const s = C[group.status];
    const hasCov = group.covStatus !== null;
    return (
      <div style={{background:'white',borderRadius:'8px',padding:'12px',border:`1px solid ${s.border}`,marginBottom:'8px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px'}}>
          <div style={{fontWeight:'800',fontSize:'13px',color:'#323338',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}} title={group.label}>{group.label}</div>
          {hasCov ? <CoverageBadge status={group.covStatus} compact/> : <Badge status={group.status}/>}
        </div>
        <div style={{background:'#f0f1f4',borderRadius:'4px',height:'5px',marginBottom:'8px'}}>
          <div style={{width:`${group.pct}%`,background:s.text,borderRadius:'4px',height:'100%'}}/>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',fontSize:'11px',color:'#676879',marginBottom:'4px'}}>
          <span>Bal: <strong style={{color:s.text}}>{group.totalBal.toFixed(0)}</strong></span>
          <span>Req: <strong>{group.totalReq>0?group.totalReq.toFixed(0):'—'}</strong></span>
          <span>{group.items.length} items</span>
        </div>
        {group.critCount>0 && <div style={{background:'#fde8eb',color:'#c93652',padding:'2px 6px',borderRadius:'4px',fontSize:'10px',fontWeight:'700',marginTop:'4px'}}>{group.critCount} Critical Rolls</div>}
      </div>
    );
  };

  const KanbanCol = ({ status, groups }) => {
    const s = C[status];
    return (
      <div style={{flex:'1 1 200px',minWidth:'190px',maxWidth:'300px'}}>
        <div style={{padding:'9px 12px',borderRadius:'8px 8px 0 0',fontWeight:'800',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.05em',background:s.bg,color:s.text,border:`1px solid ${s.border}`,borderBottom:'none',display:'flex',justifyContent:'space-between'}}>
          <span>{s.label}</span>
          <span style={{background:s.badge,border:`1px solid ${s.border}`,borderRadius:'20px',padding:'2px 7px',fontSize:'12px'}}>{groups.length}</span>
        </div>
        <div style={{border:`1px solid ${s.border}`,borderTop:'none',borderRadius:'0 0 8px 8px',background:s.bg,minHeight:'280px',padding:'8px'}}>
          {!groups.length && <div style={{textAlign:'center',color:'#9699a6',fontSize:'12px',paddingTop:'40px'}}>No groups</div>}
          {groups.map((g,i)=><KanbanCard key={i} group={g}/>)}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* ── STAT ROW — Coverage-aware when consumption is set ─────────────── */}
      {hasCovData ? (
        <>
          <div style={{background:'#323338',borderRadius:'10px',padding:'12px 16px',marginBottom:'14px',display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
            <span style={{color:'white',fontWeight:'800',fontSize:'13px'}}>📊 Production Coverage</span>
            <span style={{color:'#9699a6',fontSize:'12px'}}>Based on consumption data set in Fabric → Reconciliation → ⚙ Setup</span>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:'14px',marginBottom:'20px'}}>
            <StatCard label="🔴 Critical Short" value={covCritical} sub="Fabric < 80% of need" icon={AlertTriangle} color="#c93652"/>
            <StatCard label="🟡 Warning" value={covWarning} sub="Fabric 80-95% of need" icon={AlertCircle} color="#fdab3d"/>
            <StatCard label="🟢 OK / Excess" value={covOk} sub="Coverage ≥ 95%" icon={CheckCircle} color="#00a65e"/>
            <StatCard label="⚙ No Consumption Set" value={covNoSetup} sub="Cannot evaluate" icon={Info} color="#9699a6"/>
            <StatCard label="Total Rolls" value={fabric.length} sub="Fabric rolls in store" icon={Layers} color="#8b5cf6"/>
            <StatCard label="Total Fabric" value={`${fabric.reduce((s,r)=>s+parseFloat(r.Balance_Qty||0),0).toFixed(0)}m`} sub="Balance in inventory" icon={Package} color="#0073ea"/>
          </div>
        </>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:'14px',marginBottom:'20px'}}>
          <StatCard label="Total Items" value={allItems.length} icon={Package} color="#0073ea"/>
          <StatCard label="Critical Rolls" value={allItems.filter(i=>getStockStatus(i)==='critical').length} sub="Roll-level check" icon={AlertCircle} color="#c93652"/>
          <StatCard label="Styles" value={new Set(allItems.map(i=>i.Style_Name).filter(Boolean)).size} icon={Layers} color="#8b5cf6"/>
          <div style={{...ss,marginBottom:0,border:'1px solid #fde68a',background:'#fffbeb',display:'flex',alignItems:'center',gap:'8px',padding:'12px'}}>
            <span style={{fontSize:'12px',color:'#78350f'}}>⚙ Set consumption per style in <strong>Fabric → Reconciliation → Setup</strong> to see production coverage here</span>
          </div>
        </div>
      )}

      {/* ── STYLE-WISE SUMMARY TABLE ──────────────────────────────────────── */}
      <div style={ss}>
        <h3 style={{...hs}}><BarChart3 size={15}/> Style-wise Inventory Summary</h3>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr>{['Style','PO','Color','Category','Order Qty','Received','Issued','Balance','Reqd Fabric','Coverage','Possible Pcs','Short/Excess','QC Status','Recv Date'].map(h=>(
                <th key={h} style={{...TH,background:h==='Coverage'||h==='Possible Pcs'||h==='Short/Excess'?'#f5f3ff':undefined}}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {!styleSummary.length && <tr><td colSpan={14} style={{...TD,textAlign:'center',color:'#9699a6',padding:'30px'}}>No inventory data yet</td></tr>}
              {styleSummary.map((group, gi) =>
                Object.entries(group.cats).map(([cat, catData], ci) => {
                  const balRcv = Math.max(0, catData.ordered - catData.received);
                  const isFirst = ci === 0;
                  const nCats   = Object.keys(group.cats).length;
                  const cc      = catColor[cat]||'#676879';
                  const isFab   = cat === 'Fabric';
                  const covColor = group.covStatus==='critical'?'#c93652':group.covStatus==='warning'?'#fdab3d':group.covStatus==='ok'?'#00a65e':group.covStatus==='excess'?'#0073ea':'#9699a6';
                  return (
                    <tr key={`${gi}-${ci}`} style={{background:gi%2===0?'white':'#fafafa',borderBottom:'1px solid #f0f1f4'}}>
                      {isFirst && <>
                        <td style={{...TD,fontWeight:'800',color:'#8b5cf6',verticalAlign:'top'}} rowSpan={nCats}>{group.style}</td>
                        <td style={{...TD,color:'#0073ea',verticalAlign:'top'}} rowSpan={nCats}>{group.po}</td>
                        <td style={{...TD,verticalAlign:'top'}} rowSpan={nCats}>{group.color}</td>
                      </>}
                      <td style={TD}><span style={{background:cc+'18',color:cc,padding:'1px 6px',borderRadius:'4px',fontSize:'10px',fontWeight:'700'}}>{cat}</span></td>
                      <td style={{...TD,textAlign:'right',fontWeight:'700'}}>{group.orderQty||'—'}</td>
                      <td style={{...TD,textAlign:'right',background:'#f0fdf4'}}>{catData.received.toFixed(2)}</td>
                      <td style={{...TD,textAlign:'right',background:'#fefce8'}}>{catData.issued.toFixed(0)}</td>
                      <td style={{...TD,textAlign:'right',fontWeight:'800',color:'#00a65e'}}>{catData.balance.toFixed(2)}</td>
                      {/* Consumption-based columns — only meaningful for Fabric */}
                      <td style={{...TD,textAlign:'right',color:'#8b5cf6',background:'#faf5ff'}}>
                        {isFab && group.requiredFabric>0 ? group.requiredFabric.toFixed(2)+'m' : '—'}
                      </td>
                      <td style={{...TD,textAlign:'right',fontWeight:'800',color:covColor,background:'#faf5ff'}}>
                        {isFab && group.coveragePct!==null ? (
                          <>{group.coveragePct.toFixed(1)}%
                            {group.covStatus && <span style={{marginLeft:'4px',fontSize:'10px'}}>{group.covStatus==='critical'?'🔴':group.covStatus==='warning'?'🟡':group.covStatus==='ok'?'🟢':'🔵'}</span>}
                          </>
                        ) : isFab && !group.defCons ? <span style={{color:'#9699a6',fontSize:'10px'}}>⚙ Setup needed</span> : '—'}
                      </td>
                      <td style={{...TD,textAlign:'right',fontWeight:'700',background:'#faf5ff',color:group.covStatus==='critical'?'#c93652':'#323338'}}>
                        {isFab && group.possiblePcs!==null ? group.possiblePcs+' pcs' : '—'}
                      </td>
                      <td style={{...TD,textAlign:'right',fontWeight:'800',background:'#faf5ff',
                          color:group.shortExcess!==null?(group.shortExcess<0?'#c93652':'#00a65e'):'#9699a6'}}>
                        {isFab && group.shortExcess!==null ? (group.shortExcess>=0?'+':'')+group.shortExcess.toFixed(2)+'m' : '—'}
                      </td>
                      <td style={TD}><QCBadge status={catData.qcStatus}/></td>
                      <td style={TD}>{catData.recvDate||'—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!hasCovData && styleSummary.length>0 && (
          <div style={{padding:'10px 12px',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'0 0 8px 8px',fontSize:'12px',color:'#78350f'}}>
            💡 Coverage%, Possible Pcs and Short/Excess columns require consumption to be set. Go to <strong>Fabric → Reconciliation → ⚙ Consumption Setup</strong>.
          </div>
        )}
      </div>

      {/* ── KANBAN BOARD ─────────────────────────────────────────────────── */}
      <div style={ss}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px',flexWrap:'wrap',gap:'10px'}}>
          <div>
            <h3 style={{...hs,marginBottom:0}}><LayoutDashboard size={15}/> Kanban — Status by Group</h3>
            {hasCovData && <div style={{fontSize:'11px',color:'#676879',marginTop:'2px'}}>Status based on production coverage % (consumption-aware)</div>}
          </div>
          <div style={{display:'flex',gap:'6px'}}>
            {[{id:'style',l:'By Style'},{id:'po',l:'By PO'},{id:'color',l:'By Color'}].map(g=>(
              <button key={g.id} onClick={()=>setKanbanGroup(g.id)} style={{padding:'6px 12px',borderRadius:'6px',border:'1px solid #e6e9ef',background:kanbanGroup===g.id?'#0073ea':'white',color:kanbanGroup===g.id?'white':'#676879',fontWeight:'700',fontSize:'12px',cursor:'pointer'}}>{g.l}</button>
            ))}
          </div>
        </div>
        <div style={{display:'flex',gap:'12px',overflowX:'auto',paddingBottom:'10px',flexWrap:'wrap'}}>
          {['critical','low','adequate','excess'].map(s=>(
            <KanbanCol key={s} status={s} groups={byStatus[s]}/>
          ))}
        </div>
      </div>

      {/* ── CATEGORY CARDS ──────────────────────────────────────────────── */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:'14px'}}>
        {[{l:'Fabric',items:fabric,c:'#8b5cf6',icon:Layers},{l:'Trims',items:trims,c:'#0073ea',icon:Tag},{l:'Accessories',items:accessories,c:'#fdab3d',icon:Scissors},{l:'Packing',items:packing,c:'#00a65e',icon:Box}].map(({l,items,c,icon:Icon})=>{
          const crit=items.filter(i=>getStockStatus(i)==='critical').length;
          const low =items.filter(i=>getStockStatus(i)==='low').length;
          return (
            <div key={l} style={{...ss,marginBottom:0}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'12px'}}>
                <div style={{width:'34px',height:'34px',borderRadius:'8px',background:c+'18',display:'flex',alignItems:'center',justifyContent:'center'}}><Icon size={17} color={c}/></div>
                <div><div style={{fontWeight:'800',fontSize:'13px',color:'#323338'}}>{l}</div><div style={{fontSize:'11px',color:'#676879'}}>{items.length} items</div></div>
              </div>
              <div style={{display:'flex',gap:'6px',fontSize:'11px'}}>
                {[{n:crit,l:'Critical',bg:'#fef2f2',c:'#c93652'},{n:low,l:'Low',bg:'#fffbeb',c:'#fdab3d'},{n:items.length-crit-low,l:'OK',bg:'#f0fdf4',c:'#00a65e'}].map(x=>(
                  <div key={x.l} style={{flex:1,textAlign:'center',padding:'6px',borderRadius:'6px',background:x.bg,color:x.c}}>
                    <div style={{fontWeight:'800',fontSize:'17px'}}>{x.n}</div><div>{x.l}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ============================================================================
// PART 7 — GENERIC INVENTORY MODULE (Trims / Accessories / Packing)
// ============================================================================

function InventoryModule({ category, color, icon:CatIcon, db, onSave, onDelete }) {
  const isTablet = useIsTablet();
  const [subTab, setSubTab] = useState('inventory');
  // Hydrate from db once on mount; after that local state is the source of truth.
  // All mutations (save/delete/issue) go through setItems + onSave/onDelete which
  // update the parent db. We intentionally do NOT re-sync from db after hydration
  // so that server refreshes in the parent App cannot resurrect deleted items.
  const hydrated = useRef(false);
  const [items,  setItems]  = useState(()=> db||[]);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [selIds, setSelIds] = useState(new Set());
  const [showReq, setShowReq] = useState(false);
  const [issueLogs, setIssueLogs] = useState([]);
  const [lastSaved, setLastSaved] = useState(null);
  const fileRef = useRef();
  const localOp = useRef(false); // prevents db-sync effect overwriting state after local mutations

  const masterDB  = window.__masterDB||{};
  const styleRefs = getStyleRefs(db);
  const sourcingItems = masterDB.sourcing||[];
  const srcNames   = [...new Set(sourcingItems.map(x=>x.itemName||x.name).filter(Boolean))];
  const srcSupps   = [...new Set(sourcingItems.map(x=>x.supplierName||x.supplier).filter(Boolean))];
  const uomOpts    = (masterDB.uoms||[]).map(x=>x.name);
  const trimOpts   = (masterDB.trimTypes||[]).map(x=>x.name);

  // Sync ONLY when the parent loads fresh data from the server for the first time.
  // Always filter _deleted records so soft-deleted items never resurface after refresh.
  useEffect(()=>{
    if (hydrated.current) return;
    if (localOp.current) { localOp.current = false; return; }
    const incoming = (db||[]).filter(r => !r._deleted);
    if (incoming.length > 0) {
      hydrated.current = true;
      setItems(incoming);
    } else if ((db||[]).length > 0) {
      hydrated.current = true;
      setItems([]);
    }
  },[db]);

  const blank = {
    id:null,SKU_ID:'',HSN_Code:'',Item_Name:'',Category:category,
    Sub_Category:'',Supplier:'',UOM:'',Style_Name:'',Order_No:'',Color:'',Size:'',
    Rate:'',Required_Qty:'',Wastage_Pct:'',Wastage_Qty:0,
    Ordered_Qty:'',Received_Qty:'',Issued_Qty:'',Balance_Qty:0,
    Difference:0,Daily_Consumption:'',Days_Coverage:0,
    Rack_No:'',Shelf_No:'',Remarks:'',Quality_Status:'Pending',RTV_Qty:0
  };
  const [form, setForm] = useState(blank);
  const [fErrs, setFErrs] = useState({});
  const [poOpts, setPoOpts] = useState([]);
  const [colorOpts, setColorOpts] = useState([]);
  const [sizeOpts, setSizeOpts] = useState([]);

  const recalc = f => {
    const req=parseFloat(f.Required_Qty)||0, wPct=parseFloat(f.Wastage_Pct)||0;
    const rcv=parseFloat(f.Received_Qty)||0, iss=parseFloat(f.Issued_Qty)||0;
    const daily=parseFloat(f.Daily_Consumption)||1;
    const wQty=+(req*wPct/100).toFixed(3);
    const bal=+(rcv-iss).toFixed(3);
    const diff=+((req+wQty)-rcv).toFixed(3);
    const days=daily>0?+(bal/daily).toFixed(1):0;
    return {...f,Wastage_Qty:wQty,Balance_Qty:bal,Difference:diff,Days_Coverage:days};
  };

  const hc = (field, val) => {
    const v = val?.target ? val.target.value : val;
    setForm(f => {
      let nf = recalc({...f,[field]:v});
      if (field==='Item_Name') {
        const hsn=getHSN(v); if(hsn&&!f.HSN_Code) nf.HSN_Code=hsn;
        const src=sourcingItems.find(x=>(x.itemName||x.name||'').toLowerCase()===v.toLowerCase());
        if(src){nf.Supplier=src.supplierName||src.supplier||nf.Supplier;nf.Rate=src.rate||src.price||nf.Rate;}
      }
      return nf;
    });
    setFErrs(e=>({...e,[field]:''}));
  };

  // When style changes, pull PO/color/size from Style Registry
  const handleStyleChange = (val) => {
    const pos = getPOsForStyle(db, val);
    setPoOpts(pos);
    const cols = getColorsForPO(db, val, pos[0]);
    setColorOpts(cols);
    const sizes = getSizesForPO(db, val, pos[0]);
    setSizeOpts(sizes);
    setForm(f => recalc({...f, Style_Name:val, Order_No:pos[0]||'', Color:cols[0]||''}));
  };

  const handlePOChange = (val) => {
    const cols = getColorsForPO(db, form.Style_Name, val);
    setColorOpts(cols);
    const sizes = getSizesForPO(db, form.Style_Name, val);
    setSizeOpts(sizes);
    setForm(f => recalc({...f, Order_No:val, Color:cols[0]||''}));
  };

  const genSKU = () => setForm(f=>({...f,SKU_ID:`${category.slice(0,3).toUpperCase()}-${Date.now().toString().slice(-6)}`}));

  const validate = () => {
    const e={};
    if (!form.Item_Name) e.Item_Name='Required';
    if (!form.SKU_ID)    e.SKU_ID='Required';
    if (srcNames.length && !srcNames.includes(form.Item_Name))
      e.Item_Name=`"${form.Item_Name}" not in Sourcing. Add it there first.`;
    if (form.Supplier && srcSupps.length && !srcSupps.includes(form.Supplier))
      e.Supplier='Supplier not found in Sourcing module.';
    if (form.Style_Name && styleRefs.length && !styleRefs.includes(form.Style_Name))
      e.Style_Name=`Style "${form.Style_Name}" not in Style Registry.`;
    const rcv=parseFloat(form.Received_Qty)||0, iss=parseFloat(form.Issued_Qty)||0;
    if (iss>rcv) e.Issued_Qty=`Cannot exceed received (${rcv})`;
    setFErrs(e);
    return !Object.keys(e).length;
  };

  const handleSave = async (addNew=false) => {
    if (!validate()) return;
    const entry={...form,id:editId||Date.now()};
    const newItems=editId?items.map(i=>i.id===editId?entry:i):[entry,...items];
    localOp.current = true;
    setItems(newItems); setLastSaved(entry);
    if (!addNew){ setEditId(null); setForm(blank); }
    else { setForm({...blank,Style_Name:form.Style_Name,Order_No:form.Order_No,Color:form.Color}); setEditId(null); }
    onSave&&onSave(newItems);
    await saveDB('inventory_item',entry);
    if (!addNew) alert('Saved!');
  };

  const handleDelete = id => {
    if (!window.confirm('Delete?')) return;
    localOp.current = true;
    const n=items.filter(i=>i.id!==id);
    setItems(n);
    onDelete&&onDelete(n);
    if (lastSaved?.id===id) setLastSaved(null);
    deleteFromDB('inventory_item', id);
  };

  const toggleSel = id => setSelIds(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});
  const toggleAll = () => setSelIds(selIds.size===filtered.length?new Set():new Set(filtered.map(i=>i.id)));
  const bulkDelete = () => {
    if (!selIds.size) return;
    if (!window.confirm(`Delete ${selIds.size} items?`)) return;
    localOp.current = true;
    const n=items.filter(i=>!selIds.has(i.id));
    setItems(n);
    onDelete&&onDelete(n);
    selIds.forEach(id => deleteFromDB('inventory_item', id));
    setSelIds(new Set());
  };

  const handleBulkUpload = e => {
    const file=e.target.files[0]; if(!file) return;
    const reader=new FileReader();
    reader.onload=async evt=>{
      const rows=evt.target.result.split(/\r?\n/).map(r=>r.split(','));
      const hdr=rows[0].map(h=>h.trim().replace(/"/g,''));
      const dataRows=rows.slice(1).filter(r=>r.length>=2&&r[0].trim());
      const validStyles=new Set(styleRefs);
      const errs=[]; const newItems=[];
      dataRows.forEach((cols,idx)=>{
        const obj={...blank,id:Date.now()+idx,Category:category};
        hdr.forEach((h,i)=>{ if(cols[i]!==undefined&&blank.hasOwnProperty(h)) obj[h]=cols[i].trim().replace(/"/g,''); });
        if (obj.Style_Name&&validStyles.size&&!validStyles.has(obj.Style_Name)){
          errs.push(`Row ${idx+2}: Style "${obj.Style_Name}" not in registry — skipped`); return;
        }
        const rcv=parseFloat(obj.Received_Qty)||0, iss=parseFloat(obj.Issued_Qty)||0;
        if(iss>rcv){obj.Issued_Qty=rcv.toString();errs.push(`Row ${idx+2}: Issued capped to ${rcv}`);}
        if(!obj.HSN_Code) obj.HSN_Code=getHSN(obj.Item_Name);
        obj.Category=category;
        newItems.push(recalc(obj));
      });
      if(errs.length) alert(`⚠ Import notes:\n${errs.slice(0,10).join('\n')}`);
      if(!newItems.length) return alert('No valid rows.');
      const merged=[...newItems,...items]; setItems(merged); onSave&&onSave(merged);
      for(const it of newItems) await saveDB('inventory_item',it);
      alert(`✅ ${newItems.length} items imported!`);
    };
    reader.readAsText(file); e.target.value='';
  };

  const downloadTemplate = () => {
    const h=['Item_Name','SKU_ID','HSN_Code','Sub_Category','Supplier','UOM','Style_Name','Order_No','Color','Size','Rate','Required_Qty','Wastage_Pct','Ordered_Qty','Received_Qty','Issued_Qty','Daily_Consumption','Rack_No','Shelf_No','Remarks'];
    const ex=['Button 12mm','TRM-001001','9606210000','Buttons','ABC Traders','Pcs','STYLE-01','PO-001','Navy','M','2.50','5000','2','5100','5100','0','200','R1','S-3','Sample'];
    const csv=[h.join(','),ex.join(',')].join('\n');
    const a=document.createElement('a');a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));a.download=`${category}_template.csv`;a.click();
  };

  const exportData = () => {
    if(!items.length) return alert('No data');
    const keys=Object.keys(blank).filter(k=>k!=='id');
    const csv=[keys.join(','),...items.map(i=>keys.map(k=>(i[k]??'').toString().replace(/,/g,';')).join(','))].join('\n');
    const a=document.createElement('a');a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));a.download=`${category}_export.csv`;a.click();
  };

  const handleIssueConfirm = async slip => {
    const item=items.find(i=>i.id===parseInt(slip.Item_Id));
    if(!item) return;
    const qty=parseFloat(slip.Qty)||0;
    const rcv=parseFloat(item.Received_Qty)||0;
    const newIss=(parseFloat(item.Issued_Qty)||0)+qty;
    if(newIss>rcv){alert(`Cannot issue more than received (${rcv})!`);return;}
    const updated=recalc({...item,Issued_Qty:newIss});
    const newItems=items.map(i=>i.id===updated.id?updated:i);
    setItems(newItems); onSave&&onSave(newItems);
    const log={...slip,id:Date.now(),category,item_name:item.Item_Name,item_sku:item.SKU_ID,uom:item.UOM};
    setIssueLogs(p=>[log,...p]);
    await saveDB('requisition_slip',log); await saveDB('inventory_item',updated);
    setShowReq(false);
    alert(`✅ Req ${slip.Slip_No} processed!`);
  };

  const filtered=items.filter(i=>{
    if(!search) return true;
    const q=search.toLowerCase();
    return ['Item_Name','SKU_ID','HSN_Code','Supplier','Style_Name','Order_No'].some(k=>(i[k]||'').toLowerCase().includes(q));
  });

  const diffColor = v => parseFloat(v)>0?'#c93652':parseFloat(v)<0?'#00a65e':'#676879';
  const critCount = items.filter(i=>getStockStatus(i)==='critical').length;

  // Size analysis for Trims
  const sizeAnalysis = useMemo(()=>{
    if(category!=='Trims') return null;
    const map={};
    items.forEach(item=>{
      const key=`${item.Style_Name||'?'}|||${item.Order_No||'?'}|||${item.Color||'?'}`;
      if(!map[key]) map[key]={style:item.Style_Name,po:item.Order_No,color:item.Color,sizes:{}};
      const sz=item.Size||'All';
      if(!map[key].sizes[sz]) map[key].sizes[sz]={items:0,bal:0,req:0,iss:0,qcStatus:'PENDING'};
      const s=map[key].sizes[sz];
      s.items++; s.bal+=parseFloat(item.Balance_Qty)||0; s.req+=parseFloat(item.Required_Qty)||0; s.iss+=parseFloat(item.Issued_Qty)||0;
      const qcs=getTrimQCStatus(db,item);
      if(qcs==='FAIL') s.qcStatus='FAIL'; else if(qcs==='PASS'&&s.qcStatus==='PENDING') s.qcStatus='PASS';
    });
    return Object.values(map);
  },[items,category,db]);

  const SavedRollSummary = ({ item, onEdit, onDel }) => {
    if (!item) return null;
    const status=getStockStatus(item);
    const s=C[status];
    return (
      <div style={{background:s.bg,border:`1px solid ${s.border}`,borderRadius:'10px',padding:'16px',marginTop:'16px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
          <div style={{fontWeight:'800',fontSize:'14px',color:'#323338'}}>✅ Last Saved: {item.Item_Name} <span style={{fontFamily:'monospace',color:'#0073ea'}}>{item.SKU_ID}</span></div>
          <div style={{display:'flex',gap:'8px'}}>
            <button onClick={()=>onEdit(item)} style={{padding:'5px 12px',background:'#e8f0ff',color:'#0073ea',border:'1px solid #a8d1ff',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>Edit</button>
            <button onClick={()=>onDel(item.id)} style={{padding:'5px 12px',background:'#fef2f2',color:'#c93652',border:'1px solid #fecaca',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>Delete</button>
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:'8px',fontSize:'11px'}}>
          {[['Category',item.Category],['Supplier',item.Supplier],['UOM',item.UOM],['Rate',`₹${parseFloat(item.Rate||0).toFixed(2)}`],['Required',item.Required_Qty],['Received',item.Received_Qty],['Issued',item.Issued_Qty],['Balance',parseFloat(item.Balance_Qty||0).toFixed(2)]].map(([l,v])=>(
            <div key={l} style={{background:'white',padding:'6px 10px',borderRadius:'6px',border:`1px solid ${s.border}`}}>
              <div style={{fontSize:'9px',color:'#676879',textTransform:'uppercase',fontWeight:'700'}}>{l}</div>
              <div style={{fontWeight:'700',color:'#323338',marginTop:'2px'}}>{v||'—'}</div>
            </div>
          ))}
          <div style={{background:'white',padding:'6px 10px',borderRadius:'6px',border:`1px solid ${s.border}`}}>
            <div style={{fontSize:'9px',color:'#676879',textTransform:'uppercase',fontWeight:'700'}}>Status</div>
            <div style={{marginTop:'2px'}}><StatusBadgeWithReason item={item}/></div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <RequisitionModal open={showReq} onClose={()=>setShowReq(false)} onConfirm={handleIssueConfirm} items={items} db={db}/>
      <div style={{display:'flex',borderBottom:'1px solid #e6e9ef',background:'white',padding:'0 16px',marginBottom:'20px',borderRadius:'10px 10px 0 0',flexWrap:'wrap'}}>
        <SubTabBtn label="Inventory"    active={subTab==='inventory'}  onClick={()=>setSubTab('inventory')}  icon={Archive} count={critCount}/>
        <SubTabBtn label="Add / Edit"   active={subTab==='inward'}     onClick={()=>setSubTab('inward')}     icon={Plus}/>
        <SubTabBtn label="Bulk Upload"  active={subTab==='bulk'}       onClick={()=>setSubTab('bulk')}       icon={Upload}/>
        <SubTabBtn label="Issue (Slip)" active={subTab==='issue'}      onClick={()=>setSubTab('issue')}      icon={ClipboardList}/>
        {category==='Trims' && <SubTabBtn label="Size Analysis" active={subTab==='size'} onClick={()=>setSubTab('size')} icon={BarChart3}/>}
        <SubTabBtn label="QR Codes"     active={subTab==='qr'}         onClick={()=>setSubTab('qr')}         icon={QrCode}/>
      </div>

      {/* INVENTORY TABLE */}
      {subTab==='inventory' && (
        <div style={ss}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'14px',flexWrap:'wrap',gap:'10px'}}>
            <h3 style={{...hs,marginBottom:0}}><CatIcon size={14}/> {category} ({filtered.length})</h3>
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap',alignItems:'center'}}>
              <div style={{display:'flex',alignItems:'center',gap:'6px',border:'1px solid #e6e9ef',borderRadius:'6px',padding:'7px 10px'}}>
                <Search size={13} color="#676879"/>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…" style={{border:'none',outline:'none',fontSize:'12px',width:isTablet?'100px':'160px'}}/>
              </div>
              {selIds.size>0 && <button onClick={bulkDelete} style={{padding:'7px 12px',background:'#fef2f2',color:'#c93652',border:'1px solid #fecaca',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>🗑 Delete ({selIds.size})</button>}
              {selIds.size>0 && <button onClick={()=>{const it=items.find(i=>i.id===[...selIds][0]);if(it){setForm({...blank,...it});setEditId(it.id);setSubTab('inward');}}} style={{padding:'7px 12px',background:'#e8f0ff',color:'#0073ea',border:'1px solid #a8d1ff',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>✏ Edit Selected</button>}
              <button onClick={exportData} style={{padding:'7px 12px',background:'#1f1f1f',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px'}}>⬇ Export</button>
              <button onClick={()=>{setForm(blank);setEditId(null);setSubTab('inward')}} style={{padding:'7px 12px',background:color,color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>+ Add</button>
            </div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr>
                  <th style={{...TH,width:'36px'}}><input type="checkbox" checked={selIds.size===filtered.length&&filtered.length>0} onChange={toggleAll}/></th>
                  {['Lock','Status','SKU ID','HSN','Item Name','Sub Cat','Supplier','UOM','Rate','Required','Wst%','W.Qty','Ordered','Received','Issued','Balance','Diff','Daily','Days','Style','PO','Color','Size','QC Status','RTV','Rack','Actions'].map(h=><th key={h} style={{...TH,background:h==='Lock'?'#f0f1f4':undefined,width:h==='Lock'?'60px':undefined}}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {!filtered.length&&<tr><td colSpan={27} style={{...TD,textAlign:'center',color:'#9699a6',padding:'40px'}}>No items.</td></tr>}
                {filtered.map((item,i)=>{
                  const status=getStockStatus(item);
                  const qcStatus=getTrimQCStatus(db,item);
                  const rtv=getRTVQty(db,item);
                  const sel=selIds.has(item.id);
                  return (
                    <tr key={item.id} style={{background:sel?'#e8f0ff':i%2===0?'white':'#fafafa'}}>
                      <td style={{...TD,textAlign:'center'}}><input type="checkbox" checked={sel} onChange={()=>toggleSel(item.id)}/></td>
                      <td style={TD}><StatusBadgeWithReason item={item}/></td>
                      <td style={{...TD,fontFamily:'monospace',color:'#0073ea',fontWeight:'700',fontSize:'10px'}}>{item.SKU_ID}</td>
                      <td style={{...TD,fontFamily:'monospace',fontSize:'10px'}}>{item.HSN_Code}</td>
                      <td style={{...TD,fontWeight:'700',maxWidth:'120px',overflow:'hidden',textOverflow:'ellipsis'}} title={item.Item_Name}>{item.Item_Name}</td>
                      <td style={TD}>{item.Sub_Category}</td>
                      <td style={TD}>{item.Supplier}</td>
                      <td style={TD}>{item.UOM}</td>
                      <td style={{...TD,textAlign:'right'}}>₹{parseFloat(item.Rate||0).toFixed(2)}</td>
                      <td style={{...TD,textAlign:'right',fontWeight:'700'}}>{item.Required_Qty}</td>
                      <td style={{...TD,textAlign:'right',color:'#fdab3d'}}>{item.Wastage_Pct}%</td>
                      <td style={{...TD,textAlign:'right',color:'#fdab3d'}}>{item.Wastage_Qty}</td>
                      <td style={{...TD,textAlign:'right'}}>{item.Ordered_Qty}</td>
                      <td style={{...TD,textAlign:'right',background:'#f0fdf4'}}>{item.Received_Qty}</td>
                      <td style={{...TD,textAlign:'right',background:'#fefce8'}}>{item.Issued_Qty}</td>
                      <td style={{...TD,textAlign:'right',fontWeight:'800',color:C[status].text,background:C[status].bg}} title={getStatusReason(item)}>{parseFloat(item.Balance_Qty||0).toFixed(2)}</td>
                      <td style={{...TD,textAlign:'right',fontWeight:'700',color:diffColor(item.Difference)}}>{parseFloat(item.Difference||0).toFixed(2)}</td>
                      <td style={{...TD,textAlign:'right'}}>{item.Daily_Consumption}</td>
                      <td style={{...TD,fontWeight:'700',color:parseFloat(item.Days_Coverage)<3?'#c93652':parseFloat(item.Days_Coverage)<7?'#fdab3d':'#00a65e'}}>{item.Days_Coverage}d</td>
                      <td style={TD}>{item.Style_Name}</td>
                      <td style={TD}>{item.Order_No}</td>
                      <td style={TD}>{item.Color}</td>
                      <td style={TD}>{item.Size}</td>
                      <td style={TD}><QCBadge status={qcStatus}/></td>
                      <td style={{...TD,textAlign:'right',color:'#fdab3d',fontWeight:'700'}}>{rtv>0?rtv:'—'}</td>
                      <td style={TD}>{item.Rack_No}/{item.Shelf_No}</td>
                      <td style={TD}>
                        <div style={{display:'flex',gap:'4px'}}>
                          <button onClick={()=>{setForm({...blank,...item});setEditId(item.id);setSubTab('inward')}} style={{padding:'3px 8px',background:'#e8f0ff',color:'#0073ea',border:'1px solid #a8d1ff',borderRadius:'4px',cursor:'pointer',fontSize:'10px'}}>Edit</button>
                          <button onClick={()=>handleDelete(item.id)} style={{padding:'3px 8px',background:'#fef2f2',color:'#c93652',border:'1px solid #fecaca',borderRadius:'4px',cursor:'pointer',fontSize:'10px'}}>Del</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ADD / EDIT FORM */}
      {subTab==='inward' && (
        <div style={ss}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
            <h3 style={{...hs,marginBottom:0}}>{editId?'Edit':'Add'} {category} Item</h3>
            {editId && <button onClick={()=>{setForm(blank);setEditId(null);}} style={{padding:'6px 12px',background:'#f0f1f4',color:'#676879',border:'1px solid #e6e9ef',borderRadius:'6px',cursor:'pointer',fontSize:'12px'}}>✕ Cancel</button>}
          </div>

          {/* Identity */}
          <div style={{background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'14px',marginBottom:'14px'}}>
            <div style={{fontSize:'11px',fontWeight:'800',color:'#676879',marginBottom:'12px'}}>📦 ITEM IDENTITY</div>
            <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(${isTablet?'150px':'195px'},1fr))`,gap:'12px'}}>
              <div>
                <Fld label="SKU ID" value={form.SKU_ID} onChange={e=>hc('SKU_ID',e)} required error={fErrs.SKU_ID} placeholder="e.g. TRM-001"/>
                <button onClick={genSKU} style={{fontSize:'11px',color,background:'none',border:'none',cursor:'pointer',marginTop:'-2px'}}>⚡ Auto SKU</button>
              </div>
              <div>
                <Fld label="Item Name" value={form.Item_Name} onChange={e=>hc('Item_Name',e)} required error={fErrs.Item_Name}
                  options={srcNames.length?srcNames:trimOpts.length?trimOpts:null} placeholder="e.g. Button 12mm"/>
                {!srcNames.length && <span style={{fontSize:'10px',color:'#fdab3d'}}>⚠ Add items in Sourcing first</span>}
              </div>
              <div>
                <Fld label="HSN Code" value={form.HSN_Code} onChange={e=>hc('HSN_Code',e)} placeholder="Auto-filled"/>
                {form.Item_Name && !form.HSN_Code && <button onClick={()=>hc('HSN_Code',getHSN(form.Item_Name))} style={{fontSize:'11px',color:'#0073ea',background:'none',border:'none',cursor:'pointer',marginTop:'-2px'}}>🔍 Lookup HSN</button>}
              </div>
              <Fld label="Sub Category" value={form.Sub_Category} onChange={e=>hc('Sub_Category',e)} placeholder="Buttons, Zippers…"/>
              <div>
                <Fld label="Supplier" value={form.Supplier} onChange={e=>hc('Supplier',e)} error={fErrs.Supplier}
                  options={srcSupps.length?srcSupps:null} placeholder="Supplier name"/>
                {!srcSupps.length && <span style={{fontSize:'10px',color:'#fdab3d'}}>⚠ Add suppliers in Sourcing</span>}
              </div>
              <Fld label="UOM" value={form.UOM} onChange={e=>hc('UOM',e)} options={uomOpts.length?uomOpts:['Pcs','Meters','Kgs','Rolls','Packets','Sets','Pairs','Dozens']}/>
              <Fld label="Rate (₹)" value={form.Rate} onChange={e=>hc('Rate',e)} type="number" placeholder="Unit price"/>
            </div>
          </div>

          {/* Order linkage from Style Registry */}
          <div style={{background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'14px',marginBottom:'14px'}}>
            <div style={{fontSize:'11px',fontWeight:'800',color:'#676879',marginBottom:'12px'}}>🔗 ORDER LINKAGE — from Style Registry</div>
            <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(${isTablet?'140px':'175px'},1fr))`,gap:'12px'}}>
              <div>
                <Fld label="Style Name" value={form.Style_Name} onChange={e=>handleStyleChange(e.target.value)} error={fErrs.Style_Name} options={styleRefs}/>
                {!styleRefs.length && <span style={{fontSize:'10px',color:'#fdab3d'}}>⚠ No styles in registry</span>}
              </div>
              <Fld label="PO No" value={form.Order_No} onChange={e=>handlePOChange(e.target.value)} options={poOpts.length?poOpts:undefined} placeholder="Auto from style"/>
              <Fld label="Color" value={form.Color} onChange={e=>hc('Color',e)} options={colorOpts.length?colorOpts:undefined} placeholder="Auto from style"/>
              <Fld label="Size" value={form.Size} onChange={e=>hc('Size',e)} options={sizeOpts.length?sizeOpts:['XS','S','M','L','XL','XXL','All']}/>
              <Fld label="Rack No" value={form.Rack_No} onChange={e=>hc('Rack_No',e)} placeholder="R1"/>
              <Fld label="Shelf No" value={form.Shelf_No} onChange={e=>hc('Shelf_No',e)} placeholder="S-3"/>
            </div>
          </div>

          {/* Quantity planning */}
          <div style={{background:'#e8f0ff',border:'1px solid #a8d1ff',borderRadius:'8px',padding:'14px',marginBottom:'14px'}}>
            <div style={{fontSize:'11px',fontWeight:'800',color:'#0062c4',marginBottom:'12px'}}>📊 QUANTITY PLANNING — AUTO-CALCULATED</div>
            <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(${isTablet?'140px':'160px'},1fr))`,gap:'12px'}}>
              <Fld label="Required Qty" value={form.Required_Qty} onChange={e=>hc('Required_Qty',e)} type="number"/>
              <Fld label="Wastage %" value={form.Wastage_Pct} onChange={e=>hc('Wastage_Pct',e)} type="number"/>
              <Fld label="Wastage Qty (Auto)" value={form.Wastage_Qty} readOnly/>
              <Fld label="Ordered Qty" value={form.Ordered_Qty} onChange={e=>hc('Ordered_Qty',e)} type="number"/>
              <Fld label="Received Qty" value={form.Received_Qty} onChange={e=>hc('Received_Qty',e)} type="number"/>
              <div>
                <Fld label="Issued Qty" value={form.Issued_Qty} onChange={e=>{
                  const v=parseFloat(e.target.value)||0;
                  const rcv=parseFloat(form.Received_Qty)||0;
                  if(v>rcv){alert(`Cannot issue more than received (${rcv})!`);return;}
                  hc('Issued_Qty',e);
                }} type="number" error={fErrs.Issued_Qty}/>
              </div>
              <Fld label="Balance (Auto)" value={form.Balance_Qty} readOnly/>
              <Fld label="Difference (Auto)" value={form.Difference} readOnly/>
              <Fld label="Daily Consumption" value={form.Daily_Consumption} onChange={e=>hc('Daily_Consumption',e)} type="number"/>
              <Fld label="Days Coverage (Auto)" value={form.Days_Coverage} readOnly/>
            </div>
          </div>

          <Fld label="Remarks" value={form.Remarks} onChange={e=>hc('Remarks',e)} placeholder="Notes…"/>
          <div style={{display:'flex',gap:'12px',marginTop:'10px'}}>
            <button onClick={()=>handleSave(false)} style={{flex:2,padding:'13px',background:color,color:'white',border:'none',borderRadius:'8px',fontWeight:'800',cursor:'pointer',fontSize:'14px'}}>
              {editId?'✏️ UPDATE':'💾 SAVE'} {category.toUpperCase()} ITEM
            </button>
            {!editId && <button onClick={()=>handleSave(true)} style={{flex:1,padding:'13px',background:'#1f1f1f',color:'white',border:'none',borderRadius:'8px',fontWeight:'700',cursor:'pointer',fontSize:'13px'}}>
              💾 Save & Add New
            </button>}
          </div>
          <SavedRollSummary item={lastSaved} onEdit={item=>{setForm({...blank,...item});setEditId(item.id);}} onDel={handleDelete}/>
        </div>
      )}

      {/* BULK UPLOAD */}
      {subTab==='bulk' && (
        <div style={ss}>
          <h3 style={hs}><Upload size={14}/> Bulk Upload — {category}</h3>
          <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'8px',padding:'14px',marginBottom:'18px',fontSize:'12px',color:'#78350f'}}>
            <strong>Rules:</strong> Headers must match template exactly. Style_Name validated against Style Registry (unrecognised = skipped). Issued ≤ Received enforced. HSN auto-filled from item name if blank.
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'18px'}}>
            <div style={{border:'2px dashed #e6e9ef',borderRadius:'10px',padding:'24px',textAlign:'center'}}>
              <FileText size={34} color="#676879" style={{margin:'0 auto 10px'}}/>
              <div style={{fontWeight:'700',fontSize:'13px',marginBottom:'10px'}}>Download Template</div>
              <button onClick={downloadTemplate} style={{padding:'9px 20px',background:'#1f1f1f',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'700'}}>⬇ {category} Template</button>
            </div>
            <div style={{border:`2px dashed ${color}80`,borderRadius:'10px',padding:'24px',textAlign:'center',background:color+'08'}}>
              <Upload size={34} color={color} style={{margin:'0 auto 10px'}}/>
              <div style={{fontWeight:'700',fontSize:'13px',marginBottom:'10px'}}>Upload CSV</div>
              <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={handleBulkUpload}/>
              <button onClick={()=>fileRef.current.click()} style={{padding:'9px 20px',background:color,color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'700'}}>📂 Import CSV</button>
            </div>
          </div>
          <InvoiceReader category={category} onExtracted={data=>{
            setForm(f=>({...f,
              Invoice_No:data.invoice_no||data.invoiceNo||f.Invoice_No,
              Supplier:data.supplier||data.supplier_name||f.Supplier,
              Ordered_Qty:data.total_qty||data.qty||f.Ordered_Qty,
            }));
            setSubTab('inward');
          }}/>
        </div>
      )}

      {/* ISSUE SLIP */}
      {subTab==='issue' && (
        <div style={{display:'grid',gridTemplateColumns:isTablet?'1fr':'360px 1fr',gap:'20px'}}>
          <div style={ss}>
            <h3 style={hs}><ClipboardList size={14}/> Issue with Requisition Slip</h3>
            <p style={{fontSize:'13px',color:'#676879',marginBottom:'16px'}}>All issues are recorded against a formal slip with full traceability (Style, PO, Color, Size, Section, Line, Supervisor).</p>
            <button onClick={()=>setShowReq(true)} style={{width:'100%',padding:'14px',background:'#fdab3d',color:'white',border:'none',borderRadius:'8px',fontWeight:'800',cursor:'pointer',fontSize:'14px'}}>📋 CREATE REQUISITION SLIP</button>
          </div>
          <div style={ss}>
            <h3 style={hs}>Issue Log ({issueLogs.length})</h3>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr>{['Slip No','Date','Style','PO','Color','Size','Section','Item','Qty','Supervisor','Handed To'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
                <tbody>
                  {issueLogs.map((l,i)=>(
                    <tr key={i} style={{background:i%2===0?'white':'#fafafa'}}>
                      <td style={{...TD,color:'#0073ea',fontWeight:'700'}}>{l.Slip_No}</td>
                      <td style={TD}>{l.Date}</td><td style={TD}>{l.Style_Name}</td><td style={TD}>{l.Order_No}</td>
                      <td style={TD}>{l.Color}</td><td style={TD}>{l.Size}</td><td style={TD}>{l.Section}</td>
                      <td style={{...TD,fontWeight:'700'}}>{l.item_name}</td>
                      <td style={{...TD,textAlign:'right',fontWeight:'800',color:'#fdab3d'}}>{l.Qty} {l.uom}</td>
                      <td style={TD}>{l.Supervisor}</td><td style={TD}>{l.Handed_To}</td>
                    </tr>
                  ))}
                  {!issueLogs.length&&<tr><td colSpan={11} style={{...TD,textAlign:'center',color:'#9699a6',padding:'30px'}}>No issues yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* SIZE ANALYSIS — Trims */}
      {subTab==='size' && category==='Trims' && (
        <div style={ss}>
          <h3 style={hs}><BarChart3 size={14}/> Trims — Style → PO → Color → Size Analysis</h3>
          {!sizeAnalysis?.length && <div style={{textAlign:'center',color:'#9699a6',padding:'40px',fontSize:'13px'}}>Add trims with Size field to see size-level breakdown.</div>}
          {sizeAnalysis?.map((g,gi)=>(
            <div key={gi} style={{marginBottom:'18px',border:'1px solid #e6e9ef',borderRadius:'8px',overflow:'hidden'}}>
              <div style={{background:'#323338',color:'white',padding:'10px 16px',display:'flex',gap:'14px',alignItems:'center',flexWrap:'wrap'}}>
                <span style={{fontWeight:'800',fontSize:'13px'}}>{g.style||'(No Style)'}</span>
                <span style={{fontSize:'12px',color:'#9699a6'}}>PO: {g.po}</span>
                <span style={{fontSize:'12px',color:'#9699a6'}}>Color: {g.color}</span>
              </div>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr>{['Size','Items','Required','Issued','Balance','QC Status','Status'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
                <tbody>
                  {Object.entries(g.sizes).map(([sz,vals])=>{
                    const pct=vals.req>0?(vals.bal/vals.req)*100:100;
                    const s=pct<10?'critical':pct<30?'low':pct>150?'excess':'adequate';
                    return (
                      <tr key={sz} style={{borderBottom:'1px solid #f0f1f4'}}>
                        <td style={{...TD,fontWeight:'800',color:'#0073ea'}}>{sz}</td>
                        <td style={{...TD,textAlign:'right'}}>{vals.items}</td>
                        <td style={{...TD,textAlign:'right',fontWeight:'700'}}>{vals.req.toFixed(0)}</td>
                        <td style={{...TD,textAlign:'right',background:'#fefce8'}}>{vals.iss.toFixed(0)}</td>
                        <td style={{...TD,textAlign:'right',fontWeight:'800',color:C[s].text,background:C[s].bg}}>{vals.bal.toFixed(2)}</td>
                        <td style={TD}><QCBadge status={vals.qcStatus}/></td>
                        <td style={TD}><Badge status={s}/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* QR CODES */}
      {subTab==='qr' && (
        <div style={ss}>
          <h3 style={hs}><QrCode size={14}/> QR Codes — {category}</h3>
          <div style={{marginBottom:'16px',padding:'12px',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'8px',fontSize:'12px',color:'#78350f'}}>
            💡 Each QR encodes SKU, item name, rack and shelf. Scan to instantly locate items in store.
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:'12px'}}>
            {items.map(item=>(
              <QRImg key={item.id} data={`SKU:${item.SKU_ID}|ITEM:${item.Item_Name}|RACK:${item.Rack_No}|SHELF:${item.Shelf_No}|QTY:${item.Balance_Qty}|CAT:${category}`} label={`${item.SKU_ID}\n${item.Rack_No}/${item.Shelf_No}`} size={110}/>
            ))}
            {!items.length&&<div style={{color:'#9699a6',fontSize:'13px'}}>No items yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PART 8 — FABRIC MODULE (all algorithms + QC + Invoice Reader + Save & Add New)
// ============================================================================

function FabricModule({ db, setDb }) {
  const isTablet = useIsTablet();
  const [subTab, setSubTab] = useState('inventory');
  // Hydrate from db once on mount; after that local state is the source of truth.
  // All mutations (save/delete/issue/return) update local state + setDb so the
  // parent has the correct data, but parent re-renders / server refreshes cannot
  // resurrect deleted rolls.
  const hydrated = useRef(false);
  const [inventory, setInventory] = useState(()=> db?.materials||[]);
  const [lastIssueAt, setLastIssueAt] = useState(null);
  const [returns,   setReturns]   = useState(()=> db?.materialReturns||[]);
  const [editId,    setEditId]    = useState(null);
  const [search,    setSearch]    = useState('');
  const [selIds,    setSelIds]    = useState(new Set());
  const [showReq,   setShowReq]   = useState(false);
  const [issueLogs, setIssueLogs] = useState(()=> db?.fabricIssueLogs||[]);
  const [lastSaved, setLastSaved] = useState(null);
  const fileRef = useRef();
  const localOp = useRef(false); // prevents db-sync effect overwriting state after local mutations

  const styleRefs = getStyleRefs(db);
  const [poOpts,    setPoOpts]    = useState([]);
  const [colorOpts, setColorOpts] = useState([]);

  // Sync ONLY on first load. Filter _deleted records so they never resurface after refresh.
  useEffect(()=>{
    if (hydrated.current) return;
    if (localOp.current) { localOp.current = false; return; }
    const mat = (db?.materials||[]).filter(r => !r._deleted);
    const ret = db?.materialReturns||[];
    const hasData = (db?.materials||[]).length > 0 || ret.length > 0;
    if (hasData) {
      hydrated.current = true;
      setInventory(mat);
      if (ret.length > 0) setReturns(ret);
    }
  },[db]);

  // Cut plan state
  const [planData, setPlanData] = useState({
    style:'', po:'', color:'', shade:'', shrinkage:'', pattern:'', csv:'',
    cutQty:'', layLength:'', consumption:'', bundleSize:'20',
    ignoreShade:false, ignoreCSV:false,
    sizeBreakdown:[] // [{size, qty, pcsPerLay}] — from poSizeTable or manual
  });
  const [genOptions, setGenOptions] = useState(null);
  const [selPlan, setSelPlan] = useState(null);

  const blank = {
    id:null, SKU_ID:'', HSN_Code:'', Roll_ID:'', Invoice_Rcv_Date:'', Invoice_No:'',
    Supplier_Roll_No:'', Style_Name:'', Order_No:'', Color:'', UOM:'Meters',
    Width:'', Shade:'', M_Qty:'', Rate:'', Price:0,
    // Cut-plan fields (from CSV cols K–N)
    Consumption:'',    // metres per garment (for cut plan pcs per lay calc)
    Lay_Length:'',     // lay spread length in metres (per roll setting)
    // Shrinkage / grouping fields
    Length_Shrinkage:'', Width_Shrinkage:'', Shrinkage_Group:'', Pattern_Group:'', CSV_Group:'',
    Required_Qty:'', Wastage_Pct:'2', Wastage_Qty:0,
    Ordered_Qty:'', Received_Qty:'', Issue_Qty:'', Balance_Qty:0,
    Difference:0, Daily_Consumption:'', Days_Coverage:0,
    Rack_No:'', Shelf_No:'', Cell_No:'', Remarks:'',
    Quality_Status:'Pending', Defect_Count:0, RTV_Qty:0
  };
  const [form, setForm] = useState(blank);
  const [fErrs, setFErrs] = useState({});
  const [retForm, setRetForm] = useState({ rollId:'', returnQty:'', reason:'Excess', remarks:'' });

  const recalc = f => {
    const qty=parseFloat(f.M_Qty)||0, rate=parseFloat(f.Rate)||0;
    const iss=parseFloat(f.Issue_Qty)||0, req=parseFloat(f.Required_Qty)||0;
    const wPct=parseFloat(f.Wastage_Pct)||0, daily=parseFloat(f.Daily_Consumption)||1;
    const wQty=+(req*wPct/100).toFixed(3);
    const rcv=parseFloat(f.Received_Qty)||qty;
    const bal=+(rcv-iss).toFixed(3);
    const diff=+((req+wQty)-rcv).toFixed(3);
    const days=daily>0?+(bal/daily).toFixed(1):0;
    return {...f,Price:(qty*rate).toFixed(2),Balance_Qty:bal,Wastage_Qty:wQty,Difference:diff,Days_Coverage:days,Received_Qty:rcv.toString()};
  };

  const hc = (field,val) => {
    const v=val?.target?val.target.value:val;
    setForm(f=>{
      let nf=recalc({...f,[field]:v});
      if(field==='Style_Name'){
        const pos=getPOsForStyle(db,v); setPoOpts(pos);
        const cols=getColorsForPO(db,v,pos[0]); setColorOpts(cols);
        nf.Order_No=pos[0]||''; nf.Color=cols[0]||'';
      }
      if(field==='Order_No'){
        const cols=getColorsForPO(db,f.Style_Name,v); setColorOpts(cols);
        nf.Color=cols[0]||nf.Color;
      }
      return nf;
    });
    setFErrs(e=>({...e,[field]:''}));
  };

  const genRollID = () => setForm(f=>({...f,Roll_ID:`R-${Date.now().toString().slice(-6)}`,SKU_ID:`FAB-${Date.now().toString().slice(-6)}`}));

  const validate = () => {
    const e={};
    if(!form.Roll_ID) e.Roll_ID='Required';
    if(form.Style_Name&&styleRefs.length&&!styleRefs.includes(form.Style_Name))
      e.Style_Name=`Style "${form.Style_Name}" not in Style Registry`;
    const rcv=parseFloat(form.Received_Qty||form.M_Qty)||0;
    const iss=parseFloat(form.Issue_Qty)||0;
    if(iss>rcv) e.Issue_Qty=`Cannot exceed received (${rcv})`;
    setFErrs(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async (addNew=false) => {
    if(!validate()) return;
    // Always set code (Roll_ID-based) and name so save_universal can upsert correctly
    const entry={
      ...form,
      id: editId || 0,   // 0 = new INSERT; DB id = UPDATE
      code: `ROLL-${form.Roll_ID || Date.now()}`,
      name: [form.Style_Name,form.Order_No,form.Color].filter(Boolean).join(' | ') || form.Roll_ID || 'Roll',
    };
    const newInv=editId?inventory.map(i=>i.id===editId?entry:i):[entry,...inventory];
    localOp.current = true;
    setInventory(newInv); setDb(p=>({...p,materials:newInv}));
    setLastSaved(entry);
    if(!addNew){ setEditId(null); setForm(blank); alert('Roll saved!'); }
    else { setForm({...blank,Style_Name:form.Style_Name,Order_No:form.Order_No,Color:form.Color}); setEditId(null); }
    await saveDB('material',entry);
  };

  // CSV upload — strict header mapping, validates styles
  const handleCSVUpload = e => {
    const file=e.target.files[0]; if(!file) return;
    const reader=new FileReader();
    reader.onload=async evt=>{
      // FIX 1: Strip BOM (﻿) from the raw text before parsing
      const rawText = evt.target.result.replace(/^\uFEFF/, '');
      const rows=rawText.split(/\r?\n/).map(r=>r.split(','));
      // FIX 2: Strip BOM and whitespace from each header
      const hdr=rows[0].map(h=>h.trim().replace(/^\uFEFF/,'').replace(/"/g,''));
      const dataRows=rows.slice(1).filter(r=>r.length>2&&r[0].trim());
      const validStyles=new Set(styleRefs);
      const errs=[]; const newRolls=[];

      // CSV column → JS field aliases (handles all known column name variants)
      const CSV_ALIASES = {
        'M_Qty':        'M_Qty',
        'M Qty':        'M_Qty',
        ' M_Qty ':      'M_Qty',
        'Lay Length':   'Lay_Length',   // new column
        'Lay_Length':   'Lay_Length',
        'Consumption':  'Consumption',  // new column (m per garment)
        'No Plies':     null,           // calculated — skip
        'No_Plies':     null,
        'Ebit':         null,           // calculated — skip
        'Issued_Qty':   'Issue_Qty',
        'Issue_Qty':    'Issue_Qty',
        'CSV':          'CSV_Group',
        'Cell_No':      'Cell_No',
        'Supplier_Roll_No':'Supplier_Roll_No',
        'Shrinkage':    'Shrinkage_Group', // new alias
        'Shrinkage_Group':'Shrinkage_Group',
        'Pattern_Group':'Pattern_Group',
      };

      dataRows.forEach((cols,idx)=>{
        // Start with blank, id=0 means INSERT on save
        const obj={...blank, id:0};

        hdr.forEach((h,i)=>{
          if (cols[i] === undefined) return;
          const v = cols[i].trim().replace(/"/g,'');
          const trimH = h.trim();
          // Check alias map first
          if (CSV_ALIASES.hasOwnProperty(trimH)) {
            const target = CSV_ALIASES[trimH];
            if (target && blank.hasOwnProperty(target)) obj[target] = v;
            return; // handled
          }
          // Direct match
          if (blank.hasOwnProperty(trimH)) obj[trimH] = v;
        });

        // If this Roll_ID already exists in local inventory, reuse its DB id (→ UPDATE not INSERT)
        if (obj.Roll_ID) {
          const existing = inventory.find(r => r.Roll_ID === obj.Roll_ID);
          if (existing && existing.id && parseInt(existing.id) < 2000000000 && parseInt(existing.id) > 0) {
            obj.id = existing.id; // DB id → will UPDATE existing row
          }
        }

        // Always set code + name so save_universal can upsert correctly
        obj.code = `ROLL-${obj.Roll_ID || (Date.now()+idx)}`;
        obj.name = [obj.Style_Name,obj.Order_No,obj.Color].filter(Boolean).join(' | ') || obj.Roll_ID || 'Roll';

        // Received_Qty = M_Qty if not separately set
        if (!obj.Received_Qty && obj.M_Qty) obj.Received_Qty = obj.M_Qty;

        if (obj.Style_Name && validStyles.size && !validStyles.has(obj.Style_Name)) {
          errs.push(`Row ${idx+2}: Style "${obj.Style_Name}" not in registry — imported anyway`);
        }
        const rcv=parseFloat(obj.Received_Qty||obj.M_Qty)||0;
        const iss=parseFloat(obj.Issue_Qty)||0;
        if(iss>rcv&&rcv>0){obj.Issue_Qty=rcv.toString();errs.push(`Row ${idx+2}: Issued qty capped to ${rcv}`);}
        obj.Balance_Qty = (rcv-iss).toString();
        newRolls.push(recalc(obj));
      });

      if(errs.length) alert(`⚠ Import notes:\n${errs.slice(0,10).join('\n')}`);
      if(!newRolls.length) return alert('No valid rolls found. Check Roll_ID column is filled.');

      // Merge into local state immediately (optimistic update)
      const rollIds = new Set(newRolls.map(r=>r.Roll_ID).filter(Boolean));
      const existingKept = inventory.filter(r => !rollIds.has(r.Roll_ID));
      const merged = [...newRolls, ...existingKept];
      localOp.current = true;
      setInventory(merged); setDb(p=>({...p,materials:merged}));

      // ── BULK SAVE: one HTTP request for all rolls ──────────────────────────
      // Fixes the "100 roll limit" — no more 100+ sequential requests timing out
      try {
        const res = await fetch(DB_URL, {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ category:'material_bulk', data: newRolls })
        });
        const result = await res.json();
        if (result.success) {
          // Update local IDs with the real DB ids returned from server
          if (result.results?.length) {
            const idMap = {};
            result.results.forEach(r => { if(r.temp_id !== undefined) idMap[r.temp_id] = r.db_id; });
            setInventory(prev => prev.map(roll => {
              const dbId = idMap[roll.id];
              return dbId ? {...roll, id: dbId} : roll;
            }));
          }
          alert(`✅ ${result.inserted||0} new + ${result.updated||0} updated = ${result.total||newRolls.length} rolls saved to database!`);
        } else {
          alert(`⚠ Save issue: ${result.error}\nRolls are visible locally but may not be persisted.`);
        }
      } catch(e) {
        alert(`⚠ Network error during save: ${e.message}\nRolls visible locally. Try refreshing.`);
      }
    };
    reader.readAsText(file); e.target.value='';
  };

  const downloadTemplate = () => {
    const h=['Roll_ID','SKU_ID','HSN_Code','Invoice_No','Invoice_Rcv_Date','Style_Name','Order_No','Color','Shade','M_Qty','Received_Qty','Rate','Width','Shrinkage_Group','Pattern_Group','Required_Qty','Wastage_Pct','Daily_Consumption','Rack_No','Shelf_No'];
    const ex=['R-001001','FAB-001001','5208110000','INV-001','2024-01-15','STYLE-01','PO-001','Navy','A1','200','200','180','58 inch','S-G1','P-G1','250','2','10','R1','S-3'];
    const a=document.createElement('a');a.href=URL.createObjectURL(new Blob([[h.join(','),ex.join(',')].join('\n')],{type:'text/csv'}));a.download='Fabric_template.csv';a.click();
  };

  const toggleSel=id=>setSelIds(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;});
  const toggleAll=()=>setSelIds(selIds.size===filtered.length?new Set():new Set(filtered.map(i=>i.id)));
  const bulkDelete=()=>{
    if(!selIds.size)return;
    if(!window.confirm(`Delete ${selIds.size} rolls?`))return;
    const n=inventory.filter(i=>!selIds.has(i.id));
    setInventory(n);setDb(p=>({...p,materials:n}));
    selIds.forEach(id=>deleteFromDB('material',id));
    setSelIds(new Set());
  };

  const handleIssueConfirm = async slip => {
    const roll=inventory.find(r=>r.id===parseInt(slip.Item_Id)||r.Roll_ID===slip.rollId);
    if(!roll) return alert('Roll not found!');
    const qty=parseFloat(slip.Qty)||0;
    const rcv=parseFloat(roll.Received_Qty||roll.M_Qty)||0;
    const curIss=parseFloat(roll.Issue_Qty)||0;
    if(curIss+qty>rcv){alert(`Cannot issue more than received (${rcv}m)!`);return;}
    const updated=recalc({...roll,Issue_Qty:(curIss+qty).toString()});
    const newInv=inventory.map(i=>i.id===updated.id?updated:i);
    localOp.current = true;
    setInventory(newInv);setDb(p=>({...p,materials:newInv}));
    setLastIssueAt(Date.now()); // signal live reconciliation
    const log={...slip,id:Date.now(),item_name:roll.Roll_ID,item_sku:roll.SKU_ID,uom:'Meters'};
    const newLogs=[log,...issueLogs];setIssueLogs(newLogs);setDb(p=>({...p,fabricIssueLogs:newLogs}));
    await saveDB('requisition_slip',log);await saveDB('material',updated);
    setShowReq(false);alert(`✅ Req ${slip.Slip_No} saved!`);
  };

  const handleReturnSubmit = async () => {
    const roll=inventory.find(r=>r.Roll_ID===retForm.rollId);
    if(!roll) return alert('Roll not found!');
    const retQty=parseFloat(retForm.returnQty);
    if(retQty<=0) return alert('Invalid Qty');
    const newIss=Math.max(0,(parseFloat(roll.Issue_Qty)||0)-retQty);
    const updated=recalc({...roll,Issue_Qty:newIss.toString()});
    const newInv=inventory.map(r=>r.id===roll.id?updated:r);
    const newRet={...retForm,id:Date.now(),date:new Date().toISOString().split('T')[0],style:roll.Style_Name};
    localOp.current = true;
    setInventory(newInv);setReturns([newRet,...returns]);
    setDb(p=>({...p,materials:newInv,materialReturns:[newRet,...returns]}));
    setLastIssueAt(Date.now());
    await saveDB('material',updated);
    setRetForm({rollId:'',returnQty:'',reason:'Excess',remarks:''});
    alert('Return processed!');
  };

  // CUT OPTIMISER — uses Style Registry for filter dropdowns
  const cutStyleOpts = getStyleRefs(db);
  const cutPOOpts    = planData.style ? getPOsForStyle(db, planData.style) : [...new Set(inventory.map(i=>i.Order_No).filter(Boolean))].sort();
  const cutColorOpts = planData.style||planData.po ? getColorsForPO(db, planData.style, planData.po) : [...new Set(inventory.map(i=>i.Color).filter(Boolean))].sort();
  const getOpts = key => [...new Set(inventory.map(i=>i[key]).filter(x=>x&&x.toString().trim()))].sort();

  // ── BUNDLE GENERATOR ─────────────────────────────────────────────────────
  const generateBundles = (rollPlan, sizeBreakdown, layLen, bundleSz, poNo) => {
    if (!sizeBreakdown || sizeBreakdown.length === 0) return [];

    // Build lay map: each lay → which roll it comes from
    const layMap = [];
    let layNo = 1;
    for (const r of rollPlan) {
      for (let l = 0; l < r.laysUsed; l++) {
        layMap.push({ layNo: layNo++, rollId: r.Roll_ID, rollSKU: r.SKU_ID });
      }
    }

    const bundles = [];
    let globalBundleNo = 1;

    sizeBreakdown.forEach(sb => {
      const size = sb.size;
      const totalPcsForSize = parseInt(sb.qty) || 0;
      if (totalPcsForSize === 0) return;

      let pcsAssigned = 0;
      let sizeBundleNo = 1;
      const totalLays = layMap.length;

      while (pcsAssigned < totalPcsForSize) {
        const pcsInBundle = Math.min(bundleSz, totalPcsForSize - pcsAssigned);
        // Which lays contribute to this bundle?
        const layStart = Math.floor((pcsAssigned / totalPcsForSize) * totalLays) + 1;
        const layEnd   = Math.floor(((pcsAssigned + pcsInBundle) / totalPcsForSize) * totalLays);
        const roll     = layMap[Math.min(layStart - 1, layMap.length - 1)];

        bundles.push({
          globalBundleNo: globalBundleNo++,
          sizeBundleNo:   sizeBundleNo++,
          size, qty: pcsInBundle, poNo,
          layStart, layEnd: Math.max(layStart, layEnd),
          rollId: roll?.rollId || '',
          isLast: pcsAssigned + pcsInBundle >= totalPcsForSize,
        });
        pcsAssigned += pcsInBundle;
      }
    });

    return bundles;
  };

  // ── MAIN CUT PLAN ALGORITHM ─────────────────────────────────────────────────
  // Excel formula validation:
  //   No Lays  = Balance_Qty ÷ Lay_Length          (decimal, e.g. 51÷7.5 = 6.8)
  //   End Bit  = (No Lays − FLOOR(No Lays)) × Lay_Length  (e.g. 0.8×7.5 = 6.0m)
  // Sort rolls by End Bit ASC → smallest waste first.
  // Greedy selection until cumulative full lays ≥ target.
  // Partial last-roll use allowed — remainder stays on shelf, end bit not wasted.
  const calculateCutPlan = () => {
    const { style, po, color, shade, shrinkage, pattern, csv,
            cutQty, layLength, bundleSize, ignoreShade, ignoreCSV,
            sizeBreakdown } = planData;

    if (!cutQty || !layLength) return alert('Fill Plan Cut Qty and Lay Length');
    if (!planData.consumption)  return alert('Fill Consumption per garment (m/pc) to calculate achievable pieces');

    const layLen   = parseFloat(layLength);
    const consPc   = parseFloat(planData.consumption);   // metres per garment
    const totalPcs = parseInt(cutQty);
    const bundleSz = parseInt(bundleSize) || 20;

    // ── Pcs per lay — EXACT float, not floored ────────────────────────────────
    // Flooring here causes pieces to be undercounted. Floor only the final total.
    const hasSizeBreakdown = sizeBreakdown && sizeBreakdown.length > 0;
    const pcsPerLayFloat = hasSizeBreakdown
      ? sizeBreakdown.reduce((s, sb) => s + (parseFloat(sb.qty) || 0), 0)
      : layLen / consPc;

    if (pcsPerLayFloat <= 0) return alert('Cannot calculate pcs per lay — check Lay Length and Consumption values.');

    // ── Total lays needed (rounded UP to always meet target) ─────────────────
    const laysNeeded = Math.ceil(totalPcs / pcsPerLayFloat);

    // ── Filter eligible rolls ─────────────────────────────────────────────────
    const eligible = inventory.filter(r => {
      const bal = parseFloat(r.Balance_Qty || r.M_Qty || 0);
      if (bal <= 0) return false;
      if (r.Quality_Status === 'Rejected') return false;
      if (style     && r.Style_Name    !== style)   return false;
      if (po        && r.Order_No      && r.Order_No !== po) return false;
      if (color     && (r.Color || '').toLowerCase() !== color.toLowerCase()) return false;
      if (!ignoreShade && shade && r.Shade       && r.Shade !== shade)         return false;
      if (!ignoreCSV   && csv   && r.CSV_Group   && r.CSV_Group !== csv)       return false;
      if (shrinkage && r.Shrinkage_Group && r.Shrinkage_Group !== shrinkage)   return false;
      if (pattern   && r.Pattern_Group   && r.Pattern_Group   !== pattern)     return false;
      return true;
    });

    if (!eligible.length) return alert('No matching rolls with positive balance! Check your Style / PO / Color / Shade filters.');

    // ── Per-roll analysis — matches Excel exactly ─────────────────────────────
    const analyzed = eligible.map(r => {
      const balance    = parseFloat(r.Balance_Qty || r.M_Qty || 0);
      const noLays     = balance / layLen;                  // "No Lays" col — decimal
      const fullLays   = Math.floor(noLays);                // integer lays from roll
      const usedFabric = fullLays * layLen;                 // metres in full lays
      const endBit     = parseFloat((balance - usedFabric).toFixed(4)); // "Ebit" col
      const endBitPct  = balance > 0 ? (endBit / balance * 100) : 0;
      return { ...r, balance, noLays, fullLays, usedFabric, endBit, endBitPct };
    })
    .filter(r => r.fullLays > 0)             // must yield ≥ 1 complete lay
    .sort((a, b) => a.endBit - b.endBit);    // smallest end bit first → least waste

    if (!analyzed.length) {
      const maxBal = Math.max(...eligible.map(r => parseFloat(r.Balance_Qty || r.M_Qty || 0)));
      return alert(`No rolls long enough to complete even 1 lay of ${layLen}m.\nLongest eligible roll: ${maxBal.toFixed(2)}m`);
    }

    // ── Scenario runner ───────────────────────────────────────────────────────
    const runScenario = (sortedRolls, targetLays, label, strategy) => {
      const selected = [];
      let laysAcc = 0, totalWaste = 0, totalUsed = 0, totalBal = 0;

      for (const roll of sortedRolls) {
        if (laysAcc >= targetLays) break;
        // Partial use of last roll: use only what's needed, no end-bit loss
        const laysFromRoll  = Math.min(roll.fullLays, targetLays - laysAcc);
        const usedFromRoll  = laysFromRoll * layLen;
        // End bit only wasted when the FULL roll is consumed
        const wasteFromRoll = (laysFromRoll === roll.fullLays) ? roll.endBit : 0;

        selected.push({ ...roll, laysUsed: laysFromRoll, usedFromRoll, wasteFromRoll });
        laysAcc    += laysFromRoll;
        totalWaste += wasteFromRoll;
        totalUsed  += usedFromRoll;
        totalBal   += roll.balance;
      }

      // Achievable pieces: floor(totalUsed / consumption) — float-safe
      const actualPcs  = Math.floor(totalUsed / consPc);
      const excessPcs  = actualPcs - totalPcs;
      const shortfall  = laysAcc < targetLays
        ? parseFloat(((targetLays - laysAcc) * layLen).toFixed(2)) : 0;
      // Utilisation = usable as % of ALL fabric brought to table
      const util       = totalBal > 0 ? (totalUsed / totalBal * 100) : 0;
      const bundles    = generateBundles(selected, sizeBreakdown, layLen, bundleSz, po);

      return {
        label, strategy, targetLays, laysAchieved: laysAcc,
        targetPcs: totalPcs, actualPcs, pcsPerLayFloat,
        totalUsed, totalWaste, totalBal, util,
        excessPcs, shortfall, selected, bundles,
      };
    };

    // Scenario 3 sort: largest usable fabric first → fewest rolls to handle
    const byUsedDesc = [...analyzed].sort((a, b) => b.usedFabric - a.usedFabric);

    // S1 🎯 Min End Bit — smallest waste rolls first, greedy to exact target
    const s0 = runScenario(analyzed,   laysNeeded,     `🎯 Min Waste — ${totalPcs} pcs (${laysNeeded} lays)`, 'min_waste');
    // S2 📈 +1 Lay Buffer — same sort, always adds one extra lay for safety margin
    const s1 = runScenario(analyzed,   laysNeeded + 1, `📈 +1 Lay Buffer — ~${Math.round((laysNeeded+1)*pcsPerLayFloat)} pcs`, 'buffer');
    // S3 ♻️ Fewest Rolls — largest rolls first, reaches target in fewest roll changes
    const s2 = runScenario(byUsedDesc, laysNeeded,     `♻️ Fewest Rolls — ${totalPcs} pcs`, 'fewest_rolls');

    setGenOptions([s0, s1, s2]);
    setSelPlan(null);
  };
  const getStatus=item=>{
    const rcv=parseFloat(item.Received_Qty||item.M_Qty||0);
    const iss=parseFloat(item.Issue_Qty||0);
    const bal=parseFloat(item.Balance_Qty??rcv-iss);
    const req=parseFloat(item.Required_Qty||0);
    if(iss>rcv||bal<0)return'critical';
    if(req===0)return bal===0?'critical':'adequate';
    const pct=(bal/req)*100;
    if(pct<10)return'critical';if(pct<30)return'low';if(pct>150)return'excess';return'adequate';
  };

  const filtered=inventory.filter(i=>{
    if(!search)return true;
    const q=search.toLowerCase();
    return['Roll_ID','Style_Name','SKU_ID','HSN_Code','Color','Order_No'].some(k=>(i[k]||'').toLowerCase().includes(q));
  });

  const critCount=inventory.filter(i=>getStatus(i)==='critical').length;

  const SavedRollSummary = ({ item }) => {
    if(!item) return null;
    const status=getStatus(item);
    const qcS=getRollQCStatus(db,item);
    const rtv=getRTVQty(db,item);
    const s=C[status];
    return (
      <div style={{background:s.bg,border:`1px solid ${s.border}`,borderRadius:'10px',padding:'14px',marginTop:'14px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
          <div style={{fontWeight:'800',fontSize:'13px',color:'#323338'}}>✅ Saved: <span style={{fontFamily:'monospace',color:'#8b5cf6'}}>{item.Roll_ID}</span> &nbsp; <span style={{fontFamily:'monospace',color:'#0073ea',fontSize:'11px'}}>{item.SKU_ID}</span></div>
          <div style={{display:'flex',gap:'8px'}}>
            <button onClick={()=>{setForm({...blank,...item});setEditId(item.id);}} style={{padding:'4px 10px',background:'#e8f0ff',color:'#0073ea',border:'1px solid #a8d1ff',borderRadius:'5px',cursor:'pointer',fontSize:'11px',fontWeight:'700'}}>Edit</button>
            <button onClick={()=>{if(window.confirm('Delete?')){
const n=inventory.filter(r=>r.id!==item.id);setInventory(n);setDb(p=>({...p,materials:n}));deleteFromDB('material',item.id);setLastSaved(null);}}} style={{padding:'4px 10px',background:'#fef2f2',color:'#c93652',border:'1px solid #fecaca',borderRadius:'5px',cursor:'pointer',fontSize:'11px',fontWeight:'700'}}>Delete</button>
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:'8px',fontSize:'11px'}}>
          {[['Style',item.Style_Name],['PO',item.Order_No],['Color',item.Color],['M Qty',item.M_Qty],['Rate',`₹${parseFloat(item.Rate||0).toFixed(2)}`],['Balance',parseFloat(item.Balance_Qty||0).toFixed(2)],['Rack',`${item.Rack_No}/${item.Shelf_No}`]].map(([l,v])=>(
            <div key={l} style={{background:'white',padding:'6px 10px',borderRadius:'6px',border:`1px solid ${s.border}`}}>
              <div style={{fontSize:'9px',color:'#676879',textTransform:'uppercase',fontWeight:'700'}}>{l}</div>
              <div style={{fontWeight:'700',color:'#323338',marginTop:'2px'}}>{v||'—'}</div>
            </div>
          ))}
          <div style={{background:'white',padding:'6px 10px',borderRadius:'6px',border:`1px solid ${s.border}`}}>
            <div style={{fontSize:'9px',color:'#676879',textTransform:'uppercase',fontWeight:'700'}}>QC</div>
            <QCBadge status={qcS}/>
          </div>
          {rtv>0 && <div style={{background:'#fef2f2',padding:'6px 10px',borderRadius:'6px',border:'1px solid #fecaca'}}>
            <div style={{fontSize:'9px',color:'#676879',textTransform:'uppercase',fontWeight:'700'}}>RTV Qty</div>
            <div style={{fontWeight:'800',color:'#c93652'}}>{rtv}</div>
          </div>}
          <div style={{background:'white',padding:'6px 10px',borderRadius:'6px',border:`1px solid ${s.border}`}}>
            <div style={{fontSize:'9px',color:'#676879',textTransform:'uppercase',fontWeight:'700'}}>Status</div>
            <StatusBadgeWithReason item={item}/>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <RequisitionModal open={showReq} onClose={()=>setShowReq(false)} onConfirm={handleIssueConfirm}
        items={inventory.map(i=>({...i,Item_Name:i.Roll_ID,Issued_Qty:i.Issue_Qty}))} db={db}/>

      <div style={{display:'flex',borderBottom:'1px solid #e6e9ef',background:'white',padding:'0 16px',marginBottom:'20px',borderRadius:'10px 10px 0 0',flexWrap:'wrap'}}>
        <SubTabBtn label="Inventory"    active={subTab==='inventory'}  onClick={()=>setSubTab('inventory')}  icon={Archive} count={critCount}/>
        <SubTabBtn label="Roll Inward"  active={subTab==='inward'}     onClick={()=>setSubTab('inward')}     icon={Plus}/>
        <SubTabBtn label="Bulk / Invoice" active={subTab==='bulk'}     onClick={()=>setSubTab('bulk')}       icon={Upload}/>
        <SubTabBtn label="Cut Optimiser" active={subTab==='roll_mgmt'} onClick={()=>setSubTab('roll_mgmt')} icon={Scissors}/>
        <SubTabBtn label="Issue (Slip)" active={subTab==='issue'}      onClick={()=>setSubTab('issue')}      icon={ClipboardList}/>
        <SubTabBtn label="Return"       active={subTab==='return'}     onClick={()=>setSubTab('return')}     icon={Undo2}/>
        <SubTabBtn label="QR Codes"     active={subTab==='qr'}         onClick={()=>setSubTab('qr')}         icon={QrCode}/>
        <SubTabBtn label="📊 Reconciliation" active={subTab==='recon'} onClick={()=>setSubTab('recon')}     icon={BarChart3}/>
      </div>

      {/* INVENTORY TABLE */}
      {subTab==='inventory' && (
        <div style={ss}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'14px',flexWrap:'wrap',gap:'10px'}}>
            <h3 style={{...hs,marginBottom:0}}><Layers size={14}/> Fabric Inventory ({filtered.length})</h3>
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
              <div style={{display:'flex',alignItems:'center',gap:'6px',border:'1px solid #e6e9ef',borderRadius:'6px',padding:'7px 10px'}}>
                <Search size={13} color="#676879"/><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…" style={{border:'none',outline:'none',fontSize:'12px',width:isTablet?'90px':'150px'}}/>
              </div>
              {selIds.size>0 && <button onClick={bulkDelete} style={{padding:'7px 12px',background:'#fef2f2',color:'#c93652',border:'1px solid #fecaca',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>🗑 Delete ({selIds.size})</button>}
              {selIds.size===1 && <button onClick={()=>{const it=inventory.find(i=>i.id===[...selIds][0]);if(it){setForm({...blank,...it});setEditId(it.id);setSubTab('inward');}}} style={{padding:'7px 12px',background:'#e8f0ff',color:'#0073ea',border:'1px solid #a8d1ff',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>✏ Edit</button>}
              <button onClick={()=>{setForm(blank);setEditId(null);setSubTab('inward')}} style={{padding:'7px 12px',background:'#a25ddc',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>+ Add Roll</button>
            </div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr>
                  <th style={{...TH,width:'36px'}}><input type="checkbox" checked={selIds.size===filtered.length&&filtered.length>0} onChange={toggleAll}/></th>
                  {['Lock','Status','Roll ID','SKU ID','HSN','Style','PO','Color','Shade','Width','M Qty','Req','Wst%','Ordered','Received','Issued','Balance','Diff','Days','Rate','QC Status','RTV','Defects','Rack','Actions'].map(h=><th key={h} style={{...TH,background:h==='Lock'?'#f0f1f4':undefined,width:h==='Lock'?'60px':undefined}}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {!filtered.length&&<tr><td colSpan={25} style={{...TD,textAlign:'center',padding:'40px',color:'#9699a6'}}>No rolls.</td></tr>}
                {filtered.map((row,i)=>{
                  const s=getStatus(row);
                  const qcS=getRollQCStatus(db,row);
                  const rtv=getRTVQty(db,row);
                  const defPts=getRollDefects(db,row);
                  const sel=selIds.has(row.id);
                  return (
                    <tr key={row.id} style={{background:sel?'#e8f0ff':i%2===0?'white':'#fafafa'}}>
                      <td style={{...TD,textAlign:'center'}}><input type="checkbox" checked={sel} onChange={()=>toggleSel(row.id)}/></td>
                      <td style={TD}>{row.lock_status?<span style={{fontSize:'10px',fontWeight:'800',padding:'2px 7px',borderRadius:'20px',background:row.lock_status==='received'?'#f0fdf4':row.lock_status==='issued'?'#e8f0ff':'#fffbeb',color:row.lock_status==='received'?'#00a65e':row.lock_status==='issued'?'#0073ea':'#fdab3d',whiteSpace:'nowrap'}}>{row.lock_status==='received'?'✅ Rcvd':row.lock_status==='issued'?'📤 Issued':'🔒 Locked'}</span>:'—'}</td>
                      <td style={TD} title={getStatusReason({...row,Issued_Qty:row.Issue_Qty})}><StatusBadgeWithReason item={{...row,Issued_Qty:row.Issue_Qty}}/></td>
                      <td style={{...TD,color:row.is_end_bit?'#c93652':'#8b5cf6',fontWeight:'800'}}>{row.Roll_ID}{row.is_end_bit?<span style={{fontSize:'9px',marginLeft:'4px',color:'#c93652'}}>EB</span>:null}</td>
                      <td style={{...TD,fontFamily:'monospace',fontSize:'10px',color:'#0073ea'}}>{row.SKU_ID}</td>
                      <td style={{...TD,fontFamily:'monospace',fontSize:'10px'}}>{row.HSN_Code}</td>
                      <td style={TD}>{row.Style_Name}</td>
                      <td style={TD}>{row.Order_No}</td>
                      <td style={TD}>{row.Color}</td>
                      <td style={TD}>{row.Shade}</td>
                      <td style={TD}>{row.Width}</td>
                      <td style={{...TD,textAlign:'right'}}>{row.M_Qty}</td>
                      <td style={{...TD,textAlign:'right',fontWeight:'700'}}>{row.Required_Qty}</td>
                      <td style={{...TD,textAlign:'right',color:'#fdab3d'}}>{row.Wastage_Pct}%</td>
                      <td style={{...TD,textAlign:'right'}}>{row.Ordered_Qty}</td>
                      <td style={{...TD,textAlign:'right',background:'#f0fdf4'}}>{row.Received_Qty}</td>
                      <td style={{...TD,textAlign:'right',background:'#fefce8'}}>{row.Issue_Qty}</td>
                      <td style={{...TD,textAlign:'right',fontWeight:'800',color:C[s].text,background:C[s].bg}} title={getStatusReason({...row,Issued_Qty:row.Issue_Qty})}>{parseFloat(row.Balance_Qty||0).toFixed(2)}</td>
                      <td style={{...TD,textAlign:'right',fontWeight:'700',color:parseFloat(row.Difference)>0?'#c93652':'#00a65e'}}>{parseFloat(row.Difference||0).toFixed(2)}</td>
                      <td style={{...TD,fontWeight:'700',color:parseFloat(row.Days_Coverage)<3?'#c93652':'#00a65e'}}>{row.Days_Coverage}d</td>
                      <td style={{...TD,textAlign:'right'}}>₹{parseFloat(row.Rate||0).toFixed(2)}</td>
                      <td style={TD}><QCBadge status={qcS}/></td>
                      <td style={{...TD,textAlign:'right',color:'#fdab3d',fontWeight:'700'}}>{rtv>0?rtv:'—'}</td>
                      <td style={{...TD,textAlign:'right',color:defPts>20?'#c93652':'#676879'}}>{defPts>0?defPts.toFixed(1):'—'}</td>
                      <td style={TD}>{row.Rack_No}/{row.Shelf_No}</td>
                      <td style={TD}>
                        <div style={{display:'flex',gap:'3px'}}>
                          <button onClick={()=>{setForm({...blank,...row});setEditId(row.id);setSubTab('inward')}} style={{padding:'3px 7px',background:'#e8f0ff',color:'#0073ea',border:'1px solid #a8d1ff',borderRadius:'4px',cursor:'pointer',fontSize:'10px'}}>Edit</button>
                          <button onClick={()=>{if(window.confirm('Delete?')){const n=inventory.filter(r=>r.id!==row.id);setInventory(n);setDb(p=>({...p,materials:n}));deleteFromDB('material',row.id);}}} style={{padding:'3px 7px',background:'#fef2f2',color:'#c93652',border:'1px solid #fecaca',borderRadius:'4px',cursor:'pointer',fontSize:'10px'}}>Del</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ROLL INWARD FORM */}
      {subTab==='inward' && (
        <div style={ss}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
            <h3 style={{...hs,marginBottom:0}}>{editId?'Edit Roll':'Roll Inward Entry'}</h3>
            {editId && <button onClick={()=>{setForm(blank);setEditId(null);setLastSaved(null);}} style={{padding:'6px 12px',background:'#f0f1f4',color:'#676879',border:'1px solid #e6e9ef',borderRadius:'6px',cursor:'pointer',fontSize:'12px'}}>✕ Cancel</button>}
          </div>
          {/* Roll Identity */}
          <div style={{background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'14px',marginBottom:'14px'}}>
            <div style={{fontSize:'11px',fontWeight:'800',color:'#676879',marginBottom:'12px'}}>📦 ROLL IDENTITY</div>
            <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(${isTablet?'140px':'175px'},1fr))`,gap:'12px'}}>
              <div>
                <Fld label="Roll ID" value={form.Roll_ID} onChange={e=>hc('Roll_ID',e)} error={fErrs.Roll_ID} required placeholder="Scan barcode…"/>
                <button onClick={genRollID} style={{fontSize:'11px',color:'#8b5cf6',background:'none',border:'none',cursor:'pointer',marginTop:'-2px'}}>⚡ Generate Roll + SKU</button>
              </div>
              <Fld label="SKU ID" value={form.SKU_ID} onChange={e=>hc('SKU_ID',e)} placeholder="FAB-000001"/>
              <Fld label="HSN Code" value={form.HSN_Code} onChange={e=>hc('HSN_Code',e)} placeholder="5208110000"/>
              <Fld label="Invoice No" value={form.Invoice_No} onChange={e=>hc('Invoice_No',e)}/>
              <Fld label="Recv Date" value={form.Invoice_Rcv_Date} onChange={e=>hc('Invoice_Rcv_Date',e)} type="date"/>
              <Fld label="Supplier Roll No" value={form.Supplier_Roll_No} onChange={e=>hc('Supplier_Roll_No',e)}/>
            </div>
          </div>
          {/* Order linkage — Style Registry */}
          <div style={{background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'14px',marginBottom:'14px'}}>
            <div style={{fontSize:'11px',fontWeight:'800',color:'#676879',marginBottom:'12px'}}>🔗 ORDER LINKAGE — from Style Registry</div>
            <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(${isTablet?'140px':'175px'},1fr))`,gap:'12px'}}>
              <div>
                <Fld label="Style Name" value={form.Style_Name} onChange={e=>hc('Style_Name',e)} error={fErrs.Style_Name} options={styleRefs}/>
                {!styleRefs.length&&<span style={{fontSize:'10px',color:'#fdab3d'}}>⚠ No styles in registry</span>}
              </div>
              <Fld label="Order No (PO)" value={form.Order_No} onChange={e=>hc('Order_No',e)} options={poOpts.length?poOpts:undefined} placeholder="Auto from style"/>
              <Fld label="Color" value={form.Color} onChange={e=>hc('Color',e)} options={colorOpts.length?colorOpts:undefined} placeholder="Auto from style"/>
              <Fld label="Shade" value={form.Shade} onChange={e=>hc('Shade',e)}/>
              <Fld label="Shrinkage Group" value={form.Shrinkage_Group} onChange={e=>hc('Shrinkage_Group',e)}/>
              <Fld label="Pattern Group" value={form.Pattern_Group} onChange={e=>hc('Pattern_Group',e)}/>
              <Fld label="CSV Group" value={form.CSV_Group} onChange={e=>hc('CSV_Group',e)}/>
              <Fld label="Width (inches)" value={form.Width} onChange={e=>hc('Width',e)}/>
              <Fld label="Lay Length (m)" value={form.Lay_Length} onChange={e=>hc('Lay_Length',e)} type="number" placeholder="e.g. 7.50"/>
              <Fld label="Consumption / Pc (m)" value={form.Consumption} onChange={e=>hc('Consumption',e)} type="number" placeholder="e.g. 1.65"/>
            </div>
          </div>
          {/* Quantity planning */}
          <div style={{background:'#e8f0ff',border:'1px solid #a8d1ff',borderRadius:'8px',padding:'14px',marginBottom:'14px'}}>
            <div style={{fontSize:'11px',fontWeight:'800',color:'#0062c4',marginBottom:'12px'}}>📊 QUANTITY PLANNING — AUTO-CALCULATED</div>
            <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(${isTablet?'135px':'155px'},1fr))`,gap:'12px'}}>
              <Fld label="M Qty" value={form.M_Qty} onChange={e=>hc('M_Qty',e)} type="number"/>
              <Fld label="Rate (₹/m)" value={form.Rate} onChange={e=>hc('Rate',e)} type="number"/>
              <Fld label="Price (Auto)" value={form.Price} readOnly/>
              <Fld label="Required Qty" value={form.Required_Qty} onChange={e=>hc('Required_Qty',e)} type="number"/>
              <Fld label="Wastage %" value={form.Wastage_Pct} onChange={e=>hc('Wastage_Pct',e)} type="number"/>
              <Fld label="Wastage Qty (Auto)" value={form.Wastage_Qty} readOnly/>
              <Fld label="Ordered Qty" value={form.Ordered_Qty} onChange={e=>hc('Ordered_Qty',e)} type="number"/>
              <Fld label="Received Qty" value={form.Received_Qty} onChange={e=>hc('Received_Qty',e)} type="number"/>
              <div>
                <Fld label="Issued Qty" value={form.Issue_Qty} onChange={e=>{
                  const v=parseFloat(e.target.value)||0;
                  const rcv=parseFloat(form.Received_Qty||form.M_Qty)||0;
                  if(v>rcv){alert(`Cannot issue more than received (${rcv}m)!`);return;}
                  hc('Issue_Qty',e);
                }} type="number" error={fErrs.Issue_Qty}/>
              </div>
              <Fld label="Balance (Auto)" value={form.Balance_Qty} readOnly/>
              <Fld label="Difference (Auto)" value={form.Difference} readOnly/>
              <Fld label="Daily Consumption" value={form.Daily_Consumption} onChange={e=>hc('Daily_Consumption',e)} type="number"/>
              <Fld label="Days Coverage (Auto)" value={form.Days_Coverage} readOnly/>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(${isTablet?'140px':'170px'},1fr))`,gap:'12px',marginBottom:'14px'}}>
            <Fld label="Rack No" value={form.Rack_No} onChange={e=>hc('Rack_No',e)}/>
            <Fld label="Shelf No" value={form.Shelf_No} onChange={e=>hc('Shelf_No',e)}/>
            <Fld label="Cell No" value={form.Cell_No} onChange={e=>hc('Cell_No',e)}/>
            <Fld label="Quality Status" value={form.Quality_Status} onChange={e=>hc('Quality_Status',e)} options={['Pending','Approved','Rejected','On Hold']}/>
            <Fld label="Defect Count" value={form.Defect_Count} onChange={e=>hc('Defect_Count',e)} type="number"/>
            <Fld label="RTV Qty" value={form.RTV_Qty} onChange={e=>hc('RTV_Qty',e)} type="number"/>
          </div>
          <Fld label="Remarks" value={form.Remarks} onChange={e=>hc('Remarks',e)}/>
          <div style={{display:'flex',gap:'12px',marginTop:'12px'}}>
            <button onClick={()=>handleSubmit(false)} style={{flex:2,padding:'13px',background:'#a25ddc',color:'white',border:'none',borderRadius:'8px',fontWeight:'800',cursor:'pointer',fontSize:'14px'}}>
              {editId?'✏️ UPDATE ROLL':'💾 SAVE ROLL TO DATABASE'}
            </button>
            {!editId && <button onClick={()=>handleSubmit(true)} style={{flex:1,padding:'13px',background:'#1f1f1f',color:'white',border:'none',borderRadius:'8px',fontWeight:'700',cursor:'pointer',fontSize:'13px'}}>
              💾 Save & Add New
            </button>}
          </div>
          {lastSaved && !editId && <SavedRollSummary item={lastSaved}/>}
        </div>
      )}

      {/* BULK / INVOICE */}
      {subTab==='bulk' && (
        <div>
          <div style={ss}>
            <h3 style={hs}><Upload size={14}/> Bulk Upload — Fabric Rolls</h3>
            <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'8px',padding:'14px',marginBottom:'18px',fontSize:'12px',color:'#78350f'}}>
              Headers must match template. Style_Name validated vs Style Registry. Issued ≤ Received enforced.
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'18px'}}>
              <div style={{border:'2px dashed #e6e9ef',borderRadius:'10px',padding:'22px',textAlign:'center'}}>
                <FileText size={32} color="#676879" style={{margin:'0 auto 10px'}}/>
                <div style={{fontWeight:'700',fontSize:'13px',marginBottom:'10px'}}>Download CSV Template</div>
                <button onClick={downloadTemplate} style={{padding:'9px 20px',background:'#1f1f1f',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'700'}}>⬇ Download</button>
              </div>
              <div style={{border:'2px dashed #c4b5fd',borderRadius:'10px',padding:'22px',textAlign:'center',background:'#faf5ff'}}>
                <Upload size={32} color="#a25ddc" style={{margin:'0 auto 10px'}}/>
                <div style={{fontWeight:'700',fontSize:'13px',marginBottom:'10px'}}>Upload CSV</div>
                <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={handleCSVUpload}/>
                <button onClick={()=>fileRef.current.click()} style={{padding:'9px 20px',background:'#a25ddc',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'700'}}>📂 Import</button>
              </div>
            </div>
          </div>
          <InvoiceReader category="Fabric" onExtracted={data=>{
            setForm(f=>({...f,
              Invoice_No:data.invoice_no||data.invoiceNo||f.Invoice_No,
              Invoice_Rcv_Date:data.date||data.invoice_date||f.Invoice_Rcv_Date,
              M_Qty:data.total_qty||data.qty||f.M_Qty,
              Rate:data.unit_price||data.rate||f.Rate,
              Roll_ID:data.roll_no||data.lot_no||f.Roll_ID||`R-${Date.now().toString().slice(-6)}`,
            }));
            setSubTab('inward');
          }}/>
        </div>
      )}

      {/* CUT OPTIMISER — uses Style Registry for style/PO/color dropdowns */}
      {subTab==='roll_mgmt' && (
        <div style={ss}>
          <h3 style={hs}><Scissors size={15}/> Cut Optimisation Algorithm — End-Bit Minimiser</h3>
          <div style={{background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'14px',marginBottom:'18px'}}>
            <div style={{fontSize:'11px',fontWeight:'800',color:'#676879',marginBottom:'12px',textTransform:'uppercase'}}>🔍 Filter Rolls — Style Registry linked</div>
            <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(${isTablet?'140px':'165px'},1fr))`,gap:'12px'}}>
              <div>
                <Fld label="Style (Registry)" value={planData.style} onChange={e=>{setPlanData(p=>({...p,style:e.target.value,po:'',color:''}));}} options={cutStyleOpts}/>
              </div>
              <Fld label="PO No" value={planData.po} onChange={e=>setPlanData(p=>({...p,po:e.target.value}))} options={cutPOOpts}/>
              <Fld label="Color" value={planData.color} onChange={e=>setPlanData(p=>({...p,color:e.target.value}))} options={cutColorOpts}/>
              <Fld label="Shade" value={planData.shade} onChange={e=>setPlanData(p=>({...p,shade:e.target.value}))} options={getOpts('Shade')}/>
              <Fld label="Shrinkage" value={planData.shrinkage} onChange={e=>setPlanData(p=>({...p,shrinkage:e.target.value}))} options={getOpts('Shrinkage_Group')}/>
              <Fld label="Pattern" value={planData.pattern} onChange={e=>setPlanData(p=>({...p,pattern:e.target.value}))} options={getOpts('Pattern_Group')}/>
            </div>
          </div>
          <div style={{background:'#e8f0ff',border:'1px solid #a8d1ff',borderRadius:'8px',padding:'14px',marginBottom:'18px'}}>
            <div style={{fontSize:'11px',fontWeight:'800',color:'#0062c4',marginBottom:'12px',textTransform:'uppercase'}}>✂️ Cut Plan Parameters</div>
            <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(180px,1fr))`,gap:'12px',alignItems:'end'}}>
              <Fld label="Plan Cut Qty (Pcs)" value={planData.cutQty} onChange={e=>setPlanData(p=>({...p,cutQty:e.target.value}))} type="number" required/>
              <Fld label="Lay Length (m)" value={planData.layLength} onChange={e=>setPlanData(p=>({...p,layLength:e.target.value}))} type="number" required/>
              <Fld label="Consumption / Pc (m)" value={planData.consumption} onChange={e=>setPlanData(p=>({...p,consumption:e.target.value}))} type="number" required/>
              <Fld label="Bundle Size (pcs)" value={planData.bundleSize} onChange={e=>setPlanData(p=>({...p,bundleSize:e.target.value}))} type="number"/>
              <div style={{display:'flex',flexDirection:'column',gap:'6px',justifyContent:'flex-end',paddingBottom:'4px'}}>
                <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',cursor:'pointer'}}>
                  <input type="checkbox" checked={planData.ignoreShade} onChange={e=>setPlanData(p=>({...p,ignoreShade:e.target.checked}))}/> Ignore Shade
                </label>
                <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',cursor:'pointer'}}>
                  <input type="checkbox" checked={planData.ignoreCSV} onChange={e=>setPlanData(p=>({...p,ignoreCSV:e.target.checked}))}/> Ignore CSV Group
                </label>
              </div>
              <div>
                <div style={{fontSize:'10px',fontWeight:'700',color:'#676879',marginBottom:'3px'}}>&nbsp;</div>
                <button onClick={calculateCutPlan} style={{width:'100%',padding:'9px',background:'#0073ea',color:'white',fontWeight:'800',border:'none',borderRadius:'6px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                  <Scissors size={13}/> RUN ALGORITHM
                </button>
              </div>
            </div>
            <div style={{marginTop:'8px',fontSize:'11px',color:'#0073ea'}}>💡 Rolls sorted by smallest end-bit → least waste first. End bit = roll balance mod lay length.</div>
          </div>
          {genOptions && (
            <>
              <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(${isTablet?'200px':'240px'},1fr))`,gap:'14px',marginBottom:'22px'}}>
                {genOptions.map((opt,idx)=>{
                  const stratDesc = opt.strategy==='min_waste'?'Smallest end bits first — least fabric waste'
                                  : opt.strategy==='buffer'?'Same sort + 1 extra lay — built-in safety buffer'
                                  : 'Largest rolls first — fewest rolls to handle';
                  const utilColor = opt.util>=90?'#00a65e':opt.util>=75?'#fdab3d':'#c93652';
                  const excessColor = (opt.excessPcs||0)>=0?'#00a65e':'#c93652';
                  return (
                    <div key={idx} onClick={()=>setSelPlan(opt)} style={{border:selPlan===opt?'2px solid #0073ea':'1px solid #c5c7d4',borderRadius:'10px',padding:'16px',cursor:'pointer',background:selPlan===opt?'#e8f0ff':'white',boxShadow:selPlan===opt?'0 0 0 3px #a8d1ff':undefined,transition:'all 0.15s'}}>
                      <div style={{fontWeight:'800',color:'#323338',marginBottom:'3px',fontSize:'13px'}}>{opt.label}</div>
                      <div style={{fontSize:'11px',color:'#9699a6',marginBottom:'10px',fontStyle:'italic'}}>{stratDesc}</div>
                      <div style={{fontSize:'38px',fontWeight:'900',lineHeight:1,color:utilColor}}>{opt.util.toFixed(1)}%</div>
                      <div style={{fontSize:'11px',color:'#676879',marginBottom:'10px'}}>Fabric Utilisation</div>
                      {(opt.shortfall||0)>0&&<div style={{fontSize:'11px',color:'#c93652',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:'6px',padding:'4px 8px',marginBottom:'8px'}}>⚠ Short {opt.shortfall}m — add more rolls</div>}
                      <div style={{borderTop:'1px solid #e6e9ef',paddingTop:'8px',display:'flex',flexDirection:'column',gap:'4px',fontSize:'11px'}}>
                        {[
                          ['Target Pcs',    opt.targetPcs,       '#323338'],
                          ['Achievable Pcs',opt.actualPcs,       (opt.actualPcs||0)>=(opt.targetPcs||0)?'#00a65e':'#c93652'],
                          ['Excess Pcs',    ((opt.excessPcs||0)>=0?'+':'')+(opt.excessPcs||0), excessColor],
                          ['Lays Spread',   opt.laysAchieved,    '#0073ea'],
                          ['Rolls Needed',  opt.selected.length, '#323338'],
                          ['Fabric Pulled', (opt.totalBal||0).toFixed(1)+'m','#323338'],
                          ['Fabric Used',   opt.totalUsed.toFixed(2)+'m','#00a65e'],
                          ['End-Bit Waste', opt.totalWaste.toFixed(2)+'m','#c93652'],
                        ].map(([l,v,col])=>(
                          <div key={l} style={{display:'flex',justifyContent:'space-between'}}>
                            <span style={{color:'#676879'}}>{l}</span>
                            <strong style={{color:col}}>{v}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              {selPlan && (
                <div>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                    <h4 style={{fontSize:'13px',fontWeight:'800',color:'#323338'}}>📋 Execution Plan — {selPlan.label}</h4>
                    <div style={{fontSize:'12px',color:'#676879'}}>{selPlan.selected.length} rolls | {selPlan.laysAchieved} lays | <strong style={{color:(selPlan.actualPcs||0)>=(selPlan.targetPcs||0)?'#00a65e':'#c93652'}}>{selPlan.actualPcs||0} achievable pcs</strong> | util: <strong style={{color:'#0073ea'}}>{selPlan.util.toFixed(1)}%</strong></div>
                  </div>
                  <div style={{overflowX:'auto',border:'1px solid #e6e9ef',borderRadius:'8px'}}>
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['#','Roll ID','SKU ID','Style','PO','Color','Shade','Width','Balance (m)','No. Lays','Full Lays','End Bit (m)','End Bit %','Lays Used','Used (m)','Waste (m)','Rack','Cell'].map((h,i)=>(<th key={h} style={{...TH,background:h.includes('End Bit')||h==='Waste (m)'?'#fef2f2':h==='Used (m)'||h==='Full Lays'||h==='No. Lays'?'#f0fdf4':undefined}}>{h}</th>))}</tr></thead>
                      <tbody>
                        {selPlan.selected.map((r,i)=>(
                          <tr key={i} style={{borderBottom:'1px solid #f0f1f4',background:i%2===0?'white':'#fafafa'}}>
                            <td style={{...TD,color:'#9699a6',fontSize:'10px'}}>{i+1}</td>
                            <td style={{...TD,color:'#8b5cf6',fontWeight:'800',fontFamily:'monospace'}}>{r.Roll_ID}</td>
                            <td style={{...TD,color:'#0073ea',fontSize:'10px',fontFamily:'monospace'}}>{r.SKU_ID}</td>
                            <td style={TD}>{r.Style_Name}</td>
                            <td style={TD}>{r.Order_No}</td>
                            <td style={TD}>{r.Color}</td>
                            <td style={TD}>{r.Shade}</td>
                            <td style={TD}>{r.Width}{r.Width?'"':''}</td>
                            <td style={{...TD,textAlign:'right',fontWeight:'700'}}>{(r.balance||r.rollLen||0).toFixed(2)}</td>
                            <td style={{...TD,textAlign:'right',background:'#f0fdf4',color:'#00a65e'}}>{(r.noLays||0).toFixed(4)}</td>
                            <td style={{...TD,textAlign:'right',fontWeight:'800',background:'#f0fdf4',color:'#00a65e'}}>{r.fullLays||r.potentialLays||0}</td>
                            <td style={{...TD,textAlign:'right',fontWeight:'800',background:'#fef2f2',color:(r.endBit||0)>0?'#c93652':'#00a65e'}}>{(r.endBit||0).toFixed(4)}</td>
                            <td style={{...TD,textAlign:'right',background:'#fef2f2',color:(r.endBitPct||0)>5?'#c93652':'#9699a6'}}>{(r.endBitPct||0).toFixed(1)}%</td>
                            <td style={{...TD,textAlign:'right',fontWeight:'800',color:'#0073ea'}}>{r.laysUsed}{r.laysUsed<(r.fullLays||r.potentialLays||0)?<span style={{color:'#fdab3d',fontSize:'10px'}}> ↩</span>:null}</td>
                            <td style={{...TD,textAlign:'right',fontWeight:'800',background:'#f0fdf4',color:'#00a65e'}}>{(r.usedFromRoll||0).toFixed(2)}</td>
                            <td style={{...TD,textAlign:'right',fontWeight:'800',background:(r.wasteFromRoll||0)>0?'#fef2f2':'white',color:(r.wasteFromRoll||0)>0?'#c93652':'#00a65e'}}>{(r.wasteFromRoll||0).toFixed(2)}</td>
                            <td style={{...TD,fontWeight:'700',color:'#8b5cf6'}}>{r.Rack_No}</td>
                            <td style={TD}>{r.Cell_No||r.Shelf_No||'—'}</td>
                          </tr>
                        ))}
                        <tr style={{background:'#323338',borderTop:'2px solid #e6e9ef',fontWeight:'800'}}>
                          <td colSpan={8} style={{...TD,textAlign:'right',fontWeight:'800',color:'white',background:'#323338'}}>TOTALS →</td>
                          <td style={{...TD,textAlign:'right',fontWeight:'800',color:'#93c5fd',background:'#323338'}}>{(selPlan.totalBal||selPlan.selected.reduce((s,r)=>s+(r.balance||r.rollLen||0),0)).toFixed(2)}</td>
                          <td style={{...TD,background:'#323338'}}/><td style={{...TD,background:'#323338'}}/>
                          <td style={{...TD,background:'#323338'}}/><td style={{...TD,background:'#323338'}}/>
                          <td style={{...TD,textAlign:'right',fontWeight:'800',color:'#5ba4f5',background:'#323338'}}>{selPlan.laysAchieved}</td>
                          <td style={{...TD,textAlign:'right',fontWeight:'800',background:'#323338',color:'#4ade80'}}>{selPlan.totalUsed.toFixed(2)}</td>
                          <td style={{...TD,textAlign:'right',fontWeight:'800',background:'#323338',color:'#eb6175'}}>{selPlan.totalWaste.toFixed(2)}</td>
                          <td style={{...TD,background:'#323338'}}/><td style={{...TD,background:'#323338'}}/>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* ── BUNDLE LIST ─────────────────────────────────── */}
              {selPlan && selPlan.bundles && selPlan.bundles.length > 0 && (
                <div style={{marginTop:'20px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                    <h4 style={{fontSize:'13px',fontWeight:'800',color:'#323338'}}>📦 Bundle List — {selPlan.bundles.length} bundles | {planData.bundleSize} pcs/bundle</h4>
                    <div style={{fontSize:'11px',color:'#676879'}}>{selPlan.laysAchieved} lays × {(selPlan.pcsPerLayFloat||selPlan.pcsPerLay||0).toFixed(2)} pcs/lay = {selPlan.actualPcs||0} pcs achievable</div>
                  </div>
                  <div style={{overflowX:'auto',border:'1px solid #e6e9ef',borderRadius:'8px'}}>
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['Bundle#','Size Bndl','Size','Qty','PO','Lay Start','Lay End','Roll ID','Note'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
                      <tbody>
                        {selPlan.bundles.map((b,i)=>(
                          <tr key={i} style={{borderBottom:'1px solid #f0f1f4',background:b.isLast?'#fefce8':i%2===0?'white':'#fafafa'}}>
                            <td style={{...TD,fontWeight:'800',color:'#8b5cf6',fontFamily:'monospace'}}>#{String(b.globalBundleNo).padStart(4,'0')}</td>
                            <td style={{...TD,color:'#676879',fontFamily:'monospace'}}>#{b.sizeBundleNo}</td>
                            <td style={{...TD,fontWeight:'800',color:'#1e3a8a',background:'#e8f0ff'}}>{b.size}</td>
                            <td style={{...TD,textAlign:'right',fontWeight:'800',color:b.isLast?'#fdab3d':'#00a65e'}}>{b.qty}</td>
                            <td style={TD}>{b.poNo||'—'}</td>
                            <td style={{...TD,textAlign:'right',fontFamily:'monospace'}}>{b.layStart}</td>
                            <td style={{...TD,textAlign:'right',fontFamily:'monospace'}}>{b.layEnd}</td>
                            <td style={{...TD,color:'#8b5cf6',fontFamily:'monospace',fontSize:'11px'}}>{b.rollId||'—'}</td>
                            <td style={{...TD,textAlign:'center',color:'#fdab3d'}}>{b.isLast?'⚠ Split':''}</td>
                          </tr>
                        ))}
                        <tr style={{background:'#f5f6f8',borderTop:'2px solid #e6e9ef',fontWeight:'800'}}>
                          <td colSpan={3} style={{...TD,textAlign:'right'}}>TOTAL BUNDLES</td>
                          <td style={{...TD,textAlign:'right',color:'#0073ea'}}>{selPlan.bundles.reduce((s,b)=>s+b.qty,0)}</td>
                          <td colSpan={5} style={{...TD,fontSize:'11px',color:'#676879'}}>⚠ = split bundle (last of its size, may have fewer pcs)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
          {/* ── PROCESS: LOCK → ISSUE → RECEIVE ────────────────────── */}
          {selPlan && (
            <CutPlanProcessor
              selPlan={selPlan}
              planData={planData}
              inventory={inventory}
              onInventoryUpdate={newInv => { localOp.current=true; setInventory(newInv); setDb(p=>({...p,materials:newInv})); setLastIssueAt(Date.now()); }}
              db={db}
            />
          )}
          {!genOptions && (
            <div style={{textAlign:'center',padding:'50px',color:'#9699a6'}}>
              <Scissors size={40} style={{margin:'0 auto 12px',opacity:0.3}}/>
              <div style={{fontWeight:'700',fontSize:'14px',color:'#676879'}}>Set parameters above and click RUN ALGORITHM</div>
            </div>
          )}
        </div>
      )}

      {/* ISSUE VIA SLIP */}
      {subTab==='issue' && (
        <div style={{display:'grid',gridTemplateColumns:isTablet?'1fr':'360px 1fr',gap:'20px'}}>
          <div style={ss}>
            <h3 style={hs}><ClipboardList size={14}/> Issue Fabric via Requisition</h3>
            <p style={{fontSize:'13px',color:'#676879',marginBottom:'16px'}}>All fabric issues recorded with Slip No, Style, PO, Color, Size, Section, Line, Supervisor.</p>
            <button onClick={()=>setShowReq(true)} style={{width:'100%',padding:'14px',background:'#a25ddc',color:'white',border:'none',borderRadius:'8px',fontWeight:'800',cursor:'pointer',fontSize:'14px'}}>📋 CREATE REQUISITION SLIP</button>
          </div>
          <div style={ss}>
            <h3 style={hs}>Issue Log ({issueLogs.length})</h3>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr>{['Slip No','Date','Style','PO','Color','Size','Section','Roll','Qty','Supervisor','Handed To'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
                <tbody>
                  {issueLogs.map((l,i)=>(
                    <tr key={i} style={{background:i%2===0?'white':'#fafafa'}}>
                      <td style={{...TD,color:'#a25ddc',fontWeight:'700'}}>{l.Slip_No}</td>
                      <td style={TD}>{l.Date}</td><td style={TD}>{l.Style_Name}</td><td style={TD}>{l.Order_No}</td>
                      <td style={TD}>{l.Color}</td><td style={TD}>{l.Size}</td><td style={TD}>{l.Section}</td>
                      <td style={{...TD,fontWeight:'700'}}>{l.item_name}</td>
                      <td style={{...TD,textAlign:'right',fontWeight:'800',color:'#fdab3d'}}>{l.Qty}m</td>
                      <td style={TD}>{l.Supervisor}</td><td style={TD}>{l.Handed_To}</td>
                    </tr>
                  ))}
                  {!issueLogs.length&&<tr><td colSpan={11} style={{...TD,textAlign:'center',color:'#9699a6',padding:'30px'}}>No issues yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* RETURN */}
      {subTab==='return' && (
        <div style={{display:'grid',gridTemplateColumns:isTablet?'1fr':'360px 1fr',gap:'20px'}}>
          <div style={ss}>
            <h3 style={hs}><Undo2 size={14}/> Process Return</h3>
            <Fld label="Scan Roll ID" value={retForm.rollId} onChange={e=>setRetForm(p=>({...p,rollId:e.target.value}))} placeholder="Scan barcode…"/>
            <Fld label="Return Qty (m)" value={retForm.returnQty} onChange={e=>setRetForm(p=>({...p,returnQty:e.target.value}))} type="number" style={{marginTop:'10px'}}/>
            <Fld label="Reason" value={retForm.reason} onChange={e=>setRetForm(p=>({...p,reason:e.target.value}))} options={['Excess from Table','Defect Found','Wrong Issue','End Bit Return']} style={{marginTop:'10px'}}/>
            <Fld label="Remarks" value={retForm.remarks} onChange={e=>setRetForm(p=>({...p,remarks:e.target.value}))} style={{marginTop:'10px'}}/>
            <button onClick={handleReturnSubmit} style={{width:'100%',marginTop:'12px',padding:'12px',background:'#fdab3d',color:'white',border:'none',borderRadius:'6px',fontWeight:'800',cursor:'pointer'}}>PROCESS RETURN</button>
          </div>
          <div style={ss}>
            <h3 style={hs}>Return History</h3>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['Date','Roll ID','Style','Qty','Reason'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
              <tbody>
                {returns.map((r,i)=><tr key={i}><td style={TD}>{r.date}</td><td style={{...TD,color:'#8b5cf6',fontWeight:'700'}}>{r.rollId}</td><td style={TD}>{r.style}</td><td style={{...TD,color:'#00a65e',fontWeight:'800'}}>+{r.returnQty}m</td><td style={TD}>{r.reason}</td></tr>)}
                {!returns.length&&<tr><td colSpan={5} style={{...TD,textAlign:'center',color:'#9699a6',padding:'24px'}}>No returns yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* RECONCILIATION */}
      {subTab==='recon' && (
        <FabricReconciliation inventory={inventory} db={db} setDb={setDb} lastIssueAt={lastIssueAt}/>
      )}

      {/* QR CODES */}
      {subTab==='qr' && (
        <div style={ss}>
          <h3 style={hs}><QrCode size={14}/> QR Codes — Fabric Rolls & Locations</h3>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'18px'}}>
            <div style={{background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'14px'}}>
              <div style={{fontWeight:'700',fontSize:'13px',marginBottom:'12px',color:'#676879'}}>📦 Roll QR Codes</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'10px'}}>
                {inventory.slice(0,16).map(roll=>(
                  <QRImg key={roll.id} data={`ROLL:${roll.Roll_ID}|SKU:${roll.SKU_ID}|STYLE:${roll.Style_Name}|PO:${roll.Order_No}|RACK:${roll.Rack_No}|SHELF:${roll.Shelf_No}`} label={roll.Roll_ID} size={100}/>
                ))}
                {!inventory.length&&<div style={{color:'#9699a6',fontSize:'12px'}}>No rolls yet</div>}
              </div>
            </div>
            <div style={{background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'14px'}}>
              <div style={{fontWeight:'700',fontSize:'13px',marginBottom:'12px',color:'#676879'}}>🗄 Rack / Shelf QR Codes</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'10px'}}>
                {[...new Set(inventory.map(r=>`${r.Rack_No}|${r.Shelf_No}`).filter(x=>x!=='|'&&x))].map(loc=>{
                  const [rn,sn]=loc.split('|');
                  return <QRImg key={loc} data={`LOCATION:RACK-${rn}|SHELF-${sn}|TYPE:Fabric`} label={`${rn}/${sn}`} size={100}/>;
                })}
                {!inventory.length&&<div style={{color:'#9699a6',fontSize:'12px'}}>No rack data</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PART 9 — REPORTS MODULE
// ============================================================================

function ReportsModule({ fabric, trims, accessories, packing, db }) {
  const all=[...fabric.map(i=>({...i,_cat:'Fabric',_c:'#8b5cf6'})),...trims.map(i=>({...i,_cat:'Trims',_c:'#0073ea'})),...accessories.map(i=>({...i,_cat:'Accessories',_c:'#fdab3d'})),...packing.map(i=>({...i,_cat:'Packing',_c:'#00a65e'}))];
  const totalVal=all.reduce((s,i)=>(parseFloat(i.Balance_Qty||0))*(parseFloat(i.Rate||0))+s,0);
  const critItems=all.filter(i=>getStockStatus(i)==='critical');
  const lowItems=all.filter(i=>getStockStatus(i)==='low');
  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:'14px',marginBottom:'20px'}}>
        <StatCard label="Total SKUs" value={all.length} icon={Barcode} color="#0073ea"/>
        <StatCard label="Inv. Value" value={`₹${(totalVal/1000).toFixed(1)}K`} icon={BarChart3} color="#00a65e"/>
        <StatCard label="Critical" value={critItems.length} sub="Immediate action" icon={AlertTriangle} color="#c93652"/>
        <StatCard label="Low Stock" value={lowItems.length} sub="Order soon" icon={TrendingDown} color="#fdab3d"/>
      </div>
      {critItems.length>0&&(
        <div style={{...ss,border:'1px solid #fecaca',background:'#fff8f8'}}>
          <h3 style={{...hs,color:'#c93652'}}><AlertCircle size={14}/> 🚨 Critical Shortage</h3>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['Cat','SKU ID','Item','Balance','Required','Shortage','Days','Supplier','Reason'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
              <tbody>
                {critItems.map((i,idx)=>(
                  <tr key={idx} style={{background:idx%2===0?'#fff8f8':'white'}}>
                    <td style={TD}><span style={{background:i._c+'20',color:i._c,padding:'1px 6px',borderRadius:'4px',fontSize:'10px',fontWeight:'700'}}>{i._cat}</span></td>
                    <td style={{...TD,fontFamily:'monospace',color:'#c93652',fontWeight:'700'}}>{i.SKU_ID}</td>
                    <td style={{...TD,fontWeight:'700'}}>{i.Item_Name||i.Roll_ID}</td>
                    <td style={{...TD,color:'#c93652',fontWeight:'800'}}>{parseFloat(i.Balance_Qty||0).toFixed(2)}</td>
                    <td style={TD}>{i.Required_Qty}</td>
                    <td style={{...TD,color:'#c93652',fontWeight:'800'}}>{parseFloat(i.Difference||0).toFixed(2)}</td>
                    <td style={{...TD,color:'#c93652',fontWeight:'800'}}>{i.Days_Coverage}d</td>
                    <td style={TD}>{i.Supplier||'—'}</td>
                    <td style={{...TD,fontSize:'10px',color:'#676879',maxWidth:'160px',whiteSpace:'normal'}}>{getStatusReason(i)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div style={ss}>
        <h3 style={hs}><BarChart3 size={14}/> Category Summary</h3>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Category','Items','Critical','Low','Adequate','Excess','Inventory Value'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {[{l:'Fabric',items:fabric,c:'#8b5cf6'},{l:'Trims',items:trims,c:'#0073ea'},{l:'Accessories',items:accessories,c:'#fdab3d'},{l:'Packing',items:packing,c:'#00a65e'}].map(({l,items,c})=>{
                const val=items.reduce((s,i)=>(parseFloat(i.Balance_Qty||0))*(parseFloat(i.Rate||0))+s,0);
                return (
                  <tr key={l} style={{borderBottom:'1px solid #f0f1f4'}}>
                    <td style={TD}><span style={{background:c+'20',color:c,padding:'3px 8px',borderRadius:'4px',fontSize:'11px',fontWeight:'800'}}>{l}</span></td>
                    <td style={{...TD,textAlign:'center',fontWeight:'700'}}>{items.length}</td>
                    {['critical','low','adequate','excess'].map(s=>(
                      <td key={s} style={{...TD,textAlign:'center',fontWeight:'700',color:C[s].text}}>{items.filter(i=>getStockStatus(i)===s).length}</td>
                    ))}
                    <td style={{...TD,textAlign:'right',fontWeight:'700'}}>₹{val.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// PART 11 — FABRIC PROCESSING MODULE
// Greige → Dyeing → Printing → Warehouse
// Tracks fabric lot-by-lot through every processing stage with full loss accounting
// ============================================================================

// ── Calculation helpers ───────────────────────────────────────────────────────
const calcLotStatus = (lot) => {
  if (!lot) return 'greige';
  if (lot.stage3?.warehouse_date) return 'in_warehouse';
  if (lot.stage3?.received_from_printing) return 'printing_received';
  if (lot.stage2?.issued_to_printing_qty) return 'in_printing';
  if (lot.stage2?.received_from_dyeing) return 'dyeing_received';
  if (lot.stage1?.issued_to_dyeing_qty) return 'in_dyeing';
  return 'greige';
};

const STAGE_LABELS = {
  greige:             { label:'Greige',           color:'#676879', bg:'#f5f6f8' },
  in_dyeing:          { label:'In Dyeing',        color:'#8b5cf6', bg:'#f5f3ff' },
  dyeing_received:    { label:'Dyed ✓',           color:'#0073ea', bg:'#e8f0ff' },
  in_printing:        { label:'In Printing',      color:'#fdab3d', bg:'#fffbeb' },
  printing_received:  { label:'Printed ✓',        color:'#db2777', bg:'#fdf2f8' },
  in_warehouse:       { label:'In Warehouse',     color:'#00a65e', bg:'#f0fdf4' },
};

const fmtQty  = v => (parseFloat(v)||0).toFixed(2);
const fmtPct  = v => (parseFloat(v)||0).toFixed(2) + '%';
const fmtVal  = v => '₹' + (parseFloat(v)||0).toFixed(2);
const lossPct = (issued, received) => {
  const iss = parseFloat(issued)||0, rcv = parseFloat(received)||0;
  return iss > 0 ? (((iss - rcv) / iss) * 100).toFixed(2) : '0.00';
};

// ── AI Invoice Importer (reuses AI_URL) ──────────────────────────────────────
function AIImportBtn({ label, docType, onExtracted, color='#0073ea' }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const fileRef = useRef();

  const processFile = async (file) => {
    setLoading(true); setError('');
    try {
      let fileData, fileType;
      if (file.type.match(/image.*/)) {
        fileData = await new Promise((res,rej) => { const r=new FileReader(); r.onload=e=>res(e.target.result); r.onerror=rej; r.readAsDataURL(file); });
        fileType = 'image';
      } else if (file.type==='application/pdf'||file.name.toLowerCase().endsWith('.pdf')) {
        if (!window._pdfjsLib) {
          await new Promise((res,rej)=>{ const s=document.createElement('script'); s.src='https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'; s.onload=()=>{window.pdfjsLib.GlobalWorkerOptions.workerSrc='https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';window._pdfjsLib=window.pdfjsLib;res();};s.onerror=rej;document.head.appendChild(s); });
        }
        const buf=await file.arrayBuffer(), pdf=await window._pdfjsLib.getDocument({data:buf}).promise;
        const page=await pdf.getPage(1), vp=page.getViewport({scale:1.5});
        const canvas=document.createElement('canvas'); canvas.width=vp.width; canvas.height=vp.height;
        await page.render({canvasContext:canvas.getContext('2d'),viewport:vp}).promise;
        fileData=canvas.toDataURL('image/jpeg',0.82); fileType='image';
      } else {
        fileData=await new Promise((res,rej)=>{const r=new FileReader();r.onload=e=>res(e.target.result);r.onerror=rej;r.readAsText(file);}); fileType='text';
      }
      const res = await fetch(AI_URL, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ file:fileData, fileType, docType, invoiceType:'fabric_processing' })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      onExtracted(data);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div style={{display:'inline-flex',flexDirection:'column',gap:4}}>
      <input ref={fileRef} type="file" accept="image/*,.pdf,.csv" style={{display:'none'}} onChange={e=>{const f=e.target.files[0];if(f)processFile(f);e.target.value='';}}/>
      <button onClick={()=>fileRef.current.click()} disabled={loading} style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',background:color,color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'700',fontSize:'12px',opacity:loading?0.7:1}}>
        {loading ? <><span style={{animation:'spin 1s linear infinite',display:'inline-block'}}>⏳</span> Reading…</> : <><Camera size={13}/> {label}</>}
      </button>
      {error && <span style={{fontSize:'10px',color:'#c93652'}}>⚠ {error}</span>}
    </div>
  );
}

// ── Loss Pill ─────────────────────────────────────────────────────────────────
function LossPill({ pct, label='Loss' }) {
  const p = parseFloat(pct)||0;
  const color = p > 10 ? '#c93652' : p > 5 ? '#fdab3d' : '#00a65e';
  const bg    = p > 10 ? '#fef2f2' : p > 5 ? '#fffbeb' : '#f0fdf4';
  return (
    <div style={{display:'inline-flex',flexDirection:'column',alignItems:'center',padding:'6px 12px',background:bg,border:`1px solid ${color}40`,borderRadius:'8px',minWidth:'80px'}}>
      <div style={{fontSize:'16px',fontWeight:'900',color}}>{p.toFixed(1)}%</div>
      <div style={{fontSize:'9px',color:'#676879',fontWeight:'700',textTransform:'uppercase'}}>{label}</div>
    </div>
  );
}

// ── Stage Progress Bar ────────────────────────────────────────────────────────
function StageProgress({ lot }) {
  const stages = [
    { key:'greige',           label:'Greige\nInward',     icon:'📦' },
    { key:'in_dyeing',        label:'Issued to\nDyeing',  icon:'🎨' },
    { key:'dyeing_received',  label:'Dyeing\nReceived',   icon:'✅' },
    { key:'in_printing',      label:'Issued to\nPrinting',icon:'🖨' },
    { key:'printing_received',label:'Printing\nReceived', icon:'✅' },
    { key:'in_warehouse',     label:'In\nWarehouse',      icon:'🏪' },
  ];
  const order = stages.map(s=>s.key);
  const current = calcLotStatus(lot);
  const currentIdx = order.indexOf(current);

  return (
    <div style={{display:'flex',alignItems:'center',gap:0,margin:'12px 0',overflowX:'auto',padding:'4px 0'}}>
      {stages.map((s,i) => {
        const done = i <= currentIdx;
        const active = i === currentIdx;
        return (
          <React.Fragment key={s.key}>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,flexShrink:0}}>
              <div style={{width:36,height:36,borderRadius:'50%',background:done?'#0073ea':'#e6e9ef',color:done?'white':'#9699a6',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,border:active?'3px solid #0062c4':'3px solid transparent',boxShadow:active?'0 0 0 3px #a8d1ff':'none',transition:'all 0.2s'}}>
                {s.icon}
              </div>
              <div style={{fontSize:'9px',fontWeight:active?800:600,color:active?'#0062c4':done?'#323338':'#9699a6',textAlign:'center',whiteSpace:'pre-line',lineHeight:'1.2'}}>
                {s.label}
              </div>
            </div>
            {i < stages.length-1 && (
              <div style={{height:'2px',width:'40px',flexShrink:0,background:i<currentIdx?'#0073ea':'#e6e9ef',margin:'0 0 16px 0',alignSelf:'center',transition:'background 0.3s'}}/>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Main FabricProcessingModule ───────────────────────────────────────────────
function FabricProcessingModule({ db, setDb }) {
  const isTablet = useIsTablet();
  const hydrated = useRef(false);

  // All fabric lots
  const [lots, setLots] = useState([]);
  useEffect(() => {
    if (hydrated.current) return;
    const incoming = (db?.fabricLots||[]).filter(l=>!l._deleted);
    if (incoming.length > 0 || db?.fabricLots) { hydrated.current=true; setLots(incoming); }
  }, [db]);

  const [subTab, setSubTab] = useState('summary');
  const [selectedLotId, setSelectedLotId] = useState(null);
  const [search, setSearch] = useState('');

  const selectedLot = lots.find(l=>l.id===selectedLotId);

  const saveLots = async (newLots) => {
    setLots(newLots);
    setDb(p=>({...p, fabricLots: newLots}));
    // Save last changed lot
    const changed = newLots[newLots.length-1];
    if (changed) await saveDB('fabric_lot', changed);
  };

  const updateLot = async (lotId, patch) => {
    const newLots = lots.map(l => l.id===lotId ? {...l, ...patch} : l);
    setLots(newLots);
    setDb(p=>({...p, fabricLots:newLots}));
    const updated = newLots.find(l=>l.id===lotId);
    if (updated) await saveDB('fabric_lot', updated);
  };

  const deleteLot = (lotId) => {
    if (!window.confirm('Delete this lot? This cannot be undone.')) return;
    const newLots = lots.filter(l=>l.id!==lotId);
    setLots(newLots); setDb(p=>({...p,fabricLots:newLots}));
    deleteFromDB('fabric_lot', lotId);
    if (selectedLotId===lotId) setSelectedLotId(null);
  };

  const filtered = lots.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.lot_no||'').toLowerCase().includes(q) || (l.supplier||'').toLowerCase().includes(q) || (l.style_ref||'').toLowerCase().includes(q);
  });

  // ── Summary stats ─────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const totalGreige = lots.reduce((s,l)=>s+(parseFloat(l.actual_m_qty)||parseFloat(l.invoice_m_qty)||0),0);
    const totalDyeIssued = lots.reduce((s,l)=>s+(parseFloat(l.stage1?.issued_to_dyeing_qty)||0),0);
    const totalDyeReceived = lots.reduce((s,l)=>s+(parseFloat(l.stage2?.received_from_dyeing)||0),0);
    const totalPrintReceived = lots.reduce((s,l)=>s+(parseFloat(l.stage3?.received_from_printing)||0),0);
    const totalValue = lots.reduce((s,l)=>s+(parseFloat(l.actual_m_qty||l.invoice_m_qty||0))*(parseFloat(l.rate||0)),0);
    const dyeLoss = totalDyeIssued>0 ? ((totalDyeIssued-totalDyeReceived)/totalDyeIssued*100).toFixed(1) : '0.0';
    const printLoss = totalDyeReceived>0 ? ((totalDyeReceived-totalPrintReceived)/totalDyeReceived*100).toFixed(1) : '0.0';
    const totalLoss = totalGreige>0 ? ((totalGreige-totalPrintReceived)/totalGreige*100).toFixed(1) : '0.0';
    return { totalGreige, totalDyeIssued, totalDyeReceived, totalPrintReceived, totalValue, dyeLoss, printLoss, totalLoss, lotCount:lots.length };
  }, [lots]);

  return (
    <div>
      {/* Sub-tab navigation */}
      <div style={{display:'flex',borderBottom:'1px solid #e6e9ef',background:'white',padding:'0 16px',marginBottom:'20px',borderRadius:'10px 10px 0 0',flexWrap:'wrap'}}>
        <SubTabBtn label="Overview" active={subTab==='summary'} onClick={()=>setSubTab('summary')} icon={LayoutDashboard}/>
        <SubTabBtn label="All Lots" active={subTab==='lots'} onClick={()=>setSubTab('lots')} icon={Archive}/>
        <SubTabBtn label="Greige Inward" active={subTab==='greige'} onClick={()=>{setSubTab('greige');setSelectedLotId(null);}} icon={Plus}/>
        {selectedLot && <>
          <SubTabBtn label="🎨 Dyeing" active={subTab==='dyeing'} onClick={()=>setSubTab('dyeing')} icon={RefreshCw}/>
          <SubTabBtn label="🖨 Printing" active={subTab==='printing'} onClick={()=>setSubTab('printing')} icon={Printer}/>
          <SubTabBtn label="📊 Lot P&L" active={subTab==='lot_pl'} onClick={()=>setSubTab('lot_pl')} icon={BarChart3}/>
        </>}
        <SubTabBtn label="P&L Summary" active={subTab==='pl_summary'} onClick={()=>setSubTab('pl_summary')} icon={TrendingDown}/>
      </div>

      {/* ── OVERVIEW ─────────────────────────────────────────────────────── */}
      {subTab==='summary' && (
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:'14px',marginBottom:'20px'}}>
            <StatCard label="Total Lots" value={stats.lotCount} icon={Package} color="#0073ea"/>
            <StatCard label="Greige Inward (m)" value={fmtQty(stats.totalGreige)} icon={Layers} color="#8b5cf6"/>
            <StatCard label="In Dyeing (m)" value={fmtQty(stats.totalDyeIssued)} icon={RefreshCw} color="#fdab3d"/>
            <StatCard label="Dyeing Loss" value={`${stats.dyeLoss}%`} sub="Issued vs Received" icon={TrendingDown} color={parseFloat(stats.dyeLoss)>5?'#c93652':'#00a65e'}/>
            <StatCard label="Printing Loss" value={`${stats.printLoss}%`} sub="Issued vs Received" icon={TrendingDown} color={parseFloat(stats.printLoss)>5?'#c93652':'#00a65e'}/>
            <StatCard label="Total Process Loss" value={`${stats.totalLoss}%`} sub="Greige to printed" icon={AlertTriangle} color={parseFloat(stats.totalLoss)>12?'#c93652':'#fdab3d'}/>
            <StatCard label="Fabric Value (Greige)" value={`₹${(stats.totalValue/1000).toFixed(1)}K`} icon={BarChart3} color="#059669"/>
          </div>

          {/* Stage pipeline visualization */}
          <div style={ss}>
            <h3 style={hs}><Layers size={14}/> Fabric Processing Pipeline</h3>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr>{['Stage','Qty (m)','% of Greige','Loss at Stage','Loss (m)'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
                <tbody>
                  {[
                    {stage:'Greige Inward',   qty:stats.totalGreige,         pct:'100.00', loss:'—',          lossM:'—'},
                    {stage:'Issued to Dyeing',qty:stats.totalDyeIssued,      pct:stats.totalGreige>0?((stats.totalDyeIssued/stats.totalGreige)*100).toFixed(1):'—', loss:'—', lossM:'—'},
                    {stage:'Received from Dyeing',qty:stats.totalDyeReceived,pct:stats.totalGreige>0?((stats.totalDyeReceived/stats.totalGreige)*100).toFixed(1):'—',
                      loss:stats.dyeLoss+'%', lossM:fmtQty(stats.totalDyeIssued-stats.totalDyeReceived)},
                    {stage:'Received from Printing',qty:stats.totalPrintReceived,pct:stats.totalGreige>0?((stats.totalPrintReceived/stats.totalGreige)*100).toFixed(1):'—',
                      loss:stats.printLoss+'%', lossM:fmtQty(stats.totalDyeReceived-stats.totalPrintReceived)},
                  ].map((row,i)=>(
                    <tr key={i} style={{background:i%2===0?'white':'#fafafa',borderBottom:'1px solid #f0f1f4'}}>
                      <td style={{...TD,fontWeight:'700'}}>{row.stage}</td>
                      <td style={{...TD,textAlign:'right',fontWeight:'700',color:'#0073ea'}}>{fmtQty(row.qty)}</td>
                      <td style={{...TD,textAlign:'right'}}>{row.pct}</td>
                      <td style={{...TD,textAlign:'right',color:parseFloat(row.loss)>5?'#c93652':'#323338',fontWeight:'700'}}>{row.loss}</td>
                      <td style={{...TD,textAlign:'right',color:parseFloat(row.lossM)>0?'#c93652':'#323338'}}>{row.lossM}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent lots */}
          <div style={ss}>
            <h3 style={hs}><Archive size={14}/> Recent Lots</h3>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:'12px'}}>
              {lots.slice(0,6).map(lot=>{
                const st = STAGE_LABELS[calcLotStatus(lot)]||STAGE_LABELS.greige;
                const greige = parseFloat(lot.actual_m_qty||lot.invoice_m_qty||0);
                const finalRcv = parseFloat(lot.stage3?.received_from_printing||0);
                const ttlLoss = greige>0 ? ((greige-finalRcv)/greige*100) : 0;
                return (
                  <div key={lot.id} onClick={()=>{setSelectedLotId(lot.id);setSubTab('lot_pl');}} style={{...ss,marginBottom:0,cursor:'pointer',border:`1px solid ${st.color}40`}}
                    onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.1)'}
                    onMouseLeave={e=>e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.04)'}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}>
                      <div style={{fontWeight:'800',fontSize:'14px',color:'#323338'}}>{lot.lot_no||'—'}</div>
                      <span style={{fontSize:'10px',fontWeight:'700',padding:'2px 8px',borderRadius:'20px',background:st.bg,color:st.color}}>{st.label}</span>
                    </div>
                    <div style={{fontSize:'11px',color:'#676879',marginBottom:'6px'}}>{lot.supplier} · {lot.construction}</div>
                    <div style={{display:'flex',gap:'12px',fontSize:'12px'}}>
                      <span>Greige: <strong>{fmtQty(greige)}m</strong></span>
                      {finalRcv>0 && <span>Final: <strong style={{color:'#00a65e'}}>{fmtQty(finalRcv)}m</strong></span>}
                      {greige>0 && finalRcv>0 && <span>Loss: <strong style={{color:ttlLoss>10?'#c93652':'#fdab3d'}}>{ttlLoss.toFixed(1)}%</strong></span>}
                    </div>
                  </div>
                );
              })}
              {lots.length===0 && <div style={{color:'#9699a6',fontSize:'13px',padding:'20px',textAlign:'center'}}>No lots yet. Start by adding a Greige Inward.</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── ALL LOTS TABLE ───────────────────────────────────────────────── */}
      {subTab==='lots' && (
        <div style={ss}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'14px',flexWrap:'wrap',gap:'10px'}}>
            <h3 style={{...hs,marginBottom:0}}><Archive size={14}/> All Fabric Lots ({filtered.length})</h3>
            <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
              <div style={{display:'flex',alignItems:'center',gap:'6px',border:'1px solid #e6e9ef',borderRadius:'6px',padding:'7px 10px'}}>
                <Search size={13} color="#676879"/>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search lot / supplier…" style={{border:'none',outline:'none',fontSize:'12px',width:'160px'}}/>
              </div>
              <button onClick={()=>setSubTab('greige')} style={{padding:'7px 14px',background:'#8b5cf6',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'700'}}>+ New Lot</button>
            </div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr>{['Stage','Lot No','Supplier','Construction','Composition','Greige (m)','Dye Issued','Dye Rcvd','Dye Loss%','Prnt Rcvd','Total Loss%','Value Loss (₹)','Actions'].map(h=><th key={h} style={TH}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.length===0 && <tr><td colSpan={13} style={{...TD,textAlign:'center',color:'#9699a6',padding:'40px'}}>No lots found.</td></tr>}
                {filtered.map((lot,i)=>{
                  const stage = calcLotStatus(lot);
                  const st = STAGE_LABELS[stage]||STAGE_LABELS.greige;
                  const greige = parseFloat(lot.actual_m_qty||lot.invoice_m_qty||0);
                  const dyeIss = parseFloat(lot.stage1?.issued_to_dyeing_qty||0);
                  const dyeRcv = parseFloat(lot.stage2?.received_from_dyeing||0);
                  const prnRcv = parseFloat(lot.stage3?.received_from_printing||0);
                  const dyeLoss = lossPct(dyeIss,dyeRcv);
                  const totalLoss = lossPct(greige,prnRcv||dyeRcv||dyeIss);
                  const valueLoss = (greige - (prnRcv||dyeRcv||dyeIss||greige)) * (parseFloat(lot.rate)||0);
                  return (
                    <tr key={lot.id} style={{background:i%2===0?'white':'#fafafa',cursor:'pointer'}} onClick={()=>{setSelectedLotId(lot.id);setSubTab('lot_pl');}}>
                      <td style={TD}><span style={{fontSize:'10px',fontWeight:'700',padding:'2px 7px',borderRadius:'20px',background:st.bg,color:st.color,whiteSpace:'nowrap'}}>{st.label}</span></td>
                      <td style={{...TD,fontWeight:'700',color:'#8b5cf6'}}>{lot.lot_no}</td>
                      <td style={TD}>{lot.supplier}</td>
                      <td style={TD}>{lot.construction}</td>
                      <td style={TD}>{lot.composition}</td>
                      <td style={{...TD,textAlign:'right',fontWeight:'700'}}>{fmtQty(greige)}</td>
                      <td style={{...TD,textAlign:'right'}}>{fmtQty(dyeIss)||'—'}</td>
                      <td style={{...TD,textAlign:'right',background:'#f0fdf4'}}>{dyeRcv>0?fmtQty(dyeRcv):'—'}</td>
                      <td style={{...TD,textAlign:'right',color:parseFloat(dyeLoss)>5?'#c93652':'#323338',fontWeight:'700'}}>{dyeIss>0?dyeLoss+'%':'—'}</td>
                      <td style={{...TD,textAlign:'right',background:'#fff5e6'}}>{prnRcv>0?fmtQty(prnRcv):'—'}</td>
                      <td style={{...TD,textAlign:'right',color:parseFloat(totalLoss)>10?'#c93652':'#323338',fontWeight:'800'}}>{totalLoss!=='0.00'?totalLoss+'%':'—'}</td>
                      <td style={{...TD,textAlign:'right',color:valueLoss>0?'#c93652':'#323338',fontWeight:'700'}}>{valueLoss>0?fmtVal(valueLoss):'—'}</td>
                      <td style={TD} onClick={e=>e.stopPropagation()}>
                        <div style={{display:'flex',gap:'4px'}}>
                          <button onClick={()=>{setSelectedLotId(lot.id);setSubTab('greige');}} style={{padding:'3px 7px',background:'#e8f0ff',color:'#0073ea',border:'1px solid #a8d1ff',borderRadius:'4px',cursor:'pointer',fontSize:'10px'}}>Edit</button>
                          <button onClick={()=>deleteLot(lot.id)} style={{padding:'3px 7px',background:'#fef2f2',color:'#c93652',border:'1px solid #fecaca',borderRadius:'4px',cursor:'pointer',fontSize:'10px'}}>Del</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── SECTION 1: GREIGE INWARD ─────────────────────────────────────── */}
      {subTab==='greige' && (
        <GreigeInwardSection
          lot={selectedLot} lots={lots} db={db}
          onSave={async (data) => {
            if (selectedLotId) {
              await updateLot(selectedLotId, data);
            } else {
              const newLot = { id:Date.now(), ...data, stage1:{}, stage2:{}, stage3:{} };
              const newLots = [newLot, ...lots];
              await saveLots(newLots);
              setSelectedLotId(newLot.id);
            }
            alert('Greige lot saved!');
          }}
          onSelectLot={id=>{setSelectedLotId(id);}}
        />
      )}

      {/* ── SECTION 2: DYEING ────────────────────────────────────────────── */}
      {subTab==='dyeing' && selectedLot && (
        <DyeingSection
          lot={selectedLot}
          onSave={async(stage1, stage2) => { await updateLot(selectedLot.id, {stage1, stage2}); alert('Dyeing data saved!'); }}
        />
      )}

      {/* ── SECTION 3: PRINTING ──────────────────────────────────────────── */}
      {subTab==='printing' && selectedLot && (
        <PrintingSection
          lot={selectedLot}
          onSave={async(stage3) => { await updateLot(selectedLot.id, {stage3}); alert('Printing data saved!'); }}
        />
      )}

      {/* ── LOT P&L ──────────────────────────────────────────────────────── */}
      {subTab==='lot_pl' && selectedLot && (
        <LotPLView lot={selectedLot} onNavigate={setSubTab}/>
      )}

      {/* ── OVERALL P&L SUMMARY ──────────────────────────────────────────── */}
      {subTab==='pl_summary' && (
        <PLSummaryView lots={lots} db={db}/>
      )}
    </div>
  );
}

// ── Section 1: Greige Inward Form ─────────────────────────────────────────────
function GreigeInwardSection({ lot, lots, db, onSave, onSelectLot }) {
  const isTablet = useIsTablet();
  const styleRefs = getStyleRefs(db);

  const blank = {
    lot_no:'', inward_date:'', supplier:'', style_ref:'', po_no:'',
    construction:'', composition:'', weight_gsm:'', width_inches:'',
    invoice_no:'', invoice_m_qty:'', num_bales_rolls:'', rate:'',
    sku_id:'', actual_m_qty:'', difference_qty:'0',
    inspection_status:'Pending',
    planned_shrinkage_pct:'', actual_shrinkage_pct:'',
    planned_consumption:'', expected_meterage:'', cut_qty_projection:'',
    remarks:''
  };
  const [form, setForm] = useState(lot ? { ...blank, ...lot } : blank);

  useEffect(() => { if (lot) setForm({...blank,...lot}); }, [lot?.id]);

  const hc = (field, val) => {
    const v = val?.target ? val.target.value : val;
    setForm(f => {
      const nf = {...f, [field]:v};
      // Auto calcs
      if (field==='invoice_m_qty'||field==='actual_m_qty') {
        const inv=parseFloat(field==='invoice_m_qty'?v:nf.invoice_m_qty)||0;
        const act=parseFloat(field==='actual_m_qty'?v:nf.actual_m_qty)||0;
        nf.difference_qty = (act-inv).toFixed(2);
      }
      if (field==='planned_consumption'||field==='expected_meterage') {
        const cons=parseFloat(field==='planned_consumption'?v:nf.planned_consumption)||1;
        const exp=parseFloat(field==='expected_meterage'?v:nf.expected_meterage)||0;
        nf.cut_qty_projection = cons>0 ? Math.floor(exp/cons).toString() : '0';
      }
      return nf;
    });
  };

  const genLotNo = () => {
    const dt = new Date(); const yy=dt.getFullYear().toString().slice(-2), mm=String(dt.getMonth()+1).padStart(2,'0');
    const seq = (lots.length+1).toString().padStart(4,'0');
    hc('lot_no', `GL-${yy}${mm}-${seq}`);
  };

  const genSKU = () => hc('sku_id', `FAB-GRG-${Date.now().toString().slice(-6)}`);

  // AI extraction handler
  const handleInvoiceExtracted = (data) => {
    setForm(f => ({...f,
      supplier:     data.supplier||data.supplier_name||f.supplier,
      invoice_no:   data.invoice_no||data.invoiceNo||f.invoice_no,
      invoice_m_qty:data.total_qty||data.total_meters||data.m_qty||f.invoice_m_qty,
      num_bales_rolls:data.num_bales||data.bales||data.rolls||f.num_bales_rolls,
      rate:         data.rate||data.unit_price||f.rate,
      construction: data.construction||data.fabric_type||f.construction,
      composition:  data.composition||data.content||f.composition,
      weight_gsm:   data.weight_gsm||data.gsm||f.weight_gsm,
      width_inches: data.width||data.width_inches||f.width_inches,
    }));
  };

  const handleSave = () => {
    if (!form.lot_no) return alert('Lot No is required');
    if (!form.supplier) return alert('Supplier is required');
    onSave(form);
  };

  return (
    <div>
      {/* Lot selector if existing lot is being edited */}
      <div style={{...ss,marginBottom:'14px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px',flexWrap:'wrap',gap:'10px'}}>
          <h3 style={{...hs,marginBottom:0}}><Plus size={14}/> {lot?'Edit Greige Lot':'New Greige Inward'}</h3>
          <div style={{display:'flex',gap:'10px',flexWrap:'wrap',alignItems:'center'}}>
            <select onChange={e=>{if(e.target.value)onSelectLot(parseInt(e.target.value));}} value={lot?.id||''} style={{padding:'7px 12px',border:'1px solid #e6e9ef',borderRadius:'6px',fontSize:'12px',outline:'none'}}>
              <option value="">— Select existing lot to edit —</option>
              {lots.map(l=><option key={l.id} value={l.id}>{l.lot_no} | {l.supplier}</option>)}
            </select>
            <AIImportBtn label="Import Invoice" docType="INVOICE" onExtracted={handleInvoiceExtracted} color="#8b5cf6"/>
          </div>
        </div>
        <div style={{background:'#f5f3ff',border:'1px solid #ddd6fe',borderRadius:'8px',padding:'12px',fontSize:'12px',color:'#5b21b6',marginBottom:'0'}}>
          📤 Upload supplier invoice (PDF/image) to auto-extract fabric details. Then review and fill in the remaining fields manually.
        </div>
      </div>

      {/* Fabric Identity */}
      <div style={ss}>
        <div style={{fontSize:'11px',fontWeight:'800',color:'#676879',marginBottom:'14px',textTransform:'uppercase',display:'flex',alignItems:'center',gap:'8px'}}>📦 Greige Fabric Identity</div>
        <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(${isTablet?'150px':'190px'},1fr))`,gap:'12px'}}>
          <div>
            <Fld label="Fabric Lot No" value={form.lot_no} onChange={e=>hc('lot_no',e)} required placeholder="e.g. GL-2401-0001"/>
            <button onClick={genLotNo} style={{fontSize:'11px',color:'#8b5cf6',background:'none',border:'none',cursor:'pointer',marginTop:'-2px'}}>⚡ Auto-generate</button>
          </div>
          <div>
            <Fld label="SKU ID" value={form.sku_id} onChange={e=>hc('sku_id',e)} placeholder="FAB-GRG-000001"/>
            <button onClick={genSKU} style={{fontSize:'11px',color:'#0073ea',background:'none',border:'none',cursor:'pointer',marginTop:'-2px'}}>⚡ Auto SKU</button>
          </div>
          <Fld label="Inward Date" value={form.inward_date} onChange={e=>hc('inward_date',e)} type="date"/>
          <Fld label="Supplier / Mill" value={form.supplier} onChange={e=>hc('supplier',e)} required placeholder="Supplier name"/>
          <Fld label="Invoice No" value={form.invoice_no} onChange={e=>hc('invoice_no',e)} placeholder="Invoice #"/>
          <Fld label="Construction" value={form.construction} onChange={e=>hc('construction',e)} placeholder="e.g. 40s × 40s / 133×72"/>
          <Fld label="Composition" value={form.composition} onChange={e=>hc('composition',e)} placeholder="e.g. 100% Cotton"/>
          <Fld label="Weight (GSM)" value={form.weight_gsm} onChange={e=>hc('weight_gsm',e)} type="number" placeholder="e.g. 130"/>
          <Fld label='Width (inches)' value={form.width_inches} onChange={e=>hc('width_inches',e)} type="number" placeholder='e.g. 58"'/>
          <Fld label="No. of Bales / Rolls" value={form.num_bales_rolls} onChange={e=>hc('num_bales_rolls',e)} type="number"/>
          <Fld label="Rate (₹/m)" value={form.rate} onChange={e=>hc('rate',e)} type="number"/>
        </div>
      </div>

      {/* Order Linkage */}
      <div style={ss}>
        <div style={{fontSize:'11px',fontWeight:'800',color:'#676879',marginBottom:'12px',textTransform:'uppercase'}}>🔗 Order Linkage (Style Registry)</div>
        <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(190px,1fr))`,gap:'12px'}}>
          <Fld label="Style Ref" value={form.style_ref} onChange={e=>hc('style_ref',e)} options={styleRefs}/>
          <Fld label="PO No" value={form.po_no} onChange={e=>hc('po_no',e)} options={form.style_ref?getPOsForStyle(db,form.style_ref):[]}/>
        </div>
      </div>

      {/* Quantity Planning */}
      <div style={{...ss,background:'#f5f3ff',border:'1px solid #ddd6fe'}}>
        <div style={{fontSize:'11px',fontWeight:'800',color:'#5b21b6',marginBottom:'12px',textTransform:'uppercase'}}>📊 Quantity — Invoice vs Actual (Auto-calculated)</div>
        <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill,minmax(175px,1fr))`,gap:'12px',marginBottom:'14px'}}>
          <Fld label="Invoice M Qty (m)" value={form.invoice_m_qty} onChange={e=>hc('invoice_m_qty',e)} type="number" required/>
          <Fld label="Actual M Qty (m)" value={form.actual_m_qty} onChange={e=>hc('actual_m_qty',e)} type="number" required/>
          <div>
            <Fld label="Difference Qty (m)" value={form.difference_qty} readOnly/>
            <div style={{fontSize:'10px',color:parseFloat(form.difference_qty)<0?'#c93652':parseFloat(form.difference_qty)>0?'#00a65e':'#676879',marginTop:'2px'}}>
              {parseFloat(form.difference_qty)<0?'⬇ Short received':parseFloat(form.difference_qty)>0?'⬆ Over received':'= Exact'}
            </div>
          </div>
          <Fld label="Inspection Status" value={form.inspection_status} onChange={e=>hc('inspection_status',e)} options={['Pending','Pass','Fail','Hold','Conditional Pass']}/>
          <Fld label="Planned Shrinkage %" value={form.planned_shrinkage_pct} onChange={e=>hc('planned_shrinkage_pct',e)} type="number" placeholder="e.g. 3"/>
          <Fld label="Actual Shrinkage %" value={form.actual_shrinkage_pct} onChange={e=>hc('actual_shrinkage_pct',e)} type="number"/>
          <Fld label="Planned Consumption (m/garment)" value={form.planned_consumption} onChange={e=>hc('planned_consumption',e)} type="number" placeholder="e.g. 1.65"/>
          <Fld label="Expected Meterage (after shrinkage)" value={form.expected_meterage} onChange={e=>hc('expected_meterage',e)} type="number"/>
          <div>
            <Fld label="Cut Qty Projection (pcs)" value={form.cut_qty_projection} readOnly/>
            <div style={{fontSize:'10px',color:'#676879',marginTop:'2px'}}>Expected m ÷ Consumption</div>
          </div>
        </div>
        <Fld label="Remarks" value={form.remarks} onChange={e=>hc('remarks',e)} placeholder="Any notes about this greige lot…"/>
      </div>

      <button onClick={handleSave} style={{width:'100%',padding:'14px',background:'#8b5cf6',color:'white',border:'none',borderRadius:'8px',fontWeight:'800',cursor:'pointer',fontSize:'14px'}}>
        💾 {lot?'UPDATE':'SAVE'} GREIGE LOT
      </button>
    </div>
  );
}

// ── Section 2: Dyeing (Issue + Receipt) ──────────────────────────────────────
function DyeingSection({ lot, onSave }) {
  const [s1, setS1] = useState(lot.stage1 || {});
  const [s2, setS2] = useState(lot.stage2 || {});

  const h1 = (f,v) => { const val=v?.target?v.target.value:v; setS1(p=>{
    const n={...p,[f]:val};
    if (f==='issued_to_dyeing_qty'||f==='dyeing_permitted_wastage_pct') {
      const iss=parseFloat(f==='issued_to_dyeing_qty'?val:n.issued_to_dyeing_qty)||0;
      const wp=parseFloat(f==='dyeing_permitted_wastage_pct'?val:n.dyeing_permitted_wastage_pct)||0;
      n.expected_from_dyeing = (iss*(1-wp/100)).toFixed(2);
    }
    return n;
  });};

  const h2 = (f,v) => { const val=v?.target?v.target.value:v; setS2(p=>{
    const n={...p,[f]:val};
    if (f==='received_from_dyeing') {
      const iss=parseFloat(s1.issued_to_dyeing_qty)||0;
      const rcv=parseFloat(val)||0;
      n.dye_loss_qty    = (iss-rcv).toFixed(2);
      n.dye_loss_pct    = iss>0?((iss-rcv)/iss*100).toFixed(2):'0.00';
      n.dye_actual_shrinkage_pct = iss>0?((iss-rcv)/iss*100).toFixed(2):'0.00';
    }
    if (f==='quality_loss_qty'||f==='received_from_dyeing') {
      const rcv=parseFloat(f==='received_from_dyeing'?val:n.received_from_dyeing)||0;
      const ql=parseFloat(f==='quality_loss_qty'?val:n.quality_loss_qty)||0;
      n.quality_loss_pct = rcv>0?(ql/rcv*100).toFixed(2):'0.00';
      n.usable_after_dyeing = (rcv-ql).toFixed(2);
    }
    return n;
  });};

  const greige = parseFloat(lot.actual_m_qty||lot.invoice_m_qty||0);

  return (
    <div>
      <StageProgress lot={lot}/>

      {/* Issue to Dyeing */}
      <div style={{...ss,background:'#f5f3ff',border:'1px solid #ddd6fe'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'14px',flexWrap:'wrap',gap:'10px'}}>
          <div style={{fontSize:'11px',fontWeight:'800',color:'#5b21b6',textTransform:'uppercase'}}>🎨 Issue to Dyeing</div>
          <div style={{fontSize:'12px',color:'#676879'}}>Greige Available: <strong style={{color:'#8b5cf6'}}>{fmtQty(greige)} m</strong></div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(175px,1fr))',gap:'12px'}}>
          <Fld label="Issue Challan No" value={s1.issue_challan_no} onChange={e=>h1('issue_challan_no',e)} placeholder="IC-001"/>
          <Fld label="Issue Date" value={s1.issue_date} onChange={e=>h1('issue_date',e)} type="date"/>
          <Fld label="Issued By" value={s1.issued_by} onChange={e=>h1('issued_by',e)} placeholder="Name"/>
          <Fld label="Dyer Name" value={s1.dyer_name} onChange={e=>h1('dyer_name',e)} placeholder="Dyer / vendor name"/>
          <Fld label="Dyeing Type" value={s1.dyeing_type} onChange={e=>h1('dyeing_type',e)} options={['Reactive','Vat','Disperse','Pigment','Sulphur','Acid','Natural','Digital']}/>
          <Fld label="Issue Qty (m)" value={s1.issued_to_dyeing_qty} onChange={e=>h1('issued_to_dyeing_qty',e)} type="number"/>
          <Fld label="Dyeing Rate (₹/m)" value={s1.dyeing_rate} onChange={e=>h1('dyeing_rate',e)} type="number"/>
          <Fld label="Permitted Wastage %" value={s1.dyeing_permitted_wastage_pct} onChange={e=>h1('dyeing_permitted_wastage_pct',e)} type="number" placeholder="e.g. 5"/>
          <div>
            <Fld label="Expected from Dyeing (m)" value={s1.expected_from_dyeing} readOnly/>
            <div style={{fontSize:'10px',color:'#676879',marginTop:'2px'}}>Issued × (1 − Wastage%)</div>
          </div>
          <Fld label="Expected Delivery Date" value={s1.expected_delivery_date} onChange={e=>h1('expected_delivery_date',e)} type="date"/>
          <Fld label="Remarks" value={s1.issue_remarks} onChange={e=>h1('issue_remarks',e)} placeholder="Special instructions…"/>
        </div>
      </div>

      {/* Receive from Dyeing */}
      <div style={{...ss,background:'#e8f0ff',border:'1px solid #a8d1ff'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'14px',flexWrap:'wrap',gap:'10px'}}>
          <div style={{fontSize:'11px',fontWeight:'800',color:'#0062c4',textTransform:'uppercase'}}>📥 Receive from Dyeing</div>
          <AIImportBtn label="Import Dyeing Challan" docType="CHALLAN" onExtracted={data=>{
            h2('received_from_dyeing', data.received_qty||data.quantity||'');
            h2('dye_receive_challan_no', data.challan_no||data.invoice_no||'');
            h2('dye_receive_date', data.date||'');
          }} color="#0073ea"/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(175px,1fr))',gap:'12px',marginBottom:'14px'}}>
          <Fld label="Receive Challan No" value={s2.dye_receive_challan_no} onChange={e=>h2('dye_receive_challan_no',e)}/>
          <Fld label="Receive Date" value={s2.dye_receive_date} onChange={e=>h2('dye_receive_date',e)} type="date"/>
          <Fld label="Received Qty (m)" value={s2.received_from_dyeing} onChange={e=>h2('received_from_dyeing',e)} type="number" required/>
          <div>
            <Fld label="Loss Qty (m)" value={s2.dye_loss_qty} readOnly/>
            <div style={{fontSize:'10px',color:parseFloat(s2.dye_loss_qty)>0?'#c93652':'#00a65e',marginTop:'2px'}}>
              {s2.dye_loss_qty} = Issued − Received
            </div>
          </div>
          <div>
            <Fld label="Actual Loss %" value={s2.dye_loss_pct} readOnly/>
            <Fld label="Planned Loss %" value={s1.dyeing_permitted_wastage_pct} readOnly style={{marginTop:'4px'}}/>
          </div>
          <Fld label="Actual Shrinkage %" value={s2.dye_actual_shrinkage_pct} readOnly/>
          <Fld label="Inspection Result" value={s2.dye_inspection_result} onChange={e=>h2('dye_inspection_result',e)} options={['Pass','Fail','Hold','Conditional Pass']}/>
          <Fld label="Quality Loss Qty (m)" value={s2.quality_loss_qty} onChange={e=>h2('quality_loss_qty',e)} type="number"/>
          <div>
            <Fld label="Quality Loss %" value={s2.quality_loss_pct} readOnly/>
            <Fld label="Usable Qty (m)" value={s2.usable_after_dyeing} readOnly style={{marginTop:'4px'}}/>
          </div>
          <Fld label="Remarks" value={s2.dye_receive_remarks} onChange={e=>h2('dye_receive_remarks',e)} placeholder="QC notes, shade comments…"/>
        </div>

        {/* Loss comparison pills */}
        {s2.received_from_dyeing && (
          <div style={{display:'flex',gap:'12px',flexWrap:'wrap',padding:'12px',background:'white',borderRadius:'8px',border:'1px solid #a8d1ff'}}>
            <LossPill pct={s1.dyeing_permitted_wastage_pct} label="Planned Loss"/>
            <LossPill pct={s2.dye_loss_pct} label="Actual Loss"/>
            <LossPill pct={s2.quality_loss_pct} label="Quality Loss"/>
            <div style={{display:'inline-flex',flexDirection:'column',alignItems:'center',padding:'6px 12px',background:'#f0fdf4',border:'1px solid #b3f5d6',borderRadius:'8px',minWidth:'80px'}}>
              <div style={{fontSize:'16px',fontWeight:'900',color:'#00a65e'}}>{fmtQty(s2.usable_after_dyeing)}m</div>
              <div style={{fontSize:'9px',color:'#676879',fontWeight:'700',textTransform:'uppercase'}}>Usable Qty</div>
            </div>
          </div>
        )}
      </div>

      {/* Issue to Printing */}
      <div style={{...ss,background:'#fffbeb',border:'1px solid #fde68a'}}>
        <div style={{fontSize:'11px',fontWeight:'800',color:'#92400e',marginBottom:'14px',textTransform:'uppercase'}}>🖨 Issue to Printing</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(175px,1fr))',gap:'12px'}}>
          <Fld label="Printing Supplier" value={s2.printing_supplier} onChange={e=>h2('printing_supplier',e)} placeholder="Printer name"/>
          <Fld label="Print Issue Challan No" value={s2.print_issue_challan_no} onChange={e=>h2('print_issue_challan_no',e)}/>
          <Fld label="Issue Date (Printing)" value={s2.print_issue_date} onChange={e=>h2('print_issue_date',e)} type="date"/>
          <Fld label="Issued By" value={s2.print_issued_by} onChange={e=>h2('print_issued_by',e)} placeholder="Name"/>
          <Fld label="Issue Qty to Printing (m)" value={s2.issued_to_printing_qty} onChange={e=>{
            const val=e.target.value;
            setS2(p=>{const n={...p,issued_to_printing_qty:val};
              const iss=parseFloat(val)||0;
              const wp=parseFloat(n.print_permitted_wastage_pct)||0;
              n.expected_from_printing=(iss*(1-wp/100)).toFixed(2);
              return n;
            });
          }} type="number"/>
          <Fld label="Permitted Wastage % (Printing)" value={s2.print_permitted_wastage_pct} onChange={e=>h2('print_permitted_wastage_pct',e)} type="number"/>
          <div>
            <Fld label="Expected from Printing (m)" value={s2.expected_from_printing} readOnly/>
            <div style={{fontSize:'10px',color:'#676879',marginTop:'2px'}}>Issued × (1 − Wastage%)</div>
          </div>
          <Fld label="Expected Delivery Date (Printing)" value={s2.print_expected_delivery} onChange={e=>h2('print_expected_delivery',e)} type="date"/>
        </div>
      </div>

      <button onClick={()=>onSave(s1,s2)} style={{width:'100%',padding:'14px',background:'#a25ddc',color:'white',border:'none',borderRadius:'8px',fontWeight:'800',cursor:'pointer',fontSize:'14px'}}>
        💾 SAVE DYEING DATA
      </button>
    </div>
  );
}

// ── Section 3: Printing (Receipt + Warehouse) ─────────────────────────────────
function PrintingSection({ lot, onSave }) {
  const [s3, setS3] = useState(lot.stage3||{});

  const h3 = (f,v) => { const val=v?.target?v.target.value:v; setS3(p=>{
    const n={...p,[f]:val};
    const issuedToPrint = parseFloat(lot.stage2?.issued_to_printing_qty)||0;
    const rcv=parseFloat(f==='received_from_printing'?val:n.received_from_printing)||0;
    if (f==='received_from_printing') {
      n.print_loss_qty = (issuedToPrint-rcv).toFixed(2);
      n.print_loss_pct = issuedToPrint>0?((issuedToPrint-rcv)/issuedToPrint*100).toFixed(2):'0.00';
      n.print_actual_shrinkage_pct = issuedToPrint>0?((issuedToPrint-rcv)/issuedToPrint*100).toFixed(2):'0.00';
      // Total loss from greige
      const greige=parseFloat(lot.actual_m_qty||lot.invoice_m_qty||0);
      if (greige>0) n.total_loss_greige_to_printing_pct = ((greige-rcv)/greige*100).toFixed(2);
    }
    if (f==='print_quality_loss_qty'||f==='received_from_printing') {
      const ql=parseFloat(f==='print_quality_loss_qty'?val:n.print_quality_loss_qty)||0;
      n.print_quality_loss_pct=rcv>0?(ql/rcv*100).toFixed(2):'0.00';
      n.usable_after_printing=(rcv-ql).toFixed(2);
    }
    return n;
  });};

  const greige = parseFloat(lot.actual_m_qty||lot.invoice_m_qty||0);
  const issuedToPrint = parseFloat(lot.stage2?.issued_to_printing_qty)||0;
  const rcvFromPrint = parseFloat(s3.received_from_printing)||0;
  const rate = parseFloat(lot.rate)||0;
  const consumption = parseFloat(lot.planned_consumption)||1;
  const totalLossQty = greige - rcvFromPrint;
  const totalLossPct = greige>0 ? (totalLossQty/greige*100) : 0;
  const valueLoss    = totalLossQty * rate;
  const garmentLoss  = consumption>0 ? Math.floor(totalLossQty/consumption) : 0;

  return (
    <div>
      <StageProgress lot={lot}/>

      {/* Receive from Printing */}
      <div style={{...ss,background:'#fdf2f8',border:'1px solid #f9a8d4'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'14px',flexWrap:'wrap',gap:'10px'}}>
          <div style={{fontSize:'11px',fontWeight:'800',color:'#9d174d',textTransform:'uppercase'}}>🖨 Receive from Printing</div>
          <div style={{fontSize:'12px',color:'#676879'}}>Issued to Printing: <strong style={{color:'#db2777'}}>{fmtQty(issuedToPrint)} m</strong></div>
          <AIImportBtn label="Import Printing Challan" docType="CHALLAN" onExtracted={data=>{
            h3('received_from_printing', data.received_qty||data.quantity||'');
            h3('print_receive_challan_no', data.challan_no||data.invoice_no||'');
            h3('print_receive_date', data.date||'');
          }} color="#db2777"/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(175px,1fr))',gap:'12px',marginBottom:'14px'}}>
          <Fld label="Receive Challan No" value={s3.print_receive_challan_no} onChange={e=>h3('print_receive_challan_no',e)}/>
          <Fld label="Receive Date" value={s3.print_receive_date} onChange={e=>h3('print_receive_date',e)} type="date"/>
          <Fld label="Received Qty (m)" value={s3.received_from_printing} onChange={e=>h3('received_from_printing',e)} type="number" required/>
          <div>
            <Fld label="Loss Qty (m)" value={s3.print_loss_qty} readOnly/>
            <div style={{fontSize:'10px',color:parseFloat(s3.print_loss_qty)>0?'#c93652':'#00a65e',marginTop:'2px'}}>Issued − Received</div>
          </div>
          <div>
            <Fld label="Actual Loss %" value={s3.print_loss_pct} readOnly/>
            <Fld label="Planned Loss %" value={lot.stage2?.print_permitted_wastage_pct} readOnly style={{marginTop:'4px'}}/>
          </div>
          <Fld label="Actual Shrinkage %" value={s3.print_actual_shrinkage_pct} readOnly/>
          <Fld label="Inspection Result" value={s3.print_inspection_result} onChange={e=>h3('print_inspection_result',e)} options={['Pass','Fail','Hold','Conditional Pass']}/>
          <Fld label="Quality Loss Qty (m)" value={s3.print_quality_loss_qty} onChange={e=>h3('print_quality_loss_qty',e)} type="number"/>
          <div>
            <Fld label="Quality Loss %" value={s3.print_quality_loss_pct} readOnly/>
            <Fld label="Usable Qty (m)" value={s3.usable_after_printing} readOnly style={{marginTop:'4px'}}/>
          </div>
          <div>
            <Fld label="Total Loss % (Greige→Printing)" value={s3.total_loss_greige_to_printing_pct} readOnly/>
            <div style={{fontSize:'10px',color:'#676879',marginTop:'2px'}}>Greige: {fmtQty(greige)}m → Printed: {fmtQty(rcvFromPrint)}m</div>
          </div>
        </div>

        {/* Loss pills */}
        {s3.received_from_printing && (
          <div style={{display:'flex',gap:'12px',flexWrap:'wrap',padding:'12px',background:'white',borderRadius:'8px',border:'1px solid #f9a8d4'}}>
            <LossPill pct={lot.stage2?.print_permitted_wastage_pct} label="Planned Loss"/>
            <LossPill pct={s3.print_loss_pct} label="Actual Loss"/>
            <LossPill pct={s3.print_quality_loss_pct} label="Quality Loss"/>
            <LossPill pct={s3.total_loss_greige_to_printing_pct} label="Greige→Print"/>
            <div style={{display:'inline-flex',flexDirection:'column',alignItems:'center',padding:'6px 12px',background:'#f0fdf4',border:'1px solid #b3f5d6',borderRadius:'8px',minWidth:'80px'}}>
              <div style={{fontSize:'16px',fontWeight:'900',color:'#00a65e'}}>{fmtQty(s3.usable_after_printing)}m</div>
              <div style={{fontSize:'9px',color:'#676879',fontWeight:'700',textTransform:'uppercase'}}>Usable Qty</div>
            </div>
          </div>
        )}
      </div>

      {/* Issue to Warehouse */}
      <div style={{...ss,background:'#f0fdf4',border:'1px solid #b3f5d6'}}>
        <div style={{fontSize:'11px',fontWeight:'800',color:'#15803d',marginBottom:'14px',textTransform:'uppercase'}}>🏪 Issue to Warehouse</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(175px,1fr))',gap:'12px'}}>
          <Fld label="Warehouse Challan No" value={s3.warehouse_challan_no} onChange={e=>h3('warehouse_challan_no',e)}/>
          <Fld label="Warehouse Inward Date" value={s3.warehouse_date} onChange={e=>h3('warehouse_date',e)} type="date"/>
          <Fld label="Issued to Warehouse (m)" value={s3.issued_to_warehouse_qty} onChange={e=>h3('issued_to_warehouse_qty',e)} type="number"/>
          <Fld label="Issued By" value={s3.warehouse_issued_by} onChange={e=>h3('warehouse_issued_by',e)} placeholder="Name"/>
          <Fld label="Warehouse Rack / Location" value={s3.warehouse_location} onChange={e=>h3('warehouse_location',e)} placeholder="R1 / S-3"/>
          <Fld label="Remarks" value={s3.warehouse_remarks} onChange={e=>h3('warehouse_remarks',e)} placeholder="Any notes…"/>
        </div>
      </div>

      {/* Total loss summary */}
      {rcvFromPrint > 0 && (
        <div style={{...ss,background:'#323338',color:'white',border:'none'}}>
          <h3 style={{...hs,color:'white',borderBottomColor:'rgba(255,255,255,0.1)'}}>📊 Total Loss Summary — Greige to Printing</h3>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:'14px'}}>
            {[
              {l:'Greige Inward',   v:fmtQty(greige)+'m',     color:'#9699a6'},
              {l:'Final Usable',    v:fmtQty(s3.usable_after_printing||rcvFromPrint)+'m', color:'#4ade80'},
              {l:'Total Loss (m)',  v:fmtQty(totalLossQty)+'m', color:'#eb6175'},
              {l:'Total Loss %',    v:totalLossPct.toFixed(2)+'%', color:totalLossPct>12?'#eb6175':'#fb923c'},
              {l:'Value Loss',      v:fmtVal(valueLoss),       color:'#eb6175'},
              {l:'Garment Loss',    v:garmentLoss+' pcs',      color:'#fb923c'},
            ].map(({l,v,color})=>(
              <div key={l} style={{background:'rgba(255,255,255,0.06)',borderRadius:'8px',padding:'12px',border:'1px solid rgba(255,255,255,0.1)'}}>
                <div style={{fontSize:'10px',color:'#9699a6',fontWeight:'700',textTransform:'uppercase',marginBottom:'4px'}}>{l}</div>
                <div style={{fontSize:'20px',fontWeight:'900',color}}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:'14px',fontSize:'12px',color:'#9699a6'}}>
            💡 Garment loss based on planned consumption of {fmtQty(consumption)} m/pc. Value loss at greige rate ₹{rate}/m.
          </div>
        </div>
      )}

      <button onClick={()=>onSave(s3)} style={{width:'100%',padding:'14px',background:'#db2777',color:'white',border:'none',borderRadius:'8px',fontWeight:'800',cursor:'pointer',fontSize:'14px'}}>
        💾 SAVE PRINTING DATA
      </button>
    </div>
  );
}

// ── Lot P&L View ──────────────────────────────────────────────────────────────
function LotPLView({ lot, onNavigate }) {
  const greige   = parseFloat(lot.actual_m_qty||lot.invoice_m_qty||0);
  const rate     = parseFloat(lot.rate)||0;
  const cons     = parseFloat(lot.planned_consumption)||1;
  const dyeIss   = parseFloat(lot.stage1?.issued_to_dyeing_qty)||0;
  const dyeRcv   = parseFloat(lot.stage2?.received_from_dyeing)||0;
  const prnIss   = parseFloat(lot.stage2?.issued_to_printing_qty)||0;
  const prnRcv   = parseFloat(lot.stage3?.received_from_printing)||0;
  const usable   = parseFloat(lot.stage3?.usable_after_printing||lot.stage3?.received_from_printing||lot.stage2?.usable_after_dyeing||dyeRcv||dyeIss||greige);

  const dyeLoss  = dyeIss  > 0 ? ((dyeIss -dyeRcv)/dyeIss*100)  : 0;
  const prnLoss  = prnIss  > 0 ? ((prnIss -prnRcv)/prnIss*100)  : 0;
  const totLoss  = greige  > 0 ? ((greige -usable)/greige*100)   : 0;
  const valLoss  = (greige - usable) * rate;
  const grmLoss  = cons > 0 ? Math.floor((greige - usable)/cons) : 0;
  const dyeVal   = dyeIss * (parseFloat(lot.stage1?.dyeing_rate)||0);

  const stages = [
    { label:'Greige Inward',        qty:greige,  pct:100,                      lossQty:0,              lossPct:0,       color:'#8b5cf6' },
    { label:'Issued to Dyeing',     qty:dyeIss,  pct:greige>0?(dyeIss/greige*100):0, lossQty:0,        lossPct:0,       color:'#a25ddc' },
    { label:'Received from Dyeing', qty:dyeRcv,  pct:greige>0?(dyeRcv/greige*100):0, lossQty:dyeIss-dyeRcv,  lossPct:dyeLoss, color:'#0073ea' },
    { label:'Issued to Printing',   qty:prnIss,  pct:greige>0?(prnIss/greige*100):0, lossQty:0,        lossPct:0,       color:'#0891b2' },
    { label:'Received from Printing',qty:prnRcv, pct:greige>0?(prnRcv/greige*100):0, lossQty:prnIss-prnRcv,  lossPct:prnLoss, color:'#db2777' },
    { label:'Usable (After QC)',    qty:usable,  pct:greige>0?(usable/greige*100):0,  lossQty:0,        lossPct:0,       color:'#00a65e' },
  ];

  return (
    <div>
      <StageProgress lot={lot}/>

      {/* Lot header */}
      <div style={{...ss,background:'linear-gradient(135deg,#323338,#1f1f1f)',color:'white',border:'none'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:'10px'}}>
          <div>
            <div style={{fontSize:'22px',fontWeight:'900',marginBottom:'4px'}}>{lot.lot_no}</div>
            <div style={{color:'#9699a6',fontSize:'13px'}}>{lot.supplier} · {lot.construction} · {lot.composition}</div>
            <div style={{color:'#676879',fontSize:'12px',marginTop:'2px'}}>{lot.style_ref} / {lot.po_no}</div>
          </div>
          <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
            <button onClick={()=>onNavigate('greige')} style={{padding:'7px 14px',background:'rgba(255,255,255,0.1)',color:'white',border:'1px solid rgba(255,255,255,0.2)',borderRadius:'6px',cursor:'pointer',fontSize:'12px'}}>✏ Edit Greige</button>
            <button onClick={()=>onNavigate('dyeing')} style={{padding:'7px 14px',background:'rgba(255,255,255,0.1)',color:'white',border:'1px solid rgba(255,255,255,0.2)',borderRadius:'6px',cursor:'pointer',fontSize:'12px'}}>🎨 Dyeing</button>
            <button onClick={()=>onNavigate('printing')} style={{padding:'7px 14px',background:'rgba(255,255,255,0.1)',color:'white',border:'1px solid rgba(255,255,255,0.2)',borderRadius:'6px',cursor:'pointer',fontSize:'12px'}}>🖨 Printing</button>
          </div>
        </div>
      </div>

      {/* Stage flow table */}
      <div style={ss}>
        <h3 style={hs}><BarChart3 size={14}/> Stage-by-Stage Flow</h3>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Stage','Qty (m)','% of Greige','Loss at Stage (m)','Loss %','Bar'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {stages.filter(s=>s.qty>0).map((s,i)=>(
                <tr key={i} style={{background:i%2===0?'white':'#fafafa',borderBottom:'1px solid #f0f1f4'}}>
                  <td style={{...TD,fontWeight:'700'}}><span style={{width:'10px',height:'10px',borderRadius:'50%',background:s.color,display:'inline-block',marginRight:'6px',verticalAlign:'middle'}}></span>{s.label}</td>
                  <td style={{...TD,textAlign:'right',fontWeight:'700',color:s.color}}>{fmtQty(s.qty)}</td>
                  <td style={{...TD,textAlign:'right'}}>{s.pct.toFixed(1)}%</td>
                  <td style={{...TD,textAlign:'right',color:s.lossQty>0?'#c93652':'#9699a6'}}>{s.lossQty>0?fmtQty(s.lossQty):'—'}</td>
                  <td style={{...TD,textAlign:'right',fontWeight:'700',color:s.lossPct>5?'#c93652':s.lossPct>0?'#fdab3d':'#9699a6'}}>{s.lossPct>0?s.lossPct.toFixed(2)+'%':'—'}</td>
                  <td style={{...TD,minWidth:'120px'}}>
                    <div style={{background:'#f0f1f4',borderRadius:'4px',height:'8px',overflow:'hidden'}}>
                      <div style={{width:`${Math.min(s.pct,100)}%`,height:'100%',background:s.color,borderRadius:'4px'}}/>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* P&L cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:'14px',marginBottom:'20px'}}>
        <StatCard label="Greige (m)" value={fmtQty(greige)} icon={Layers} color="#8b5cf6"/>
        <StatCard label="Usable Final (m)" value={fmtQty(usable)} icon={CheckCircle} color="#00a65e"/>
        <StatCard label="Total Loss (m)" value={fmtQty(greige-usable)} icon={TrendingDown} color="#c93652"/>
        <StatCard label="Total Loss %" value={`${totLoss.toFixed(1)}%`} sub={`Dyeing: ${dyeLoss.toFixed(1)}% + Printing: ${prnLoss.toFixed(1)}%`} icon={AlertTriangle} color={totLoss>12?'#c93652':'#fdab3d'}/>
        <StatCard label="Value Loss (₹)" value={`₹${(valLoss/1000).toFixed(1)}K`} sub={`@ ₹${rate}/m`} icon={BarChart3} color="#c93652"/>
        <StatCard label="Garment Loss (pcs)" value={grmLoss} sub={`@ ${fmtQty(cons)}m/pc`} icon={Package} color="#fdab3d"/>
        <StatCard label="Dyeing Cost (₹)" value={`₹${(dyeVal/1000).toFixed(1)}K`} sub={`${fmtQty(dyeIss)}m × ₹${lot.stage1?.dyeing_rate||0}`} icon={RefreshCw} color="#a25ddc"/>
      </div>

      {/* Value breakdown */}
      <div style={ss}>
        <h3 style={hs}><BarChart3 size={14}/> Value at Each Stage</h3>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(175px,1fr))',gap:'12px'}}>
          {[
            {l:'Greige Value',       v:greige*rate,        note:`${fmtQty(greige)}m × ₹${rate}`},
            {l:'Dyeing Cost',        v:dyeIss*(parseFloat(lot.stage1?.dyeing_rate)||0), note:`${fmtQty(dyeIss)}m × ₹${lot.stage1?.dyeing_rate||0}`},
            {l:'Dyeing Loss Value',  v:(dyeIss-dyeRcv)*rate, note:`${fmtQty(dyeIss-dyeRcv)}m × ₹${rate}`},
            {l:'Print Loss Value',   v:(prnIss-prnRcv)*rate, note:`${fmtQty(prnIss-prnRcv)}m × ₹${rate}`},
            {l:'Total Loss Value',   v:valLoss, note:'Greige rate × total loss qty'},
          ].map(({l,v,note})=>(
            <div key={l} style={{background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'8px',padding:'12px'}}>
              <div style={{fontSize:'10px',color:'#676879',fontWeight:'700',textTransform:'uppercase',marginBottom:'4px'}}>{l}</div>
              <div style={{fontSize:'18px',fontWeight:'900',color:l.includes('Loss')?'#c93652':'#323338'}}>{fmtVal(v)}</div>
              <div style={{fontSize:'10px',color:'#9699a6',marginTop:'2px'}}>{note}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Overall P&L Summary ───────────────────────────────────────────────────────
function PLSummaryView({ lots, db }) {
  const styleRefs = getStyleRefs(db);
  const [filterStyle, setFilterStyle] = useState('');

  const filtered = filterStyle ? lots.filter(l=>l.style_ref===filterStyle) : lots;

  const agg = useMemo(()=>{
    return filtered.reduce((acc, lot) => {
      const greige = parseFloat(lot.actual_m_qty||lot.invoice_m_qty||0);
      const rate   = parseFloat(lot.rate)||0;
      const cons   = parseFloat(lot.planned_consumption)||1;
      const dyeIss = parseFloat(lot.stage1?.issued_to_dyeing_qty)||0;
      const dyeRcv = parseFloat(lot.stage2?.received_from_dyeing)||0;
      const prnIss = parseFloat(lot.stage2?.issued_to_printing_qty)||0;
      const prnRcv = parseFloat(lot.stage3?.received_from_printing)||0;
      const usable = parseFloat(lot.stage3?.usable_after_printing||prnRcv||dyeRcv||dyeIss||greige);
      const lossQty = greige - usable;
      acc.totalGreige   += greige;
      acc.totalDyeIss   += dyeIss;
      acc.totalDyeRcv   += dyeRcv;
      acc.totalPrnIss   += prnIss;
      acc.totalPrnRcv   += prnRcv;
      acc.totalUsable   += usable;
      acc.totalLossQty  += lossQty;
      acc.totalValue    += greige*rate;
      acc.totalValueLoss+= lossQty*rate;
      acc.totalGrmLoss  += cons>0?Math.floor(lossQty/cons):0;
      acc.dyeCost       += dyeIss*(parseFloat(lot.stage1?.dyeing_rate)||0);
      return acc;
    }, {totalGreige:0,totalDyeIss:0,totalDyeRcv:0,totalPrnIss:0,totalPrnRcv:0,totalUsable:0,totalLossQty:0,totalValue:0,totalValueLoss:0,totalGrmLoss:0,dyeCost:0});
  }, [filtered]);

  const dyeLossPct  = agg.totalDyeIss>0  ? ((agg.totalDyeIss-agg.totalDyeRcv)/agg.totalDyeIss*100)   : 0;
  const prnLossPct  = agg.totalPrnIss>0  ? ((agg.totalPrnIss-agg.totalPrnRcv)/agg.totalPrnIss*100)    : 0;
  const totalLossPct= agg.totalGreige>0  ? (agg.totalLossQty/agg.totalGreige*100) : 0;

  return (
    <div>
      {/* Filters */}
      <div style={{...ss,padding:'14px'}}>
        <div style={{display:'flex',gap:'12px',flexWrap:'wrap',alignItems:'center'}}>
          <span style={{fontSize:'12px',fontWeight:'700',color:'#676879'}}>Filter by:</span>
          <select value={filterStyle} onChange={e=>setFilterStyle(e.target.value)} style={{padding:'7px 12px',border:'1px solid #e6e9ef',borderRadius:'6px',fontSize:'12px',outline:'none'}}>
            <option value="">All Styles</option>
            {styleRefs.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{fontSize:'12px',color:'#676879'}}>{filtered.length} lots</span>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:'14px',marginBottom:'20px'}}>
        <StatCard label="Total Lots" value={filtered.length} icon={Package} color="#0073ea"/>
        <StatCard label="Greige Inward (m)" value={fmtQty(agg.totalGreige)} icon={Layers} color="#8b5cf6"/>
        <StatCard label="Dyeing Loss %" value={`${dyeLossPct.toFixed(1)}%`} sub={`${fmtQty(agg.totalDyeIss-agg.totalDyeRcv)}m lost`} icon={TrendingDown} color={dyeLossPct>5?'#c93652':'#00a65e'}/>
        <StatCard label="Printing Loss %" value={`${prnLossPct.toFixed(1)}%`} sub={`${fmtQty(agg.totalPrnIss-agg.totalPrnRcv)}m lost`} icon={TrendingDown} color={prnLossPct>5?'#c93652':'#00a65e'}/>
        <StatCard label="Total Process Loss" value={`${totalLossPct.toFixed(1)}%`} sub={`${fmtQty(agg.totalLossQty)}m`} icon={AlertTriangle} color={totalLossPct>12?'#c93652':'#fdab3d'}/>
        <StatCard label="Value Loss (₹)" value={`₹${(agg.totalValueLoss/1000).toFixed(1)}K`} icon={BarChart3} color="#c93652"/>
        <StatCard label="Garment Loss (pcs)" value={agg.totalGrmLoss.toLocaleString()} icon={Package} color="#fdab3d"/>
        <StatCard label="Final Usable (m)" value={fmtQty(agg.totalUsable)} icon={CheckCircle} color="#00a65e"/>
      </div>

      {/* Stage pipeline */}
      <div style={ss}>
        <h3 style={hs}><BarChart3 size={14}/> Processing Pipeline — All Lots</h3>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Stage','Total Qty (m)','% of Greige','Loss at Stage (m)','Loss %','Value Loss (₹)'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {[
                {s:'Greige Inward',         q:agg.totalGreige,  pct:100,                             lq:0,  lp:0},
                {s:'Issued to Dyeing',       q:agg.totalDyeIss, pct:agg.totalGreige>0?(agg.totalDyeIss/agg.totalGreige*100):0, lq:0, lp:0},
                {s:'Received from Dyeing',   q:agg.totalDyeRcv, pct:agg.totalGreige>0?(agg.totalDyeRcv/agg.totalGreige*100):0, lq:agg.totalDyeIss-agg.totalDyeRcv, lp:dyeLossPct},
                {s:'Issued to Printing',     q:agg.totalPrnIss, pct:agg.totalGreige>0?(agg.totalPrnIss/agg.totalGreige*100):0, lq:0, lp:0},
                {s:'Received from Printing', q:agg.totalPrnRcv, pct:agg.totalGreige>0?(agg.totalPrnRcv/agg.totalGreige*100):0, lq:agg.totalPrnIss-agg.totalPrnRcv, lp:prnLossPct},
                {s:'Final Usable',           q:agg.totalUsable, pct:agg.totalGreige>0?(agg.totalUsable/agg.totalGreige*100):0, lq:agg.totalLossQty, lp:totalLossPct},
              ].map((r,i)=>(
                <tr key={i} style={{background:i%2===0?'white':'#fafafa',borderBottom:'1px solid #f0f1f4'}}>
                  <td style={{...TD,fontWeight:'700'}}>{r.s}</td>
                  <td style={{...TD,textAlign:'right',fontWeight:'700',color:'#0073ea'}}>{fmtQty(r.q)}</td>
                  <td style={{...TD,textAlign:'right'}}>
                    <div style={{display:'flex',alignItems:'center',gap:'8px',justifyContent:'flex-end'}}>
                      <div style={{background:'#f0f1f4',borderRadius:'4px',height:'8px',width:'80px',overflow:'hidden'}}>
                        <div style={{width:`${Math.min(r.pct,100)}%`,height:'100%',background:'#0073ea',borderRadius:'4px'}}/>
                      </div>
                      {r.pct.toFixed(1)}%
                    </div>
                  </td>
                  <td style={{...TD,textAlign:'right',color:r.lq>0?'#c93652':'#9699a6'}}>{r.lq>0?fmtQty(r.lq):'—'}</td>
                  <td style={{...TD,textAlign:'right',fontWeight:'700',color:r.lp>5?'#c93652':r.lp>0?'#fdab3d':'#9699a6'}}>{r.lp>0?r.lp.toFixed(2)+'%':'—'}</td>
                  <td style={{...TD,textAlign:'right',color:r.lq>0?'#c93652':'#9699a6'}}>{r.lq>0?fmtVal(r.lq*(parseFloat(filtered[0]?.rate)||0)):'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lot-by-lot P&L table */}
      <div style={ss}>
        <h3 style={hs}><FileText size={14}/> Lot-by-Lot P&L</h3>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Lot No','Supplier','Style','Greige (m)','Dye Loss%','Prn Loss%','Total Loss%','Loss (m)','Value Loss (₹)','Garment Loss'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {filtered.map((lot,i)=>{
                const g=parseFloat(lot.actual_m_qty||lot.invoice_m_qty||0);
                const dIss=parseFloat(lot.stage1?.issued_to_dyeing_qty)||0;
                const dRcv=parseFloat(lot.stage2?.received_from_dyeing)||0;
                const pIss=parseFloat(lot.stage2?.issued_to_printing_qty)||0;
                const pRcv=parseFloat(lot.stage3?.received_from_printing)||0;
                const usb=parseFloat(lot.stage3?.usable_after_printing||pRcv||dRcv||dIss||g);
                const rate=parseFloat(lot.rate)||0;
                const cons=parseFloat(lot.planned_consumption)||1;
                const dl=dIss>0?((dIss-dRcv)/dIss*100):0;
                const pl=pIss>0?((pIss-pRcv)/pIss*100):0;
                const tl=g>0?((g-usb)/g*100):0;
                const vl=(g-usb)*rate;
                const gl=cons>0?Math.floor((g-usb)/cons):0;
                return (
                  <tr key={lot.id} style={{background:i%2===0?'white':'#fafafa',borderBottom:'1px solid #f0f1f4'}}>
                    <td style={{...TD,fontWeight:'700',color:'#8b5cf6'}}>{lot.lot_no}</td>
                    <td style={TD}>{lot.supplier}</td>
                    <td style={TD}>{lot.style_ref||'—'}</td>
                    <td style={{...TD,textAlign:'right',fontWeight:'700'}}>{fmtQty(g)}</td>
                    <td style={{...TD,textAlign:'right',color:dl>5?'#c93652':'#323338'}}>{dIss>0?dl.toFixed(2)+'%':'—'}</td>
                    <td style={{...TD,textAlign:'right',color:pl>5?'#c93652':'#323338'}}>{pIss>0?pl.toFixed(2)+'%':'—'}</td>
                    <td style={{...TD,textAlign:'right',fontWeight:'800',color:tl>10?'#c93652':'#fdab3d'}}>{g>0&&usb<g?tl.toFixed(2)+'%':'—'}</td>
                    <td style={{...TD,textAlign:'right',color:'#c93652'}}>{g>usb?fmtQty(g-usb):'—'}</td>
                    <td style={{...TD,textAlign:'right',color:vl>0?'#c93652':'#323338',fontWeight:'700'}}>{vl>0?fmtVal(vl):'—'}</td>
                    <td style={{...TD,textAlign:'right',color:'#fdab3d'}}>{gl>0?gl+' pcs':'—'}</td>
                  </tr>
                );
              })}
              {!filtered.length&&<tr><td colSpan={10} style={{...TD,textAlign:'center',color:'#9699a6',padding:'30px'}}>No lots found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// PART 10 — MAIN COMPONENT
// ============================================================================


// ============================================================================
// PART 12 — CUT PLAN WORKFLOW: LOCK → ISSUE → RECEIVE → END BIT
// ============================================================================

// ── Word / Print Report Generator ────────────────────────────────────────────
const generateWordReport = (plan, planData, inventory = []) => {
  const today   = new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
  const ref     = `CPO-${Date.now().toString().slice(-8)}`;
  const totalBal = plan.selected ? plan.selected.reduce((s,r)=>s+parseFloat(r.balance||r.Balance_Qty||0),0) : 0;
  const totalUsed  = plan.totalUsed  || 0;
  const totalWaste = plan.totalWaste || 0;
  const strategy = plan.strategy==='min_waste'?'Min End Bit':plan.strategy==='buffer'?'+1 Lay Buffer':'Fewest Rolls';

  // Compact roll table rows
  const rollRows = (plan.selected||[]).map((r,i) => `
    <tr style="background:${i%2===0?'white':'#f5f6f8'}">
      <td style="${td}">${i+1}</td>
      <td style="${td};font-weight:bold;color:#5b21b6;font-family:monospace">${r.Roll_ID||'—'}</td>
      <td style="${td}">${r.Color||'—'} ${r.Shade?'/ '+r.Shade:''}</td>
      <td style="${td};text-align:right">${r.Width?r.Width+'"':'—'}</td>
      <td style="${td};text-align:right;font-weight:bold">${parseFloat(r.balance||r.Balance_Qty||0).toFixed(2)}</td>
      <td style="${td};text-align:right;color:#676879">${(r.noLays||0).toFixed?r.noLays.toFixed(2):'—'}</td>
      <td style="${td};text-align:right;color:#059669;font-weight:bold">${r.fullLays??r.laysUsed??'—'}</td>
      <td style="${td};text-align:right;color:#0073ea;font-weight:bold">${r.laysUsed??'—'}</td>
      <td style="${td};text-align:right;font-weight:bold;background:#e8f5e9;color:#15803d">${parseFloat(r.usedFromRoll||0).toFixed(2)}m</td>
      <td style="${td};text-align:right;font-weight:bold;background:#fce8e8;color:#c93652">${parseFloat(r.wasteFromRoll||0).toFixed(4)}m</td>
      <td style="${td};font-weight:bold;color:#5b21b6">${r.Rack_No||'—'} / ${r.Cell_No||r.Shelf_No||'—'}</td>
    </tr>`).join('');

  const ebRows = (plan.selected||[]).filter(r=>parseFloat(r.wasteFromRoll||0)>0.001).map((r,i)=>`
    <tr style="background:${i%2===0?'#fff8f8':'white'}">
      <td style="${td};font-family:monospace;color:#c93652;font-weight:bold">EB-${r.Roll_ID}</td>
      <td style="${td}">${r.Color||'—'}</td>
      <td style="${td}">${r.Shade||'—'}</td>
      <td style="${td};text-align:right;color:#c93652;font-weight:bold">${parseFloat(r.wasteFromRoll).toFixed(4)} m</td>
      <td style="${td}">${r.end_bit_rack||r.Rack_No||'—'} / ${r.end_bit_cell||r.Cell_No||'—'}</td>
    </tr>`).join('');

  // Shared cell styles
  const td  = 'border:1px solid #c5c7d4;padding:4pt 5pt;font-size:8pt';
  const thS = 'border:1px solid #1e3a5f;padding:5pt;font-size:8pt;color:white;background:#1e3a5f;text-align:left';
  const thG = 'border:1px solid #15803d;padding:5pt;font-size:8pt;color:white;background:#15803d;text-align:left';
  const thR = 'border:1px solid #b91c1c;padding:5pt;font-size:8pt;color:white;background:#b91c1c;text-align:left';

  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
    xmlns:w="urn:schemas-microsoft-com:office:word"
    xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8">
<style>
  /* LANDSCAPE A4 */
  @page WordSection1 {
    size:842pt 595pt;
    mso-page-orientation:landscape;
    margin:1.2cm 1.2cm 1.5cm 1.2cm;
  }
  div.WordSection1 { page:WordSection1; }
  body  { font-family:Arial,sans-serif; font-size:9pt; color:#323338; }
  h1    { font-size:15pt; font-weight:900; color:#1e3a5f; margin:0 0 3pt; }
  h2    { font-size:10pt; font-weight:bold; color:#1e3a5f;
          border-bottom:1.5pt solid #0073ea; padding-bottom:3pt; margin:10pt 0 5pt; }
  table { border-collapse:collapse; width:100%; margin-bottom:8pt; }
  .kv   { border:1px solid #e6e9ef; padding:4pt 6pt; font-size:8.5pt; }
  .kv-h { border:1px solid #e6e9ef; padding:4pt 6pt; font-size:8pt;
          background:#f0f1f4; font-weight:bold; color:#676879; }
  .sig  { border:1px solid #c5c7d4; padding:28pt 6pt 6pt; text-align:center; }
  @media print { body { -webkit-print-color-adjust:exact; } }
</style>
</head>
<body><div class="WordSection1">

<!-- HEADER -->
<table style="border:none;border-bottom:2pt solid #1e3a5f;margin-bottom:8pt;width:100%">
  <tr>
    <td style="border:none;padding-bottom:6pt;width:70%">
      <h1>✂ CUT PLAN EXECUTION ORDER</h1>
      <div style="font-size:9pt;color:#676879">Nirvikalp 4.0 ERP &nbsp;|&nbsp; Material Store &amp; Cutting Floor</div>
    </td>
    <td style="border:none;text-align:right;vertical-align:top;font-size:8pt;color:#676879;padding-bottom:6pt;white-space:nowrap">
      <div><b>Date:</b> ${today}</div>
      <div><b>Ref:</b> ${ref}</div>
      <div><b>Strategy:</b> ${strategy}</div>
    </td>
  </tr>
</table>

<!-- PLAN DETAILS + KPIs in one row -->
<table style="width:100%;margin-bottom:8pt">
  <tr>
    <td style="width:50%;vertical-align:top;padding-right:8pt;border:none">
      <h2>Order Details</h2>
      <table>
        <tr><td class="kv-h" style="width:35%">Style</td><td class="kv">${planData.style||'—'}</td><td class="kv-h" style="width:20%">PO No</td><td class="kv">${planData.po||'—'}</td></tr>
        <tr><td class="kv-h">Color</td><td class="kv">${planData.color||'—'}</td><td class="kv-h">Lay Length</td><td class="kv">${planData.layLength||'—'} m</td></tr>
        <tr><td class="kv-h">Consumption/pc</td><td class="kv">${planData.consumption||'—'} m</td><td class="kv-h">Shade Filter</td><td class="kv">${planData.shade||'Any'}</td></tr>
      </table>
    </td>
    <td style="width:50%;vertical-align:top;padding-left:8pt;border:none">
      <h2>Plan Summary</h2>
      <table>
        <tr>
          ${[['Target Pcs',plan.targetPcs,'#1e3a5f'],['Achievable',plan.actualPcs||0,(plan.actualPcs||0)>=(plan.targetPcs||0)?'#15803d':'#c93652'],['Excess',(plan.excessPcs||0)>=0?'+'+(plan.excessPcs||0):(plan.excessPcs||0),(plan.excessPcs||0)>=0?'#15803d':'#c93652']].map(([l,v,cl])=>
            `<td style="border:1px solid #e6e9ef;padding:5pt;text-align:center">
              <div style="font-size:8pt;color:#676879;font-weight:bold">${l}</div>
              <div style="font-size:14pt;font-weight:900;color:${cl}">${v}</div>
            </td>`).join('')}
        </tr>
        <tr>
          ${[['Lays',plan.laysAchieved,'#0073ea'],['Rolls',plan.selected?.length||0,'#323338'],['Fabric Pulled',totalBal.toFixed(2)+'m','#323338'],['Fabric Used',totalUsed.toFixed(2)+'m','#15803d'],['End Bit Waste',totalWaste.toFixed(4)+'m','#c93652'],['Utilisation',(plan.util||0).toFixed(1)+'%',(plan.util||0)>=90?'#15803d':(plan.util||0)>=75?'#fdab3d':'#c93652']].map(([l,v,cl])=>
            `<td style="border:1px solid #e6e9ef;padding:4pt;text-align:center">
              <div style="font-size:7pt;color:#676879;font-weight:bold">${l}</div>
              <div style="font-size:11pt;font-weight:800;color:${cl}">${v}</div>
            </td>`).join('')}
        </tr>
      </table>
    </td>
  </tr>
</table>

${(plan.shortfall||0)>0?`<div style="background:#fef2f2;border:1pt solid #fecaca;border-left:4pt solid #c93652;padding:5pt 8pt;font-size:8.5pt;color:#c93652;margin-bottom:8pt;font-weight:bold">⚠ SHORTFALL: ${plan.shortfall}m of fabric not available. Add more rolls before cutting.</div>`:''}

<!-- ROLL EXECUTION LIST -->
<h2>Roll Execution List — Collect from Rack &amp; Bring to Spreading Table</h2>
<table>
  <thead>
    <tr>
      <th style="${thS};width:3%">#</th>
      <th style="${thS};width:12%">Roll ID</th>
      <th style="${thS};width:12%">Color / Shade</th>
      <th style="${thS};width:5%">Width</th>
      <th style="${thS};width:8%">Balance</th>
      <th style="${thS};width:7%">No.Lays</th>
      <th style="${thS};width:7%">Full Lays</th>
      <th style="${thG};width:7%">Lays Used</th>
      <th style="${thG};width:10%">Used (m)</th>
      <th style="${thR};width:11%">End Bit (m)</th>
      <th style="${thS};width:12%">Rack / Cell</th>
    </tr>
  </thead>
  <tbody>${rollRows}</tbody>
  <tfoot>
    <tr style="background:#323338">
      <td colspan="4" style="border:1px solid #1e3a5f;padding:5pt;text-align:right;color:white;font-weight:bold;font-size:8pt">TOTALS →</td>
      <td style="border:1px solid #1e3a5f;padding:5pt;text-align:right;color:#93c5fd;font-weight:bold;font-size:8pt">${totalBal.toFixed(2)}m</td>
      <td colspan="3" style="border:1px solid #1e3a5f;padding:5pt"></td>
      <td style="border:1px solid #1e3a5f;padding:5pt;text-align:right;color:#4ade80;font-weight:bold;font-size:8pt">${totalUsed.toFixed(2)}m</td>
      <td style="border:1px solid #1e3a5f;padding:5pt;text-align:right;color:#eb6175;font-weight:bold;font-size:8pt">${totalWaste.toFixed(4)}m</td>
      <td style="border:1px solid #1e3a5f;padding:5pt"></td>
    </tr>
  </tfoot>
</table>
<p style="font-size:7.5pt;color:#676879;margin:3pt 0 8pt">
  No.Lays = Balance ÷ Lay Length (decimal) &nbsp;|&nbsp; Full Lays = floor(No.Lays) &nbsp;|&nbsp;
  End Bit = Balance − Full Lays × Lay Length &nbsp;|&nbsp; ↩ Partial = only some lays used, remainder stays on shelf
</p>

${totalWaste>0.001?`
<h2 style="color:#b91c1c">End Bit Returns — Store After Cutting</h2>
<table>
  <thead>
    <tr>
      <th style="${thR};width:20%">End Bit Roll ID</th>
      <th style="${thR};width:15%">Color</th>
      <th style="${thR};width:10%">Shade</th>
      <th style="${thR};width:12%">End Bit (m)</th>
      <th style="${thR};width:20%">Return Location</th>
    </tr>
  </thead>
  <tbody>${ebRows}</tbody>
</table>`:''}

<!-- AUTHORISATION -->
<h2>Authorisation</h2>
<table style="width:100%">
  <tr>
    ${['Store In-Charge','Spreading Supervisor','Cutting Manager','QC Inspector'].map(name=>`
    <td class="sig" style="width:25%">
      <div style="border-top:1pt solid #676879;padding-top:4pt;font-size:8pt;color:#676879;font-weight:bold">${name}</div>
      <div style="font-size:7.5pt;color:#9699a6">Date: ___________</div>
    </td>`).join('')}
  </tr>
</table>

<div style="margin-top:10pt;padding:4pt 8pt;background:#f5f6f8;border:1pt solid #e6e9ef;font-size:7.5pt;color:#9699a6;text-align:center">
  Generated by Nirvikalp 4.0 ERP &nbsp;|&nbsp; ${today} &nbsp;|&nbsp; Ref: ${ref} &nbsp;|&nbsp; Rolls locked in system until released.
</div>

</div></body></html>`;

  const blob = new Blob(['﻿', html], { type: 'application/msword' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `CutPlan_${(planData.style||'').replace(/[^a-z0-9]/gi,'_')}_${Date.now()}.doc`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
};

const generateEndBitBarcodes = (endBits, planLabel) => {
  const QR_API = 'https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=';
  const items = endBits.map(eb => {
    const data = `ENDBIT:${eb.rollId}|QTY:${eb.endBitQty}m|RACK:${eb.rack}|CELL:${eb.cell}|COLOR:${eb.color}|SHADE:${eb.shade}`;
    return `
      <div style="display:inline-block;border:1pt solid #333;padding:6pt;margin:4pt;width:130pt;vertical-align:top;text-align:center;font-family:Arial;page-break-inside:avoid">
        <img src="${QR_API}${encodeURIComponent(data)}" width="100" height="100" style="display:block;margin:0 auto 4pt"/>
        <div style="font-size:8pt;font-weight:bold;color:#c93652">END BIT</div>
        <div style="font-size:9pt;font-weight:bold;font-family:monospace">${eb.rollId}</div>
        <div style="font-size:11pt;font-weight:900;color:#1e3a5f">${parseFloat(eb.endBitQty).toFixed(2)} m</div>
        <div style="font-size:8pt;color:#676879">${eb.color} / ${eb.shade || '—'} / ${eb.width ? eb.width + '"' : ''}</div>
        <div style="font-size:8pt;color:#676879">Rack: ${eb.rack || '—'} / Cell: ${eb.cell || '—'}</div>
        <div style="font-size:7pt;color:#9699a6;margin-top:2pt">${planLabel}</div>
      </div>`;
  });
  const html = `<html><head><title>End Bit Barcodes</title>
    <style>body{margin:1cm;font-family:Arial} @media print{.no-print{display:none}} h2{color:#1e3a5f}</style>
  </head><body>
    <div class="no-print" style="margin-bottom:10pt">
      <h2>End Bit Barcode Labels — ${planLabel}</h2>
      <button onclick="window.print()" style="padding:8pt 20pt;background:#1e3a5f;color:white;border:none;border-radius:4pt;cursor:pointer;font-size:11pt">🖨 Print Labels</button>
    </div>
    <div style="font-size:9pt;color:#676879;margin-bottom:8pt">Print on A4 · Cut and paste on each end bit roll</div>
    ${items.join('')}
  </body></html>`;
  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
};

// ── CUT PLAN PROCESSOR COMPONENT ─────────────────────────────────────────────
function CutPlanProcessor({ selPlan, planData, inventory, onInventoryUpdate, db }) {
  const [planStatus,  setPlanStatus]  = useState(null);
  const [planRef,     setPlanRef]     = useState('');
  const [endBits,     setEndBits]     = useState([]);
  const [lockedRolls, setLockedRolls] = useState([]);
  const [receiveNotes, setReceiveNotes] = useState('');
  const [showConfirm,  setShowConfirm]  = useState(null);

  // ── Restore plan state from DB-loaded inventory on mount / plan change ────
  // Rolls carry lock_status in DB. On refresh, derive state from rolls.
  useEffect(() => {
    if (!selPlan?.selected?.length) {
      setPlanStatus(null); setPlanRef(''); setEndBits([]); setLockedRolls([]);
      setShowConfirm(null);
      return;
    }
    // Find the first selected roll in current inventory to check its lock_status
    const firstRoll = inventory.find(r => selPlan.selected.some(s => s.id === r.id || s.Roll_ID === r.Roll_ID));
    if (!firstRoll) return;
    const ls = firstRoll.lock_status;
    if (ls === 'received') {
      setPlanStatus('received');
      setPlanRef(firstRoll.locked_plan_ref || '');
      // Rebuild end bits from inventory (they were saved on receive)
      const savedEbits = inventory.filter(r => r.is_end_bit && r.parent_plan_ref === firstRoll.locked_plan_ref);
      setEndBits(savedEbits);
      setLockedRolls(selPlan.selected.map(r => ({
        ...r, end_bit_qty: parseFloat(r.end_bit_reserve||r.wasteFromRoll||0),
        end_bit_rack: r.end_bit_rack||r.Rack_No||'', end_bit_cell: r.end_bit_cell||r.Cell_No||''
      })));
    } else if (ls === 'issued') {
      setPlanStatus('issued');
      setPlanRef(firstRoll.locked_plan_ref || '');
      setLockedRolls(selPlan.selected.map(r => {
        const inv = inventory.find(x => x.id===r.id||x.Roll_ID===r.Roll_ID) || r;
        return { ...r, end_bit_qty: parseFloat(inv.end_bit_reserve||r.wasteFromRoll||0),
          end_bit_rack: inv.end_bit_rack||r.Rack_No||'', end_bit_cell: inv.end_bit_cell||r.Cell_No||'' };
      }));
    } else if (ls === 'locked') {
      setPlanStatus('locked');
      setPlanRef(firstRoll.locked_plan_ref || '');
      setLockedRolls(selPlan.selected.map(r => {
        const inv = inventory.find(x => x.id===r.id||x.Roll_ID===r.Roll_ID) || r;
        return { ...r, end_bit_qty: parseFloat(inv.end_bit_reserve||r.wasteFromRoll||0),
          end_bit_rack: inv.end_bit_rack||r.Rack_No||'', end_bit_cell: inv.end_bit_cell||r.Cell_No||'' };
      }));
    } else {
      setPlanStatus(null); setPlanRef(''); setEndBits([]); setLockedRolls([]);
    }
    setShowConfirm(null);
  }, [selPlan]);

  if (!selPlan) return null;

  const today = new Date().toISOString().split('T')[0];
  const ref   = planRef || `CPO-${Date.now().toString().slice(-8)}`;

  // ── LOCK ROLLS ──────────────────────────────────────────────────────────────
  const handleLock = () => {
    const pRef = `CPO-${Date.now().toString().slice(-8)}`;
    setPlanRef(pRef);
    const rollIds = new Set(selPlan.selected.map(r => r.id));
    const updated = inventory.map(roll => {
      if (!rollIds.has(roll.id)) return roll;
      const planRoll = selPlan.selected.find(r => r.id === roll.id);
      return {
        ...roll,
        lock_status:      'locked',
        locked_plan_ref:  pRef,
        locked_date:      today,
        lays_in_plan:     planRoll?.laysUsed || 0,
        end_bit_reserve:  parseFloat((planRoll?.wasteFromRoll || 0).toFixed(4)),
        used_in_plan:     parseFloat((planRoll?.usedFromRoll  || 0).toFixed(4)),
        end_bit_rack:     roll.Rack_No,
        end_bit_cell:     roll.Cell_No,
      };
    });
    onInventoryUpdate(updated);
    setLockedRolls(selPlan.selected.map(r => ({
      ...r,
      end_bit_qty:  parseFloat((r.wasteFromRoll || 0).toFixed(4)),
      end_bit_rack: r.Rack_No || '',
      end_bit_cell: r.Cell_No || r.Shelf_No || '',
    })));
    setPlanStatus('locked');
    setShowConfirm(null);
    // Persist lock_status to DB so it survives page refresh
    setTimeout(async () => {
      const changed = newInv.filter(r => rollIds.has(r.id) && r.lock_status === 'locked');
      for (const r of changed) await saveDB('material', r);
    }, 50);
  };

  // ── MARK ISSUED ─────────────────────────────────────────────────────────────
  const handleIssue = () => {
    const rollIds = new Set(selPlan.selected.map(r => r.id));
    const updated = inventory.map(roll => {
      if (!rollIds.has(roll.id)) return roll;
      return { ...roll, lock_status: 'issued', issued_date: today };
    });
    onInventoryUpdate(updated);
    setPlanStatus('issued');
    setShowConfirm(null);
    // Persist issued status to DB
    setTimeout(async () => {
      const changed = updated.filter(r => rollIds.has(r.id) && r.lock_status === 'issued');
      for (const r of changed) await saveDB('material', r);
    }, 50);
  };

  // ── MARK RECEIVED & CREATE END BIT ROLLS ────────────────────────────────────
  const handleReceive = () => {
    const rollIds = new Set(selPlan.selected.map(r => r.id));
    const newEndBitRolls = [];
    const updated = inventory.map(roll => {
      if (!rollIds.has(roll.id)) return roll;
      const lr = lockedRolls.find(x => x.id === roll.id);
      const ebQty = lr ? parseFloat(lr.end_bit_qty) : parseFloat(roll.end_bit_reserve || 0);
      const usedQty = parseFloat(roll.used_in_plan || 0);
      // Create end bit roll entry if there's remaining fabric
      if (ebQty > 0) {
        newEndBitRolls.push({
          id:           Date.now() + Math.random(),
          Roll_ID:      `EB-${roll.Roll_ID}`,
          SKU_ID:       `EB-${roll.SKU_ID || roll.Roll_ID}`,
          Style_Name:   roll.Style_Name,
          Order_No:     roll.Order_No,
          Color:        roll.Color,
          Shade:        roll.Shade,
          Width:        roll.Width,
          M_Qty:        ebQty,
          Balance_Qty:  ebQty,
          Received_Qty: ebQty,
          Issue_Qty:    0,
          Rate:         roll.Rate || 0,
          UOM:          roll.UOM || 'Meters',
          Rack_No:      lr?.end_bit_rack || roll.Rack_No || '',
          Cell_No:      lr?.end_bit_cell || roll.Cell_No || '',
          Quality_Status: roll.Quality_Status || 'Pending',
          is_end_bit:   true,
          parent_roll_id: roll.Roll_ID,
          parent_plan_ref: planRef,
          created_date: today,
        });
      }
      return {
        ...roll,
        lock_status:    'received',
        received_date:  today,
        Balance_Qty:    Math.max(0, parseFloat(roll.Balance_Qty || roll.M_Qty || 0) - usedQty - ebQty),
        Issued_Qty:     usedQty,
        Issue_Qty:      usedQty,
      };
    });
    // Merge end bit rolls into inventory
    const withEndBits = [...updated, ...newEndBitRolls];
    onInventoryUpdate(withEndBits);
    setEndBits(newEndBitRolls);
    setPlanStatus('received');
    setShowConfirm(null);
    // Persist received rolls + end bit rolls to DB
    setTimeout(async () => {
      const changedRolls = updated.filter(r => rollIds.has(r.id));
      for (const r of changedRolls) await saveDB('material', r);
      // Save end bit rolls as new DB entries (bulk for speed)
      if (newEndBitRolls.length > 0) {
        try {
          await fetch(DB_URL, {
            method:'POST', headers:{'Content-Type':'application/json'},
            body: JSON.stringify({ category:'material_bulk', data: newEndBitRolls })
          });
        } catch(e) { console.warn('End bit save error:', e); }
      }
    }, 50);
  };

  // ── RELEASE ─────────────────────────────────────────────────────────────────
  const handleRelease = () => {
    const rollIds = new Set(selPlan.selected.map(r => r.id));
    const updated = inventory.map(roll => {
      if (!rollIds.has(roll.id)) return roll;
      const { lock_status, locked_plan_ref, locked_date, lays_in_plan, end_bit_reserve,
              used_in_plan, end_bit_rack, end_bit_cell, issued_date, received_date, ...rest } = roll;
      return rest;
    });
    onInventoryUpdate(updated);
    setPlanStatus(null); setLockedRolls([]); setEndBits([]);
    setShowConfirm(null);
    // Clear lock_status in DB
    setTimeout(async () => {
      const prevLocked = selPlan.selected.map(r => inventory.find(x=>x.id===r.id||x.Roll_ID===r.Roll_ID)).filter(Boolean);
      for (const r of prevLocked) await saveDB('material', {...r, lock_status:null, locked_plan_ref:null, locked_date:null});
    }, 50);
  };

  const STATUS_META = {
    locked:   { color:'#fdab3d', bg:'#fffbeb', border:'#fde68a', label:'🔒 LOCKED',         desc:'Rolls reserved for this plan. No other plan can use them.' },
    issued:   { color:'#0073ea', bg:'#e8f0ff', border:'#a8d1ff', label:'📤 ISSUED TO CUTTING', desc:'Rolls marked as sent to spreading table. Waiting for receipt.' },
    received: { color:'#00a65e', bg:'#f0fdf4', border:'#b3f5d6', label:'✅ RECEIVED BY CUTTING', desc:'Cutting confirmed receipt. End bits back in store.' },
  };
  const sm = planStatus ? STATUS_META[planStatus] : null;

  const ConfirmDialog = ({ action, msg, onConfirm, onCancel }) => (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:'white',borderRadius:'12px',padding:'24px',maxWidth:'400px',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
        <div style={{fontWeight:'800',fontSize:'16px',marginBottom:'12px',color:'#323338'}}>{action}</div>
        <div style={{fontSize:'13px',color:'#676879',marginBottom:'20px'}}>{msg}</div>
        <div style={{display:'flex',gap:'10px'}}>
          <button onClick={onConfirm} style={{flex:2,padding:'11px',background:'#1e3a5f',color:'white',border:'none',borderRadius:'7px',cursor:'pointer',fontWeight:'800',fontSize:'13px'}}>Confirm</button>
          <button onClick={onCancel}  style={{flex:1,padding:'11px',background:'#f0f1f4',color:'#676879',border:'1px solid #e6e9ef',borderRadius:'7px',cursor:'pointer'}}>Cancel</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{marginTop:'20px'}}>
      {showConfirm === 'lock'    && <ConfirmDialog action="🔒 Lock Rolls" msg={`Lock ${selPlan.selected.length} rolls for this cut plan? No other plan can use these rolls until released.`} onConfirm={handleLock} onCancel={()=>setShowConfirm(null)}/>}
      {showConfirm === 'issue'   && <ConfirmDialog action="📤 Mark as Issued" msg={`Mark all ${selPlan.selected.length} rolls as Issued to Cutting? Rolls will show status 'Awaiting Pickup' in the store.`} onConfirm={handleIssue} onCancel={()=>setShowConfirm(null)}/>}
      {showConfirm === 'receive' && <ConfirmDialog action="✅ Mark as Received" msg={`Confirm Cutting has received all rolls? End bits will be added back to store inventory automatically.`} onConfirm={handleReceive} onCancel={()=>setShowConfirm(null)}/>}
      {showConfirm === 'release' && <ConfirmDialog action="🔓 Release Locks" msg={`Release all roll locks? This plan will be cancelled. Rolls go back to available stock.`} onConfirm={handleRelease} onCancel={()=>setShowConfirm(null)}/>}

      {/* Status banner */}
      {sm && (
        <div style={{padding:'12px 16px',background:sm.bg,border:`1px solid ${sm.border}`,borderRadius:'10px',marginBottom:'16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <div style={{fontWeight:'800',fontSize:'14px',color:sm.color}}>{sm.label}</div>
            <div style={{fontSize:'12px',color:'#676879',marginTop:'2px'}}>{sm.desc} &nbsp;|&nbsp; Ref: <strong>{planRef}</strong></div>
          </div>
          <div style={{display:'flex',gap:'8px'}}>
            {planStatus==='locked'   && <button onClick={()=>setShowConfirm('release')} style={{padding:'7px 14px',background:'#fef2f2',color:'#c93652',border:'1px solid #fecaca',borderRadius:'6px',cursor:'pointer',fontWeight:'700',fontSize:'12px'}}>🔓 Release</button>}
            {planStatus==='received' && <button onClick={()=>setShowConfirm('release')} style={{padding:'7px 14px',background:'#fef2f2',color:'#c93652',border:'1px solid #fecaca',borderRadius:'6px',cursor:'pointer',fontWeight:'700',fontSize:'12px'}}>🔓 Reset</button>}
          </div>
        </div>
      )}

      {/* Action toolbar */}
      <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginBottom:'18px',padding:'14px',background:'#f5f6f8',border:'1px solid #e6e9ef',borderRadius:'10px'}}>
        <div style={{fontSize:'12px',fontWeight:'700',color:'#676879',alignSelf:'center',marginRight:'4px'}}>Actions:</div>

        {!planStatus && (
          <button onClick={()=>setShowConfirm('lock')} style={{padding:'10px 18px',background:'#fdab3d',color:'white',border:'none',borderRadius:'7px',cursor:'pointer',fontWeight:'800',fontSize:'13px',display:'flex',alignItems:'center',gap:'7px'}}>
            🔒 Process (Lock Rolls)
          </button>
        )}
        {planStatus==='locked' && (
          <button onClick={()=>setShowConfirm('issue')} style={{padding:'10px 18px',background:'#0073ea',color:'white',border:'none',borderRadius:'7px',cursor:'pointer',fontWeight:'800',fontSize:'13px',display:'flex',alignItems:'center',gap:'7px'}}>
            📤 Mark as Issued to Cutting
          </button>
        )}
        {planStatus==='issued' && (
          <button onClick={()=>setShowConfirm('receive')} style={{padding:'10px 18px',background:'#00a65e',color:'white',border:'none',borderRadius:'7px',cursor:'pointer',fontWeight:'800',fontSize:'13px',display:'flex',alignItems:'center',gap:'7px'}}>
            ✅ Mark as Received by Cutting
          </button>
        )}

        {/* Always available */}
        <button onClick={()=>generateWordReport(selPlan, planData)} style={{padding:'10px 18px',background:'#1e3a5f',color:'white',border:'none',borderRadius:'7px',cursor:'pointer',fontWeight:'700',fontSize:'12px',display:'flex',alignItems:'center',gap:'7px'}}>
          📄 Download Word Report (.doc)
        </button>

        {(planStatus==='received' || (endBits.length === 0 && selPlan.selected.some(r => parseFloat(r.wasteFromRoll || 0) > 0))) && (
          <button onClick={()=>{
            const barcodeData = (endBits.length > 0 ? endBits : selPlan.selected.filter(r=>parseFloat(r.wasteFromRoll||0)>0).map(r=>({
              rollId: `EB-${r.Roll_ID}`, endBitQty: r.wasteFromRoll, color: r.Color, shade: r.Shade, width: r.Width,
              rack: r.end_bit_rack||r.Rack_No||'', cell: r.end_bit_cell||r.Cell_No||''
            }))).map(eb=>({
              rollId: eb.Roll_ID||eb.rollId, endBitQty: eb.M_Qty||eb.endBitQty,
              color: eb.Color||eb.color, shade: eb.Shade||eb.shade, width: eb.Width||eb.width,
              rack: eb.Rack_No||eb.rack||'', cell: eb.Cell_No||eb.cell||''
            }));
            generateEndBitBarcodes(barcodeData, selPlan.label);
          }} style={{padding:'10px 18px',background:'#a25ddc',color:'white',border:'none',borderRadius:'7px',cursor:'pointer',fontWeight:'700',fontSize:'12px',display:'flex',alignItems:'center',gap:'7px'}}>
            🏷 Print End Bit Barcodes
          </button>
        )}
      </div>

      {/* End bit location editor (visible from locked state onward) */}
      {lockedRolls.length > 0 && (
        <div style={{marginBottom:'18px'}}>
          <div style={{fontSize:'13px',fontWeight:'800',color:'#323338',marginBottom:'10px'}}>
            🗃 End Bit Return Locations {planStatus === 'received' ? '(Saved to Inventory)' : '(Edit before receiving)'}
          </div>
          <div style={{overflowX:'auto',border:'1px solid #e6e9ef',borderRadius:'8px'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['Roll ID','Color','Shade','End Bit (m)','Return Rack','Return Cell','Barcode'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
              <tbody>
                {lockedRolls.filter(r => parseFloat(r.end_bit_qty || 0) > 0).map((r, i) => (
                  <tr key={r.id} style={{background:i%2===0?'white':'#fafafa'}}>
                    <td style={{...TD,fontWeight:'700',color:'#c93652',fontFamily:'monospace'}}>EB-{r.Roll_ID}</td>
                    <td style={TD}>{r.Color}</td>
                    <td style={TD}>{r.Shade || '—'}</td>
                    <td style={{...TD,textAlign:'right',fontWeight:'800',color:'#c93652'}}>{parseFloat(r.end_bit_qty).toFixed(4)} m</td>
                    <td style={TD}>
                      <input value={r.end_bit_rack || ''} onChange={e => setLockedRolls(p => p.map(x => x.id===r.id?{...x,end_bit_rack:e.target.value}:x))}
                        disabled={planStatus==='received'}
                        style={{border:'1px solid #e6e9ef',borderRadius:'4px',padding:'4px 8px',fontSize:'12px',width:'70px',background:planStatus==='received'?'#f0f1f4':'white'}}/>
                    </td>
                    <td style={TD}>
                      <input value={r.end_bit_cell || ''} onChange={e => setLockedRolls(p => p.map(x => x.id===r.id?{...x,end_bit_cell:e.target.value}:x))}
                        disabled={planStatus==='received'}
                        style={{border:'1px solid #e6e9ef',borderRadius:'4px',padding:'4px 8px',fontSize:'12px',width:'70px',background:planStatus==='received'?'#f0f1f4':'white'}}/>
                    </td>
                    <td style={TD}>
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=${encodeURIComponent(`EB-${r.Roll_ID}|${parseFloat(r.end_bit_qty).toFixed(2)}m|${r.Color}`)}`}
                        alt="QR" width={60} height={60}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Post-receive end bit inventory confirmation */}
      {planStatus === 'received' && endBits.length > 0 && (
        <div style={{background:'#f0fdf4',border:'1px solid #b3f5d6',borderRadius:'10px',padding:'14px'}}>
          <div style={{fontWeight:'800',fontSize:'13px',color:'#15803d',marginBottom:'10px'}}>✅ End Bit Rolls Created in Inventory</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:'10px'}}>
            {endBits.map((eb, i) => (
              <div key={i} style={{background:'white',border:'1px solid #b3f5d6',borderRadius:'7px',padding:'10px',fontSize:'12px'}}>
                <div style={{fontWeight:'800',color:'#c93652',fontFamily:'monospace',marginBottom:'4px'}}>{eb.Roll_ID}</div>
                <div style={{color:'#00a65e',fontWeight:'700',fontSize:'14px'}}>{parseFloat(eb.M_Qty).toFixed(4)} m</div>
                <div style={{color:'#676879',fontSize:'11px'}}>{eb.Color} / {eb.Shade || '—'} / {eb.Width ? eb.Width + '"' : ''}</div>
                <div style={{color:'#676879',marginTop:'3px',fontSize:'11px'}}>📍 {eb.Rack_No || '—'} / {eb.Cell_No || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── FABRIC RECONCILIATION DASHBOARD ──────────────────────────────────────────

// ============================================================================
// CONSUMPTION SETUP + HIERARCHICAL RECONCILIATION + ALARM DASHBOARD
// ============================================================================

// ── Helper: get stored consumption for a style ─────────────────────────────
const getStyleConsumption = (db, styleRef) => {
  const style = getStyles(db).find(s => s.styleRef === styleRef || s.name === styleRef);
  return style?.fabric_consumption || null;
};

// ── Core coverage calculation ──────────────────────────────────────────────
// Returns per-style/PO/color/size coverage objects
function buildCoverageTree(db, inventory) {
  const styles   = getStyles(db);
  const poRows   = db?.poSizeTable || [];
  const result   = [];

  styles.forEach(style => {
    const styleRef = style.styleRef || style.name;
    const cons_cfg = style.fabric_consumption || {};
    const defCons  = parseFloat(cons_cfg.default || cons_cfg.consumption || 0);
    const wastePct = parseFloat(cons_cfg.wastage_pct || 5) / 100;
    const effCons  = defCons > 0 ? defCons * (1 + wastePct) : 0;

    // All rolls for this style
    const styleRolls = inventory.filter(r => r.Style_Name === styleRef);
    const styleAvail  = styleRolls.reduce((s, r) => s + parseFloat(r.Balance_Qty || 0), 0);

    // Group PO rows for this style → by PO → by color → by size
    const poMap = {};
    poRows.filter(r => (r.style_ref || r.styleRef) === styleRef).forEach(r => {
      const po    = r.po_no || r.poNo || '—';
      const color = r.color || r.colour || '—';
      const sz    = r.size || 'ALL';
      const qty   = parseInt(r.qty) || 0;
      if (!poMap[po]) poMap[po] = {};
      if (!poMap[po][color]) poMap[po][color] = {};
      if (!poMap[po][color][sz]) poMap[po][color][sz] = 0;
      poMap[po][color][sz] += qty;
    });

    if (!Object.keys(poMap).length && !styleRolls.length) return; // no data

    const poList = [];
    let styleTotalRequired = 0;

    Object.entries(poMap).forEach(([po, colorMap]) => {
      const poRolls  = styleRolls.filter(r => r.Order_No === po);
      const poAvail  = poRolls.reduce((s, r) => s + parseFloat(r.Balance_Qty || 0), 0);
      const colorList = [];
      let poRequired  = 0;

      Object.entries(colorMap).forEach(([color, sizeMap]) => {
        const colorRolls = poRolls.filter(r => (r.Color || '').toLowerCase() === color.toLowerCase());
        const colorAvail = colorRolls.reduce((s, r) => s + parseFloat(r.Balance_Qty || 0), 0);
        // QC-passed only
        const qcPassAvail = colorRolls
          .filter(r => getRollQCStatus(db, r) === 'PASS')
          .reduce((s, r) => s + parseFloat(r.Balance_Qty || 0), 0);

        // Size breakdown
        const sizeList = [];
        let colorRequired = 0;

        Object.entries(sizeMap).forEach(([sz, qty]) => {
          const szCons   = parseFloat(cons_cfg.by_size?.[sz] || defCons || 0);
          const szEff    = szCons > 0 ? szCons * (1 + wastePct) : effCons;
          const szReq    = szEff > 0 ? qty * szEff : 0;
          colorRequired += szReq;
          sizeList.push({ size: sz, qty, consumption: szCons, required: szReq });
        });

        const colorTotalQty  = Object.values(sizeMap).reduce((a, b) => a + b, 0);
        const colorPossible  = effCons > 0 ? Math.floor(colorAvail / effCons) : 0;
        const colorQCPoss    = effCons > 0 ? Math.floor(qcPassAvail / effCons) : 0;
        const excessShort    = colorAvail - colorRequired;
        const coveragePct    = colorRequired > 0 ? (colorAvail / colorRequired * 100) : (colorAvail > 0 ? Infinity : 0);
        const status = coveragePct === 0 ? 'no_fabric'
                     : coveragePct < 80  ? 'critical'
                     : coveragePct < 95  ? 'warning'
                     : coveragePct < 110 ? 'ok'
                     : 'excess';

        poRequired += colorRequired;
        colorList.push({ color, totalQty: colorTotalQty, required: colorRequired,
          available: colorAvail, qcPassAvail, excessShort, coveragePct, status,
          colorPossible, colorQCPoss, rolls: colorRolls.length, sizes: sizeList });
      });

      const poExcess   = poAvail - poRequired;
      const poCovPct   = poRequired > 0 ? (poAvail / poRequired * 100) : (poAvail > 0 ? Infinity : 0);
      const poStatus   = poCovPct === 0 ? 'no_fabric' : poCovPct < 80 ? 'critical'
                       : poCovPct < 95 ? 'warning' : poCovPct < 110 ? 'ok' : 'excess';
      styleTotalRequired += poRequired;
      poList.push({ po, required: poRequired, available: poAvail, excessShort: poExcess,
        coveragePct: poCovPct, status: poStatus, colors: colorList, rolls: poRolls.length });
    });

    const styleExcess  = styleAvail - styleTotalRequired;
    const styleCovPct  = styleTotalRequired > 0 ? (styleAvail / styleTotalRequired * 100) : (styleAvail > 0 ? Infinity : 0);
    const styleStatus  = styleCovPct === 0 && !styleTotalRequired ? 'no_consumption'
                       : styleCovPct < 80 ? 'critical'
                       : styleCovPct < 95 ? 'warning'
                       : styleCovPct < 110 ? 'ok'
                       : styleTotalRequired === 0 ? 'no_po'
                       : 'excess';

    result.push({ styleRef, style, defCons, wastePct: wastePct * 100, effCons,
      required: styleTotalRequired, available: styleAvail, excessShort: styleExcess,
      coveragePct: styleCovPct, status: styleStatus, pos: poList,
      hasConsumption: defCons > 0 });
  });

  return result;
}

// ── Status display config ────────────────────────────────────────────────────
const ST = {
  critical:      { color:'#c93652', bg:'#fef2f2', border:'#fecaca', label:'🔴 CRITICAL',     icon:'🚨' },
  warning:       { color:'#fdab3d', bg:'#fffbeb', border:'#fde68a', label:'🟡 WARNING',       icon:'⚠️'  },
  ok:            { color:'#00a65e', bg:'#f0fdf4', border:'#b3f5d6', label:'🟢 OK',            icon:'✅'  },
  excess:        { color:'#0073ea', bg:'#e8f0ff', border:'#a8d1ff', label:'🔵 EXCESS',        icon:'⬆'  },
  no_fabric:     { color:'#676879', bg:'#f5f6f8', border:'#e6e9ef', label:'⬜ NO FABRIC',     icon:'📭'  },
  no_consumption:{ color:'#9699a6', bg:'#f5f6f8', border:'#e6e9ef', label:'— SETUP NEEDED',  icon:'⚙'  },
  no_po:         { color:'#9699a6', bg:'#f5f6f8', border:'#e6e9ef', label:'— NO PO',          icon:'📋'  },
};

function CoverageBadge({ status, compact=false }) {
  const s = ST[status] || ST.no_consumption;
  return (
    <span style={{ fontSize:'10px', fontWeight:'800', padding:'2px 8px', borderRadius:'20px',
      background:s.bg, color:s.color, border:`1px solid ${s.border}`, whiteSpace:'nowrap' }}>
      {compact ? s.icon : s.label}
    </span>
  );
}

function CoverageBar({ pct, status }) {
  const s = ST[status] || ST.no_consumption;
  const w = Math.min(100, Math.max(0, isFinite(pct) ? pct : 0));
  return (
    <div style={{ background:'#f0f1f4', borderRadius:'4px', height:'6px', overflow:'hidden', minWidth:'60px' }}>
      <div style={{ width:`${w}%`, height:'100%', background:s.color, borderRadius:'4px', transition:'width 0.3s' }}/>
    </div>
  );
}

// ── Consumption Setup Component ───────────────────────────────────────────────
function ConsumptionSetup({ db, setDb }) {
  const styles   = getStyles(db);
  const styleRefs = getStyleRefs(db);
  const [selStyle, setSelStyle] = useState('');
  const [form,     setForm]     = useState({ default:'', wastage_pct:'5', lay_length:'7.5' });
  const [sizeCons, setSizeCons] = useState({}); // {size: meters}
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);

  const sizeOpts = selStyle ? getSizesForPO(db, selStyle, null) : ['XS','S','M','L','XL','XXL'];

  // Load existing consumption when style changes
  const handleStyleChange = (ref) => {
    setSelStyle(ref);
    const existing = getStyleConsumption(db, ref);
    if (existing) {
      setForm({ default: existing.default||'', wastage_pct: existing.wastage_pct||'5', lay_length: existing.lay_length||'7.5' });
      setSizeCons(existing.by_size || {});
    } else {
      setForm({ default:'', wastage_pct:'5', lay_length:'7.5' });
      setSizeCons({});
    }
    setSaved(false);
  };

  const handleSave = async () => {
    if (!selStyle) return alert('Select a style first');
    if (!form.default) return alert('Enter default consumption (m/pc)');
    setSaving(true);
    const styleObj = styles.find(s => s.styleRef === selStyle || s.name === selStyle);
    if (!styleObj) { setSaving(false); return alert('Style not found'); }
    const updated = {
      ...styleObj,
      fabric_consumption: {
        default:     parseFloat(form.default),
        wastage_pct: parseFloat(form.wastage_pct || 5),
        lay_length:  parseFloat(form.lay_length  || 7.5),
        by_size:     Object.fromEntries(
          Object.entries(sizeCons).filter(([,v]) => v && parseFloat(v) > 0)
            .map(([k,v]) => [k, parseFloat(v)])
        ),
      }
    };
    // Save to DB via style category
    await saveDB('style', updated);
    // Update local db
    setDb(prev => ({
      ...prev,
      mgmtStyles: (prev.mgmtStyles || []).map(s =>
        (s.styleRef === selStyle || s.name === selStyle) ? updated : s
      )
    }));
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const effCons = parseFloat(form.default) * (1 + parseFloat(form.wastage_pct || 0) / 100);

  return (
    <div>
      <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:'8px', padding:'12px', marginBottom:'18px', fontSize:'12px', color:'#78350f' }}>
        💡 Set fabric consumption per style here. The reconciliation will automatically calculate fabric coverage for every PO, color, and size against actual inventory.
      </div>

      {/* Style selector */}
      <div style={ss}>
        <h3 style={hs}><Zap size={14}/> Consumption Setup per Style</h3>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'16px' }}>
          <div>
            <label style={{ fontSize:'10px', fontWeight:'700', color:'#676879', textTransform:'uppercase', display:'block', marginBottom:'4px' }}>Select Style *</label>
            <select value={selStyle} onChange={e => handleStyleChange(e.target.value)}
              style={{ width:'100%', padding:'9px 12px', border:'1px solid #e6e9ef', borderRadius:'7px', fontSize:'13px', outline:'none' }}>
              <option value=''>— Choose style —</option>
              {styleRefs.map(s => {
                const hasCons = !!getStyleConsumption(db, s);
                return <option key={s} value={s}>{hasCons ? '✅ ' : '⚙ '}{s}</option>;
              })}
            </select>
          </div>
          {selStyle && (
            <div style={{ background:'#f0fdf4', border:'1px solid #b3f5d6', borderRadius:'8px', padding:'10px' }}>
              <div style={{ fontSize:'10px', color:'#676879', fontWeight:'700', textTransform:'uppercase', marginBottom:'4px' }}>Effective consumption (with wastage)</div>
              <div style={{ fontSize:'22px', fontWeight:'900', color:'#00a65e' }}>{isNaN(effCons) ? '—' : effCons.toFixed(4)} m/pc</div>
              <div style={{ fontSize:'11px', color:'#676879' }}>{form.default}m × (1 + {form.wastage_pct}%)</div>
            </div>
          )}
        </div>

        {selStyle && (
          <>
            {/* Main consumption fields */}
            <div style={{ background:'#f5f6f8', border:'1px solid #e6e9ef', borderRadius:'8px', padding:'14px', marginBottom:'14px' }}>
              <div style={{ fontSize:'11px', fontWeight:'800', color:'#676879', textTransform:'uppercase', marginBottom:'12px' }}>Default Consumption Settings</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:'12px' }}>
                <Fld label="Default Consumption (m/pc) *" value={form.default}
                  onChange={e => setForm(f => ({...f, default: e.target.value}))} type="number" placeholder="e.g. 1.65" required/>
                <Fld label="Process Wastage %" value={form.wastage_pct}
                  onChange={e => setForm(f => ({...f, wastage_pct: e.target.value}))} type="number" placeholder="5"/>
                <Fld label="Default Lay Length (m)" value={form.lay_length}
                  onChange={e => setForm(f => ({...f, lay_length: e.target.value}))} type="number" placeholder="7.5"/>
              </div>
            </div>

            {/* Size-wise consumption (optional override) */}
            <div style={{ background:'#e8f0ff', border:'1px solid #a8d1ff', borderRadius:'8px', padding:'14px', marginBottom:'16px' }}>
              <div style={{ fontSize:'11px', fontWeight:'800', color:'#0062c4', textTransform:'uppercase', marginBottom:'12px' }}>
                Size-wise Consumption Override <span style={{ color:'#676879', fontWeight:'400' }}>(optional — leave blank to use default)</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:'10px' }}>
                {sizeOpts.map(sz => (
                  <div key={sz}>
                    <label style={{ fontSize:'10px', fontWeight:'700', color:'#676879', textTransform:'uppercase', display:'block', marginBottom:'3px' }}>{sz} (m/pc)</label>
                    <input type="number" value={sizeCons[sz] || ''} placeholder={form.default || 'e.g. 1.65'}
                      onChange={e => setSizeCons(p => ({ ...p, [sz]: e.target.value }))}
                      style={{ width:'100%', padding:'7px 10px', border:'1px solid #a8d1ff', borderRadius:'6px', fontSize:'12px', outline:'none', boxSizing:'border-box' }}/>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:'11px', color:'#676879', marginTop:'8px' }}>
                💡 Sizes with no override will use the default {form.default || '—'}m/pc
              </div>
            </div>

            <button onClick={handleSave} disabled={saving}
              style={{ padding:'12px 32px', background: saved ? '#00a65e' : '#0073ea', color:'white', border:'none', borderRadius:'8px', fontWeight:'800', cursor:'pointer', fontSize:'14px', display:'flex', alignItems:'center', gap:'8px' }}>
              {saving ? '⏳ Saving…' : saved ? '✅ Saved to Database!' : '💾 Save Consumption for ' + selStyle}
            </button>
          </>
        )}
      </div>

      {/* Quick overview of all styles */}
      <div style={ss}>
        <h3 style={hs}><ClipboardList size={14}/> All Styles — Consumption Status</h3>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>{['Style','Consumption (m/pc)','Wastage %','Eff. Cons','Lay Length','Size Overrides','Action'].map(h => <th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {styleRefs.map((ref, i) => {
                const cons = getStyleConsumption(db, ref);
                const eff  = cons ? (parseFloat(cons.default) * (1 + (cons.wastage_pct||5)/100)).toFixed(4) : null;
                return (
                  <tr key={ref} style={{ background: i%2===0?'white':'#fafafa', borderBottom:'1px solid #f0f1f4' }}>
                    <td style={{ ...TD, fontWeight:'700', color:'#8b5cf6' }}>{ref}</td>
                    <td style={{ ...TD, textAlign:'right', fontWeight:'700', color: cons?'#00a65e':'#9699a6' }}>
                      {cons ? `${cons.default} m` : <span style={{ color:'#c93652' }}>⚠ Not set</span>}
                    </td>
                    <td style={{ ...TD, textAlign:'right' }}>{cons ? `${cons.wastage_pct || 5}%` : '—'}</td>
                    <td style={{ ...TD, textAlign:'right', fontWeight:'700', color:'#0073ea' }}>{eff ? `${eff} m` : '—'}</td>
                    <td style={{ ...TD, textAlign:'right' }}>{cons?.lay_length ? `${cons.lay_length} m` : '—'}</td>
                    <td style={{ TD, textAlign:'center' }}>{cons?.by_size && Object.keys(cons.by_size).length > 0 ? `${Object.keys(cons.by_size).length} sizes` : '—'}</td>
                    <td style={TD}><button onClick={() => handleStyleChange(ref)} style={{ padding:'4px 10px', background:'#e8f0ff', color:'#0073ea', border:'1px solid #a8d1ff', borderRadius:'5px', cursor:'pointer', fontSize:'11px', fontWeight:'700' }}>Edit</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Main FabricReconciliation (replaces old simple version) ─────────────────
function FabricReconciliation({ inventory, db, setDb, lastIssueAt }) {
  const [reconView, setReconView] = useState('coverage'); // 'setup' | 'coverage' | 'alarms'
  const [expandedStyle, setExpandedStyle] = useState(null);
  const [expandedPO,    setExpandedPO]    = useState(null);
  const [filterStatus,  setFilterStatus]  = useState('all');
  const [filterStyle,   setFilterStyle]   = useState('');

  // Re-runs on every inventory change AND every issue/receive (lastIssueAt)
  const tree = useMemo(() => buildCoverageTree(db, inventory), [db, inventory, lastIssueAt]);
  const treeUpdatedAt = useMemo(() => new Date(), [tree]);
  const filtered = tree.filter(s => {
    if (filterStyle && s.styleRef !== filterStyle) return false;
    if (filterStatus === 'all') return true;
    return s.status === filterStatus;
  });

  // Summary stats
  const stats = useMemo(() => ({
    critical: tree.filter(s => s.status === 'critical').length,
    warning:  tree.filter(s => s.status === 'warning').length,
    ok:       tree.filter(s => s.status === 'ok').length,
    excess:   tree.filter(s => s.status === 'excess').length,
    noSetup:  tree.filter(s => s.status === 'no_consumption' || s.status === 'no_po').length,
    colorAlarms: tree.flatMap(s => s.pos.flatMap(p => p.colors.filter(c => c.status === 'critical' || c.status === 'warning'))).length,
  }), [tree]);

  const styleRefs = getStyleRefs(db);

  return (
    <div>
      {/* Sub-navigation */}
      <div style={{ display:'flex', borderBottom:'1px solid #e6e9ef', background:'white', marginBottom:'20px', borderRadius:'10px 10px 0 0' }}>
        {[
          { id:'coverage', label:'📊 Coverage Analysis', desc:'Style/PO/Color/Size' },
          { id:'alarms',   label:`🚨 Alarms & Warnings${stats.critical+stats.warning > 0 ? ` (${stats.critical+stats.warning})` : ''}`, desc:'Action required' },
          { id:'setup',    label:'⚙ Consumption Setup', desc:'Enter m/pc per style' },
        ].map(v => (
          <button key={v.id} onClick={() => setReconView(v.id)} style={{ flex:1, padding:'12px 8px', border:'none', borderBottom: reconView===v.id ? '3px solid #0073ea' : '3px solid transparent', background:'transparent', color: reconView===v.id ? '#0062c4' : '#676879', fontWeight: reconView===v.id ? 800 : 600, fontSize:'13px', cursor:'pointer' }}>
            {v.label}
          </button>
        ))}
      </div>

      {/* ── SETUP TAB ─────────────────────────────────────────────────────── */}
      {/* Live status bar */}
      <div style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 14px',background:'#f0fdf4',border:'1px solid #b3f5d6',borderRadius:'8px',marginBottom:'16px',fontSize:'12px'}}>
        <div style={{width:'8px',height:'8px',borderRadius:'50%',background:'#00a65e',animation:'pulse 2s infinite',flexShrink:0}}/>
        <span style={{color:'#15803d',fontWeight:'700'}}>LIVE</span>
        <span style={{color:'#676879'}}>Reconciliation updates automatically with every issue, receive, or inventory change.</span>
        <span style={{marginLeft:'auto',color:'#9699a6',fontSize:'11px'}}>Last calculated: {treeUpdatedAt.toLocaleTimeString()}</span>
      </div>

      {reconView === 'setup' && <ConsumptionSetup db={db} setDb={setDb}/>}

      {/* ── ALARMS TAB ────────────────────────────────────────────────────── */}
      {reconView === 'alarms' && (
        <div>
          {/* KPI row */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))', gap:'14px', marginBottom:'20px' }}>
            <StatCard label="Critical Styles" value={stats.critical} sub="Fabric < 80% of need" icon={AlertTriangle} color="#c93652"/>
            <StatCard label="Warning Styles"  value={stats.warning}  sub="Fabric 80-95% of need" icon={AlertCircle} color="#fdab3d"/>
            <StatCard label="OK Styles"       value={stats.ok}       sub="95-110% covered" icon={CheckCircle} color="#00a65e"/>
            <StatCard label="Excess Styles"   value={stats.excess}   sub="Over 110% available" icon={TrendingDown} color="#0073ea"/>
            <StatCard label="Color Alarms"    value={stats.colorAlarms} sub="Color-level issues" icon={AlertCircle} color="#c93652"/>
            <StatCard label="Setup Needed"    value={stats.noSetup}  sub="No consumption set" icon={Info} color="#9699a6"/>
          </div>

          {/* Critical shortage alarm list */}
          {tree.filter(s => s.status === 'critical' || s.status === 'warning').length === 0 ? (
            <div style={{ ...ss, textAlign:'center', color:'#00a65e', padding:'40px', fontSize:'16px', fontWeight:'700' }}>
              ✅ No critical shortages detected
            </div>
          ) : (
            <div style={ss}>
              <h3 style={{ ...hs, color:'#c93652' }}><AlertTriangle size={14}/> 🚨 Shortage Alarms — Immediate Action Needed</h3>
              {tree.filter(s => s.status === 'critical' || s.status === 'warning').map(s => (
                <div key={s.styleRef} style={{ marginBottom:'16px', border:`1px solid ${ST[s.status].border}`, borderRadius:'10px', overflow:'hidden' }}>
                  {/* Style header */}
                  <div style={{ background: ST[s.status].bg, padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'8px' }}>
                    <div>
                      <div style={{ fontWeight:'800', fontSize:'15px', color:'#323338' }}>{s.styleRef}</div>
                      <div style={{ fontSize:'12px', color:'#676879' }}>
                        Cons: {s.defCons}m + {s.wastePct}% wastage = <strong>{s.effCons.toFixed(4)}m eff.</strong>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'12px', alignItems:'center', flexWrap:'wrap' }}>
                      <CoverageBadge status={s.status}/>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontSize:'20px', fontWeight:'900', color: ST[s.status].color }}>{s.coveragePct.toFixed(1)}%</div>
                        <div style={{ fontSize:'11px', color:'#676879' }}>coverage</div>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontSize:'14px', fontWeight:'800', color:'#c93652' }}>
                          {s.excessShort < 0 ? `${Math.abs(s.excessShort).toFixed(2)}m SHORT` : `+${s.excessShort.toFixed(2)}m`}
                        </div>
                        <div style={{ fontSize:'11px', color:'#676879' }}>Avail: {s.available.toFixed(2)}m / Reqd: {s.required.toFixed(2)}m</div>
                      </div>
                    </div>
                  </div>

                  {/* Color-level breakdown */}
                  <div style={{ padding:'10px 16px' }}>
                    {s.pos.flatMap(p => p.colors.filter(cl => cl.status === 'critical' || cl.status === 'warning').map(cl => (
                      <div key={p.po+cl.color} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'8px 10px', background: ST[cl.status].bg, borderRadius:'7px', marginBottom:'6px', flexWrap:'wrap' }}>
                        <div style={{ minWidth:'80px' }}>
                          <div style={{ fontSize:'10px', color:'#676879', fontWeight:'700' }}>PO</div>
                          <div style={{ fontWeight:'700', fontSize:'12px', color:'#0073ea' }}>{p.po}</div>
                        </div>
                        <div style={{ minWidth:'80px' }}>
                          <div style={{ fontSize:'10px', color:'#676879', fontWeight:'700' }}>Color</div>
                          <div style={{ fontWeight:'700', fontSize:'13px' }}>{cl.color}</div>
                        </div>
                        <div style={{ flex:1, minWidth:'120px' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', fontSize:'10px', marginBottom:'3px' }}>
                            <span style={{ color:'#676879' }}>{cl.available.toFixed(1)}m avail</span>
                            <span style={{ color:'#676879' }}>{cl.required.toFixed(1)}m needed</span>
                          </div>
                          <CoverageBar pct={cl.coveragePct} status={cl.status}/>
                        </div>
                        <div style={{ minWidth:'80px', textAlign:'right' }}>
                          <div style={{ fontWeight:'800', color: ST[cl.status].color, fontSize:'13px' }}>
                            {cl.excessShort < 0 ? `${Math.abs(cl.excessShort).toFixed(2)}m` : `+${cl.excessShort.toFixed(2)}m`}
                          </div>
                          <div style={{ fontSize:'10px', color:'#676879' }}>
                            {cl.excessShort < 0 ? 'SHORT' : 'EXCESS'}
                          </div>
                        </div>
                        <CoverageBadge status={cl.status}/>
                        <div style={{ fontSize:'11px', color:'#676879' }}>
                          Can cut ~{cl.colorPossible} pcs | QC✓ ~{cl.colorQCPoss} pcs
                        </div>
                      </div>
                    )))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Styles needing consumption setup */}
          {stats.noSetup > 0 && (
            <div style={{ ...ss, border:'1px solid #e6e9ef' }}>
              <h3 style={{ ...hs, color:'#9699a6' }}><Info size={14}/> ⚙ Consumption Not Set — Cannot Evaluate</h3>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                {tree.filter(s => s.status === 'no_consumption' || s.status === 'no_po').map(s => (
                  <div key={s.styleRef} style={{ background:'#f5f6f8', border:'1px solid #e6e9ef', borderRadius:'7px', padding:'8px 14px', fontSize:'13px', color:'#676879' }}>
                    {s.styleRef}
                    <span style={{ marginLeft:'8px', fontSize:'10px', color:'#9699a6' }}>
                      {s.status === 'no_po' ? '(no PO data)' : '(set consumption)'}
                    </span>
                  </div>
                ))}
              </div>
              <button onClick={() => setReconView('setup')} style={{ marginTop:'12px', padding:'8px 18px', background:'#0073ea', color:'white', border:'none', borderRadius:'7px', cursor:'pointer', fontWeight:'700', fontSize:'12px' }}>
                ⚙ Set Consumption Now
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── COVERAGE ANALYSIS TAB ─────────────────────────────────────────── */}
      {reconView === 'coverage' && (
        <div>
          {/* Filters */}
          <div style={{ ...ss, padding:'12px 14px', marginBottom:'14px' }}>
            <div style={{ display:'flex', gap:'12px', flexWrap:'wrap', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:'10px', fontWeight:'700', color:'#676879', textTransform:'uppercase', marginBottom:'3px' }}>Filter Style</div>
                <select value={filterStyle} onChange={e => setFilterStyle(e.target.value)} style={{ padding:'7px 12px', border:'1px solid #e6e9ef', borderRadius:'6px', fontSize:'12px', outline:'none' }}>
                  <option value=''>All Styles</option>
                  {styleRefs.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:'10px', fontWeight:'700', color:'#676879', textTransform:'uppercase', marginBottom:'3px' }}>Filter Status</div>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding:'7px 12px', border:'1px solid #e6e9ef', borderRadius:'6px', fontSize:'12px', outline:'none' }}>
                  <option value='all'>All</option>
                  <option value='critical'>🔴 Critical</option>
                  <option value='warning'>🟡 Warning</option>
                  <option value='ok'>🟢 OK</option>
                  <option value='excess'>🔵 Excess</option>
                  <option value='no_consumption'>⚙ Setup Needed</option>
                </select>
              </div>
              <div style={{ fontSize:'12px', color:'#676879', alignSelf:'flex-end', paddingBottom:'2px' }}>
                {filtered.length} styles shown
                {stats.noSetup > 0 && <span style={{ color:'#fdab3d', marginLeft:'10px' }}>⚠ {stats.noSetup} styles need consumption setup</span>}
              </div>
              {!filtered.some(s => s.hasConsumption) && filtered.length > 0 && (
                <button onClick={() => setReconView('setup')} style={{ alignSelf:'flex-end', padding:'7px 14px', background:'#0073ea', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'700', fontSize:'12px' }}>
                  ⚙ Set Consumption
                </button>
              )}
            </div>
          </div>

          {/* Style-level summary table */}
          <div style={ss}>
            <h3 style={hs}><BarChart3 size={14}/> Style-level Coverage</h3>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>{['Status','Style','Cons. (m/pc)','Wastage%','Eff. Cons','Available (m)','Required (m)','Short/Excess (m)','Coverage %','Progress','Detail'].map(h => <th key={h} style={TH}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filtered.length===0 && <tr><td colSpan={11} style={{ ...TD, textAlign:'center', color:'#9699a6', padding:'40px' }}>No data</td></tr>}
                  {filtered.map((s, i) => {
                    const se = s.excessShort;
                    const seColor = se < 0 ? '#c93652' : '#00a65e';
                    return (
                      <tr key={s.styleRef} style={{ background: i%2===0 ? 'white' : '#fafafa', borderBottom:'1px solid #f0f1f4', cursor:'pointer' }}
                        onClick={() => setExpandedStyle(expandedStyle === s.styleRef ? null : s.styleRef)}>
                        <td style={TD}><CoverageBadge status={s.status}/></td>
                        <td style={{ ...TD, fontWeight:'800', color:'#8b5cf6' }}>{s.styleRef}</td>
                        <td style={{ ...TD, textAlign:'right', color:'#0073ea' }}>{s.defCons > 0 ? s.defCons+'m' : <span style={{ color:'#c93652' }}>⚠ Not set</span>}</td>
                        <td style={{ ...TD, textAlign:'right' }}>{s.wastePct}%</td>
                        <td style={{ ...TD, textAlign:'right', fontWeight:'700' }}>{s.effCons > 0 ? s.effCons.toFixed(4)+'m' : '—'}</td>
                        <td style={{ ...TD, textAlign:'right', fontWeight:'700', color:'#0073ea' }}>{s.available.toFixed(2)}</td>
                        <td style={{ ...TD, textAlign:'right', color:'#8b5cf6' }}>{s.required > 0 ? s.required.toFixed(2) : '—'}</td>
                        <td style={{ ...TD, textAlign:'right', fontWeight:'800', color:seColor }}>{s.required > 0 ? (se>=0?'+':'')+se.toFixed(2) : '—'}</td>
                        <td style={{ ...TD, textAlign:'right', fontWeight:'800', color:ST[s.status]?.color }}>{s.required > 0 ? s.coveragePct.toFixed(1)+'%' : '—'}</td>
                        <td style={{ ...TD, minWidth:'90px' }}>{s.required > 0 && <CoverageBar pct={s.coveragePct} status={s.status}/>}</td>
                        <td style={{ ...TD, color:'#676879', fontSize:'11px' }}>{expandedStyle===s.styleRef?'▲ Hide':'▼ View POs'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── PO & Color drill-down ──────────────────────────────────── */}
            {filtered.filter(s => expandedStyle === s.styleRef).map(s => (
              <div key={s.styleRef} style={{ margin:'12px 0', border:'1px solid #e6e9ef', borderRadius:'10px', overflow:'hidden' }}>
                <div style={{ background:'#323338', color:'white', padding:'10px 16px', fontWeight:'800', fontSize:'13px' }}>
                  {s.styleRef} — PO / Color / Size Breakdown
                </div>

                {!s.hasConsumption && (
                  <div style={{ padding:'16px', background:'#fffbeb', color:'#78350f', fontSize:'12px' }}>
                    ⚙ Consumption not set for this style. <button onClick={() => { setReconView('setup'); setExpandedStyle(null); }} style={{ marginLeft:'8px', color:'#0073ea', background:'none', border:'none', cursor:'pointer', fontWeight:'700', textDecoration:'underline' }}>Set it now →</button>
                  </div>
                )}

                {s.pos.map(p => (
                  <div key={p.po} style={{ borderBottom:'1px solid #f0f1f4' }}>
                    {/* PO header */}
                    <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px', background:'#f5f6f8', cursor:'pointer', flexWrap:'wrap' }}
                      onClick={() => setExpandedPO(expandedPO === s.styleRef+p.po ? null : s.styleRef+p.po)}>
                      <span style={{ fontWeight:'800', color:'#0073ea', minWidth:'100px' }}>PO: {p.po}</span>
                      <CoverageBadge status={p.status} compact/>
                      <span style={{ fontSize:'12px', color:'#676879' }}>Avail: <strong>{p.available.toFixed(2)}m</strong></span>
                      <span style={{ fontSize:'12px', color:'#676879' }}>Reqd: <strong>{p.required > 0 ? p.required.toFixed(2)+'m' : '—'}</strong></span>
                      {p.required > 0 && <span style={{ fontSize:'12px', fontWeight:'700', color: p.excessShort<0?'#c93652':'#00a65e' }}>
                        {p.excessShort<0?`⬇ ${Math.abs(p.excessShort).toFixed(2)}m SHORT`:`⬆ +${p.excessShort.toFixed(2)}m`}
                      </span>}
                      <span style={{ fontSize:'11px', color:'#9699a6' }}>{expandedPO===s.styleRef+p.po?'▲':' ▼ Colors'}</span>
                    </div>

                    {/* Color breakdown */}
                    {expandedPO === s.styleRef+p.po && (
                      <div style={{ padding:'10px 16px', background:'white' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                          <thead>
                            <tr>{['Status','Color','Order Qty','Available (m)','Reqd (m)','QC-Pass (m)','Coverage%','Possible Pcs','QC-Pass Pcs','Short/Excess (m)'].map(h => <th key={h} style={{ ...TH, fontSize:'9px' }}>{h}</th>)}</tr>
                          </thead>
                          <tbody>
                            {p.colors.map((cl, ci) => (
                              <React.Fragment key={cl.color}>
                                <tr style={{ background: ci%2===0?'white':'#fafafa', borderBottom:'1px solid #f0f1f4' }}>
                                  <td style={TD}><CoverageBadge status={cl.status} compact/></td>
                                  <td style={{ ...TD, fontWeight:'700' }}>{cl.color}</td>
                                  <td style={{ ...TD, textAlign:'right' }}>{cl.totalQty} pcs</td>
                                  <td style={{ ...TD, textAlign:'right', fontWeight:'700', color:'#0073ea' }}>{cl.available.toFixed(2)}</td>
                                  <td style={{ ...TD, textAlign:'right', color:'#8b5cf6' }}>{cl.required > 0 ? cl.required.toFixed(2) : '—'}</td>
                                  <td style={{ ...TD, textAlign:'right', color:'#00a65e', background:'#f0fdf4' }}>{cl.qcPassAvail.toFixed(2)}</td>
                                  <td style={{ ...TD, textAlign:'right', fontWeight:'800', color:ST[cl.status]?.color }}>
                                    {cl.required > 0 ? (
                                      <>{cl.coveragePct.toFixed(1)}%<CoverageBar pct={cl.coveragePct} status={cl.status}/></>
                                    ) : '—'}
                                  </td>
                                  <td style={{ ...TD, textAlign:'right', fontWeight:'700' }}>{cl.colorPossible > 0 ? cl.colorPossible+' pcs' : '—'}</td>
                                  <td style={{ ...TD, textAlign:'right', color:'#00a65e', fontWeight:'700', background:'#f0fdf4' }}>{cl.colorQCPoss > 0 ? cl.colorQCPoss+' pcs' : '—'}</td>
                                  <td style={{ ...TD, textAlign:'right', fontWeight:'800', color: cl.excessShort<0?'#c93652':'#00a65e' }}>
                                    {cl.required > 0 ? (cl.excessShort>=0?'+':'')+cl.excessShort.toFixed(2) : '—'}
                                  </td>
                                </tr>
                                {/* Size breakdown */}
                                {cl.sizes.length > 0 && (
                                  <tr>
                                    <td colSpan={10} style={{ padding:'0 0 8px 32px', background:'#fafafa' }}>
                                      <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', padding:'6px 0' }}>
                                        {cl.sizes.map(sz => (
                                          <div key={sz.size} style={{ background:'white', border:'1px solid #e6e9ef', borderRadius:'6px', padding:'4px 10px', fontSize:'11px', textAlign:'center' }}>
                                            <div style={{ fontWeight:'700', color:'#323338' }}>{sz.size}</div>
                                            <div style={{ color:'#0073ea' }}>{sz.qty} pcs</div>
                                            {sz.required > 0 && <div style={{ color:'#8b5cf6', fontSize:'10px' }}>{sz.required.toFixed(2)}m reqd</div>}
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// Pulse animation for live indicator
if (!document.getElementById('nirvikalp-pulse-css')) {
  const style = document.createElement('style');
  style.id = 'nirvikalp-pulse-css';
  style.textContent = '@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}';
  document.head.appendChild(style);
}

export default function MaterialManagement({ db, setDb }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const isTablet = useIsTablet();

  useEffect(()=>{ window.__masterDB = db; },[db]);

  const [trimsInv, setTrimsInv] = useState(db?.trimsInventory||[]);
  const [accInv,   setAccInv]   = useState(db?.accessoriesInventory||[]);
  const [packInv,  setPackInv]  = useState(db?.packingInventory||[]);
  const fabric = db?.materials||[];

  useEffect(()=>{
    if(db?.trimsInventory)       setTrimsInv(db.trimsInventory);
    if(db?.accessoriesInventory) setAccInv(db.accessoriesInventory);
    if(db?.packingInventory)     setPackInv(db.packingInventory);
  },[db]);

  const saveCategory = (key,data) => setDb(p=>({...p,[key]:data}));
  const allItems=[...fabric,...trimsInv,...accInv,...packInv];
  const critCount=allItems.filter(i=>getStockStatus(i)==='critical').length;
  const lowCount =allItems.filter(i=>getStockStatus(i)==='low').length;

  const tabs=[
    {id:'dashboard',        label:'Dashboard',       icon:LayoutDashboard},
    {id:'fabric',           label:'Fabric',          icon:Layers},
    {id:'trims',            label:'Trims',           icon:Tag},
    {id:'accessories',      label:'Accessories',     icon:Scissors},
    {id:'packing',          label:'Packing',         icon:Box},
    {id:'fabric_processing',label:'Greige → Finish', icon:RefreshCw},
    {id:'racks',            label:'Racks / QR',      icon:MapPin},
    {id:'reports',          label:'Reports',         icon:BarChart3},
  ];

  return (
    <div style={{height:'100%',display:'flex',flexDirection:'column',background:'#f0f1f4',overflow:'hidden'}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @media print{.no-print{display:none!important}}`}</style>
      {/* TOP NAV */}
      <div style={{display:'flex',alignItems:'center',borderBottom:'1px solid #e6e9ef',background:'white',padding:'0 16px',flexWrap:'wrap',flexShrink:0}}>
        <div style={{display:'flex',flexWrap:'wrap',flex:1}}>
          {tabs.map(t=>(
            <SubTabBtn key={t.id} label={isTablet&&t.label.length>8?t.label.slice(0,7)+'…':t.label} active={activeTab===t.id} onClick={()=>setActiveTab(t.id)} icon={t.icon}/>
          ))}
        </div>
        <div style={{display:'flex',gap:'8px',padding:'8px 0',flexShrink:0}}>
          {critCount>0&&<div onClick={()=>setActiveTab('reports')} style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'12px',color:'#c93652',fontWeight:'800',background:'#fde8eb',padding:'4px 10px',borderRadius:'20px',border:'1px solid #fecaca',cursor:'pointer'}} title="View critical items"><AlertCircle size={12}/>{critCount} Critical</div>}
          {lowCount>0&&<div onClick={()=>setActiveTab('reports')} style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'12px',color:'#fdab3d',fontWeight:'800',background:'#fff5e6',padding:'4px 10px',borderRadius:'20px',border:'1px solid #fde68a',cursor:'pointer'}} title="View low stock items"><TrendingDown size={12}/>{lowCount} Low</div>}
        </div>
      </div>

      {/* CONTENT */}
      <div style={{flex:1,overflowY:'auto',padding:isTablet?'12px':'20px'}}>
        <div style={{maxWidth:'1800px',margin:'0 auto'}}>
          {activeTab==='dashboard'    && <KanbanDashboard fabric={fabric} trims={trimsInv} accessories={accInv} packing={packInv} db={db}/>}
          {activeTab==='fabric'       && <FabricModule db={db} setDb={setDb}/>}
          {activeTab==='trims'        && <InventoryModule category="Trims" color="#0073ea" icon={Tag} db={trimsInv} onSave={d=>{setTrimsInv(d);saveCategory('trimsInventory',d)}} onDelete={d=>{setTrimsInv(d);saveCategory('trimsInventory',d)}}/>}
          {activeTab==='accessories'  && <InventoryModule category="Accessories" color="#fdab3d" icon={Scissors} db={accInv} onSave={d=>{setAccInv(d);saveCategory('accessoriesInventory',d)}} onDelete={d=>{setAccInv(d);saveCategory('accessoriesInventory',d)}}/>}
          {activeTab==='packing'      && <InventoryModule category="Packing" color="#00a65e" icon={Box} db={packInv} onSave={d=>{setPackInv(d);saveCategory('packingInventory',d)}} onDelete={d=>{setPackInv(d);saveCategory('packingInventory',d)}}/>}
          {activeTab==='fabric_processing' && <FabricProcessingModule db={db} setDb={setDb}/>}
          {activeTab==='racks'        && <RackConfigurator db={db} setDb={setDb}/>}
          {activeTab==='reports'      && <ReportsModule fabric={fabric} trims={trimsInv} accessories={accInv} packing={packInv} db={db}/>}
        </div>
      </div>
    </div>
  );
}
